import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import { SUNNY } from "./theme/sunny";

const PEE = {
  ...SUNNY,
  cream:   "#FFFDE0",
  paper:   "#FFFBE8",
  sun:     "#F5E225",
  sunDeep: "#C9B100",
  tomato:  "#DAA520",
  grass:   "#8B7000",
  sky:     "#BFA800",
  ink:     "#3D2800",
  inkSoft: "#6B4C00",
};
import { Patch, SunGlyph, WeatherSticker, SunArcBar } from "./components/Sticker";
const MapView = lazy(() => import('./MapView'));
const VenueMap = lazy(() => import('./MapView').then(m => ({ default: m.VenueMap })));

// ── Debug flag — set to true to show OSM source links on playground cards ─────
const DEBUG = false;

// Diagnostic logging for the search/enrichment pipeline. Chatty by design, so
// it's silenced in production builds unless localStorage pg_debug=1 is set
// (e.g. from the console while investigating a bad search on a real device).
const DEBUG_LOG = process.env.NODE_ENV !== "production" ||
  (() => { try { return localStorage.getItem("pg_debug") === "1"; } catch { return false; } })();
function dlog(...args) { if (DEBUG_LOG) console.log(...args); }

// ── Helpers ──────────────────────────────────────────────────────────────────
function getSunPosition(lat, lng, date = new Date()) {
  const rad = Math.PI / 180;
  const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000);
  const declination = 23.45 * Math.sin(rad * (360 / 365) * (dayOfYear - 81));
  // Use UTC time + longitude offset to get true solar time, rather than
  // assuming solar noon = 12:00 local clock time (which can be off by >1 hour)
  const utcHours = date.getUTCHours() + date.getUTCMinutes() / 60;
  const solarHour = ((utcHours + lng / 15) % 24 + 24) % 24;
  const hourAngle = (solarHour - 12) * 15;
  const altitude = Math.asin(
    Math.sin(rad * lat) * Math.sin(rad * declination) +
    Math.cos(rad * lat) * Math.cos(rad * declination) * Math.cos(rad * hourAngle)
  ) / rad;
  const azimuth = Math.atan2(
    Math.sin(rad * hourAngle),
    Math.cos(rad * hourAngle) * Math.sin(rad * lat) - Math.tan(rad * declination) * Math.cos(rad * lat)
  ) / rad + 180;
  return { altitude: Math.round(altitude), azimuth: Math.round(azimuth) };
}

function getSunTimes(lat, lng) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let sunrise = null, sunset = null;
  let prevAlt = getSunPosition(lat, lng, today).altitude;
  for (let m = 5; m <= 24 * 60; m += 5) {
    const d = new Date(today.getTime() + m * 60000);
    const { altitude } = getSunPosition(lat, lng, d);
    if (prevAlt < 0 && altitude >= 0 && !sunrise) sunrise = d;
    if (prevAlt >= 0 && altitude < 0 && !sunset) sunset = d;
    prevAlt = altitude;
  }
  return { sunrise, sunset };
}

function getSunScore(altitude, azimuth, sunrise, sunset, referenceTime = new Date()) {
  const fmt = t => t ? t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
  if (altitude < 5) {
    const beforeSunrise = sunrise && referenceTime < sunrise;
    if (altitude >= 0) return { score: 0, label: sunset ? `Setting — set at ${fmt(sunset)}` : "Sun setting soon" };
    if (beforeSunrise)  return { score: 0, label: sunrise ? `Sunrise at ${fmt(sunrise)}` : "Sun not yet risen" };
    return { score: 0, label: sunset ? `Sun set at ${fmt(sunset)}` : "Sun has set" };
  }
  if (altitude > 45) return { score: 100, label: "Full sun overhead" };
  if (altitude > 25) return { score: 75, label: "Good sun angle" };
  return { score: 40, label: "Low sun angle" };
}

// ── "Best window today" — score the remaining daylight hours from the hourly
// forecast (weather.hourly, see /api/weather) + sun math, and pick the best
// contiguous stretch worth calling out on the detail view. Returns
// { start, end, temp } (Dates + avg °F) or null when there's no hourly data,
// the day is over, or nothing left today is actually pleasant.
function bestWindowToday(weather, lat, lng, now = new Date()) {
  const h = weather?.hourly;
  if (!h?.time?.length || !h.temperature_2m) return null;
  const { sunset } = getSunTimes(lat, lng);
  if (!sunset || now >= sunset) return null;

  const hours = [];
  for (let i = 0; i < h.time.length; i++) {
    // timezone=auto gives venue-local ISO strings with no offset; Date parses
    // those as local time, which matches since users search near themselves.
    const t = new Date(h.time[i]);
    if (isNaN(t) || t.getDate() !== now.getDate()) continue;
    // Keep the hour currently in progress, drop fully-past ones and post-sunset.
    if (t.getTime() + 60 * 60000 < now.getTime() || t >= sunset) continue;
    const alt = getSunPosition(lat, lng, new Date(t.getTime() + 30 * 60000)).altitude;
    if (alt < 5) continue;
    const temp = h.temperature_2m[i];
    if (temp == null) continue;
    const precip = h.precipitation?.[i] ?? 0;
    const wind = h.wind_speed_10m?.[i] ?? 0;
    const cloud = h.cloud_cover?.[i] ?? 0;
    let score = 100;
    if (temp < 45) score -= 40; else if (temp < 55) score -= 20;
    if (temp > 95) score -= 40; else if (temp > 85) score -= 15;
    if (precip >= 2) score -= 70; else if (precip > 0) score -= 35;
    if (wind >= 20) score -= 30; else if (wind >= 12) score -= 12;
    score -= cloud * 0.15;            // overcast knocks it down a notch
    score += Math.min(alt, 45) * 0.3; // reward a higher sun
    hours.push({ t, temp, score });
  }
  if (!hours.length) return null;
  const max = Math.max(...hours.map(x => x.score));
  if (max < 45) return null; // nothing left today is genuinely nice — say nothing

  // Longest contiguous run of near-peak hours (within 10 pts of the best).
  let best = null, run = [];
  for (const hr of hours) {
    const prev = run[run.length - 1];
    if (hr.score >= max - 10 && (!prev || hr.t.getTime() - prev.t.getTime() === 60 * 60000)) {
      run.push(hr);
    } else {
      run = hr.score >= max - 10 ? [hr] : [];
    }
    if (run.length && (!best || run.length > best.length)) best = [...run];
  }
  if (!best) return null;
  const temp = Math.round(best.reduce((s, x) => s + x.temp, 0) / best.length);
  const end = new Date(best[best.length - 1].t.getTime() + 60 * 60000);
  return { start: best[0].t, end: end > sunset ? sunset : end, temp };
}

const TRAIL_MODES = new Set(["hiking", "mtb", "nature_play"]);

const RADIUS_OPTIONS = [
  { meters: 3200 },
  { meters: 8000 },
  { meters: 16000 },
  { meters: 40000 },
];

// "Search this area" searches what the user framed, not the radius they last
// picked — zooming out to a whole city and getting "no results within 2mi" is
// the bug this exists to kill. Snapped UP to a preset rather than used raw, so
// the radius pills, the "search wider" prompts and the empty-state copy (all of
// which look the current radius up in RADIUS_OPTIONS) stay in sync. The 3200m
// floor means a zoomed-in search behaves exactly as it did before.
function radiusForViewport(viewMeters) {
  if (!viewMeters || !Number.isFinite(viewMeters)) return null;
  const fits = RADIUS_OPTIONS.find(o => o.meters >= viewMeters);
  return (fits ?? RADIUS_OPTIONS[RADIUS_OPTIONS.length - 1]).meters;
}

// ── Unit helpers ──────────────────────────────────────────────────────────────
function radLabel(meters, metric) {
  return metric ? `${Math.round(meters / 1000)}km` : `${Math.round(meters / 1609)}mi`;
}
function fmtTemp(f, metric) {
  if (f == null) return "?";
  return metric ? `${Math.round((f - 32) * 5 / 9)}°C` : `${f}°F`;
}
function fmtDist(miles, metric) {
  if (miles == null) return "";
  const n = parseFloat(miles);
  if (metric) {
    const km = n * 1.60934;
    return km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;
  }
  return `${n.toFixed(1)}mi`;
}
function fmtShortDist(miles, metric) {
  if (metric) {
    const m = Math.round(miles * 1609.34);
    return m < 1000 ? `${m}m` : `${(m / 1000).toFixed(1)}km`;
  }
  const ft = Math.round(miles * 5280);
  return ft < 500 ? `${ft}ft` : `${parseFloat(miles).toFixed(2)}mi`;
}
function windDir(deg) {
  if (deg == null) return "";
  const dirs = ["N","NE","E","SE","S","SW","W","NW"];
  return " " + dirs[Math.round(deg / 45) % 8];
}
function fmtWind(mph, metric, deg) {
  if (mph == null) return "?";
  const speed = metric ? `${Math.round(mph * 1.60934)}km/h` : `${mph}mph`;
  return speed + windDir(deg);
}
function fmtVis(miles, metric) {
  if (miles == null) return "?";
  return metric ? `${Math.round(miles * 1.60934)}km` : `${miles}mi`;
}

const FACING_DEGREES = { North: 0, Northeast: 45, East: 90, Southeast: 135, South: 180, Southwest: 225, West: 270, Northwest: 315 };

function getConditionsScore(pg, sunInfo, weather) {
  if (!sunInfo) return 0;
  let score;
  if (sunInfo.altitude >= 5) {
    // Daytime: sun-angle base score + facing bonus
    score = sunInfo.score;
    const facingDeg = FACING_DEGREES[pg.facing];
    if (facingDeg !== undefined) {
      const diff = Math.abs(((sunInfo.azimuth - facingDeg + 540) % 360) - 180);
      score += Math.cos(diff * Math.PI / 180) * 20;
    }
  } else if (sunInfo.altitude >= -6) {
    // Civil twilight: sun set or nearly set, some daylight remains — weather-only
    score = 65;
  } else {
    // Dark: weather-only with lower base
    score = 40;
  }
  // Weather penalties apply in all lighting conditions
  if (weather) {
    if (weather.fog) score -= 20;
    if (weather.wind > 35) score -= 40;
    else if (weather.wind > 25) score -= 25;
    else if (weather.wind > 15) score -= 10;
    if (weather.condition === "Thunderstorm") score -= 100;
    else if (weather.condition === "Rain" || weather.precipitation >= 2) score -= 80;
    else if (weather.condition === "Drizzle" || weather.precipitation > 0) score -= 50;
    else if (weather.condition === "Snow") score -= 40;
  }
  return Math.max(0, Math.min(100, Math.round(score)));
}

function isWindSheltered(pg, windDirection) {
  const facingDeg = FACING_DEGREES[pg.facing];
  if (facingDeg !== undefined && windDirection != null) {
    // Playground faces away from wind source (> 90° away) = in the lee
    const diff = Math.abs(((windDirection - facingDeg + 540) % 360) - 180);
    return diff > 90;
  }
  return (pg.features || []).includes("shade");
}

// ── Toll-bridge detection ─────────────────────────────────────────────────────
// Bay Area toll crossings, modeled as "gate" segments laid ACROSS each water
// channel (perpendicular to the bridge deck — i.e. along the water the bridge
// spans). If the straight line from the user to a venue crosses a gate, the trip
// almost certainly requires that toll bridge. This is geometry only (no routing
// API), so it's a heuristic: reliable where Bay water forces you onto a specific
// bridge, but straight-line ≠ driving route, hence "likely toll," not gospel.
// Toll-free workarounds that go AROUND the water (e.g. SR-37 from Vallejo to
// Marin) are intentionally left un-flagged by keeping each gate inside the
// bridged channel rather than spanning open bay.
const TOLL_BRIDGE_GATES = [
  { name: "Golden Gate Bridge",          a: { lat: 37.8195, lng: -122.4980 }, b: { lat: 37.8205, lng: -122.4580 } },
  { name: "Bay Bridge",                  a: { lat: 37.7400, lng: -122.3550 }, b: { lat: 37.8800, lng: -122.3450 } },
  { name: "Richmond–San Rafael Bridge",  a: { lat: 37.8900, lng: -122.4450 }, b: { lat: 37.9900, lng: -122.4350 } },
  { name: "San Mateo Bridge",            a: { lat: 37.5500, lng: -122.2000 }, b: { lat: 37.6600, lng: -122.1800 } },
  { name: "Dumbarton Bridge",            a: { lat: 37.4600, lng: -122.1350 }, b: { lat: 37.5400, lng: -122.1250 } },
  { name: "Carquinez/Benicia Bridge",    a: { lat: 38.0620, lng: -122.2720 }, b: { lat: 38.0450, lng: -122.1180 } },
  { name: "Antioch Bridge",              a: { lat: 38.0300, lng: -121.8300 }, b: { lat: 38.0300, lng: -121.7800 } },
];

// Proper segment crossing (lng as x, lat as y; planar is fine at Bay scale).
function segmentsCross(p1, p2, p3, p4) {
  const side = (a, b, c) => (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
  const d1 = side(p3, p4, p1), d2 = side(p3, p4, p2);
  const d3 = side(p1, p2, p3), d4 = side(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
         ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

// Returns the name of the toll bridge a straight trip would likely cross, else null.
function tollBridgeForTrip(fromLat, fromLng, toLat, toLng) {
  if (fromLat == null || fromLng == null || toLat == null || toLng == null) return null;
  const from = { lat: fromLat, lng: fromLng }, to = { lat: toLat, lng: toLng };
  for (const g of TOLL_BRIDGE_GATES) {
    if (segmentsCross(from, to, g.a, g.b)) return g.name;
  }
  return null;
}

// Single source of truth for the travel-time label shown on the card, the
// top-result hero, and the detail "directions" pill — they must all agree.
//
//   miles      — display distance; callers pass `roadDist ?? haversineDist`
//                so the label keys off the same number everywhere.
//   driveSecs  — real OSRM driving duration from /api/walk-times:
//                undefined = still loading, null = no route, number = seconds.
//
// Rule: walk (or hike/bike) for short hops, drive beyond. For driving we use
// the real routed duration when we have it — that's the closest match to what
// Apple/Google Maps will show (only live traffic differs). The constant-speed
// `÷25 mph` estimate is a "~" fallback used only while routing is loading or
// returned no route.
const WALK_THRESHOLD_MI = 1.0; // restore: walk within a mile, drive after
function formatTravelTime(miles, isBikeMode, driveSecs, activityMode) {
  const m = parseFloat(miles);
  if (!isFinite(m)) return "";
  const label = TRAIL_MODES.has(activityMode) ? "hike" : "walk";

  // Self-powered estimate for short hops.
  if (isBikeMode) {
    const bikeMins = Math.max(1, Math.round(m * 6)); // ~10 mph
    if (bikeMins <= 30) return `${bikeMins} min bike`;
  } else if (m <= WALK_THRESHOLD_MI) {
    return `${Math.max(1, Math.round(m * 20))} min ${label}`; // ~3 mph
  }

  // Drive: prefer the real OSRM routed duration; fall back to a constant-speed
  // estimate (flagged with "~") only while it's loading or unavailable.
  const approx = driveSecs == null;
  const driveMins = driveSecs != null
    ? Math.max(1, Math.round(driveSecs / 60))
    : Math.round(m / 25 * 60);
  return `🚗 ${approx ? "~" : ""}${driveMins} min drive`;
}

function getSchoolStatus() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 6=Sat
  const hour = now.getHours() + now.getMinutes() / 60;
  if (day === 0 || day === 6) return { open: true, label: "Open today", publicNote: "May not be open to public" };
  if (hour < 8 || hour >= 15.5) return { open: true, label: "Open now", publicNote: "May not be open to public" };
  return { open: false, label: "School hours — likely closed", publicNote: "May not be open to public" };
}

const GENERIC_NAME_RE = /^playground(\s|$)|^(neighborhood|community|local|city|district|area|unnamed|public)\s+playground$/i;
function displayName(name, address) {
  if (!GENERIC_NAME_RE.test(name)) return name;
  if (address) {
    const street = address.split(",")[0].trim().replace(/^\d+\s+/, "");
    if (street && street.length > 3) return `${street} Playground`;
  }
  return "Neighborhood Playground";
}

function getCompass(deg) {
  const dirs = ["North","Northeast","East","Southeast","South","Southwest","West","Northwest"];
  return dirs[Math.round(deg / 45) % 8];
}

// ── Map helpers ──────────────────────────────────────────────────────────────
function openAppleMaps(name, lat, lng, fromLat, fromLng) {
  const coords = `${lat},${lng}`;
  const dest = name ? `${encodeURIComponent(name)}@${coords}` : coords;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const scheme = isIOS ? "maps://" : "https://maps.apple.com/";
  const url = fromLat && fromLng
    ? `${scheme}?saddr=${fromLat},${fromLng}&daddr=${dest}&dirflg=d`
    : `${scheme}?q=${encodeURIComponent(name)}&ll=${coords}`;
  if (isIOS) { window.location.href = url; } else { window.open(url, "_blank"); }
}
// Multi-stop: user → waypoint (parking) → destination (playground)
function openAppleMapsViaParking(fromLat, fromLng, parkLat, parkLng, destLat, destLng) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const scheme = isIOS ? "maps://" : "https://maps.apple.com/";
  // Apple Maps supports multiple daddr with "+to:" chaining
  const url = `${scheme}?saddr=${fromLat},${fromLng}&daddr=${parkLat},${parkLng}+to:${destLat},${destLng}&dirflg=d`;
  if (isIOS) { window.location.href = url; } else { window.open(url, "_blank"); }
}
function openGoogleMaps(name, lat, lng, fromLat, fromLng) {
  const coords = `${lat},${lng}`;
  // Always route by coordinates — name-based routing fails for generic facility
  // names ("Bathroom", "Restroom", "Parking") that Google can't geocode reliably.
  const url = fromLat && fromLng
    ? `https://www.google.com/maps/dir/?api=1&origin=${fromLat},${fromLng}&destination=${coords}`
    : `https://www.google.com/maps/search/?api=1&query=${coords}`;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) { window.location.href = url; } else { window.open(url, "_blank"); }
}
function osmEmbedUrl(lat, lng, zoom = 15) {
  return `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.007},${lng+0.01},${lat+0.007}&layer=mapnik&marker=${lat},${lng}`;
}

// ── Geocode: zip table + city name fuzzy match, zero network calls ────────────
const ZIP_DB = {
  // California
  "90001":{"lat":33.9731,"lng":-118.2479,"label":"Los Angeles, CA"},
  "90210":{"lat":34.0901,"lng":-118.4065,"label":"Beverly Hills, CA"},
  "90401":{"lat":34.0195,"lng":-118.4912,"label":"Santa Monica, CA"},
  "91101":{"lat":34.1478,"lng":-118.1445,"label":"Pasadena, CA"},
  "92101":{"lat":32.7157,"lng":-117.1611,"label":"San Diego, CA"},
  "92037":{"lat":32.8478,"lng":-117.2745,"label":"La Jolla, CA"},
  "93101":{"lat":34.4208,"lng":-119.6982,"label":"Santa Barbara, CA"},
  "94002":{"lat":37.5185,"lng":-122.2772,"label":"Belmont, CA"},
  "94005":{"lat":37.6879,"lng":-122.4027,"label":"Brisbane, CA"},
  "94010":{"lat":37.5630,"lng":-122.3647,"label":"Burlingame, CA"},
  "94014":{"lat":37.6879,"lng":-122.4342,"label":"Daly City, CA"},
  "94015":{"lat":37.6546,"lng":-122.4822,"label":"Daly City, CA"},
  "94019":{"lat":37.4746,"lng":-122.4286,"label":"Half Moon Bay, CA"},
  "94025":{"lat":37.4530,"lng":-122.1817,"label":"Menlo Park, CA"},
  "94027":{"lat":37.4530,"lng":-122.2008,"label":"Atherton, CA"},
  "94030":{"lat":37.5952,"lng":-122.3994,"label":"Millbrae, CA"},
  "94040":{"lat":37.3861,"lng":-122.0839,"label":"Mountain View, CA"},
  "94041":{"lat":37.3894,"lng":-122.0819,"label":"Mountain View, CA"},
  "94043":{"lat":37.4030,"lng":-122.0469,"label":"Mountain View, CA"},
  "94044":{"lat":37.6138,"lng":-122.4869,"label":"Pacifica, CA"},
  "94061":{"lat":37.4824,"lng":-122.2324,"label":"Redwood City, CA"},
  "94062":{"lat":37.4688,"lng":-122.2530,"label":"Redwood City, CA"},
  "94063":{"lat":37.4852,"lng":-122.2033,"label":"Redwood City, CA"},
  "94065":{"lat":37.5327,"lng":-122.2477,"label":"Redwood Shores, CA"},
  "94066":{"lat":37.6318,"lng":-122.4089,"label":"San Bruno, CA"},
  "94070":{"lat":37.5116,"lng":-122.2608,"label":"San Carlos, CA"},
  "94080":{"lat":37.6546,"lng":-122.4074,"label":"South San Francisco, CA"},
  "94085":{"lat":37.3880,"lng":-122.0080,"label":"Sunnyvale, CA"},
  "94086":{"lat":37.3774,"lng":-122.0302,"label":"Sunnyvale, CA"},
  "94087":{"lat":37.3563,"lng":-122.0346,"label":"Sunnyvale, CA"},
  "94089":{"lat":37.4088,"lng":-121.9966,"label":"Sunnyvale, CA"},
  "94102":{"lat":37.7793,"lng":-122.4192,"label":"San Francisco, CA"},
  "94103":{"lat":37.7726,"lng":-122.4099,"label":"SoMa, San Francisco, CA"},
  "94107":{"lat":37.7576,"lng":-122.3947,"label":"Potrero Hill, SF, CA"},
  "94109":{"lat":37.7941,"lng":-122.4223,"label":"Nob Hill, SF, CA"},
  "94110":{"lat":37.7484,"lng":-122.4156,"label":"Mission, SF, CA"},
  "94114":{"lat":37.7580,"lng":-122.4347,"label":"Castro, SF, CA"},
  "94116":{"lat":37.7434,"lng":-122.4842,"label":"West Portal, SF, CA"},
  "94117":{"lat":37.7695,"lng":-122.4449,"label":"Haight-Ashbury, SF, CA"},
  "94118":{"lat":37.7823,"lng":-122.4608,"label":"Inner Richmond, SF, CA"},
  "94121":{"lat":37.7786,"lng":-122.4930,"label":"Outer Richmond, SF, CA"},
  "94122":{"lat":37.7606,"lng":-122.4869,"label":"Outer Sunset, SF, CA"},
  "94123":{"lat":37.7999,"lng":-122.4366,"label":"Marina, SF, CA"},
  "94124":{"lat":37.7317,"lng":-122.3892,"label":"Bayview, SF, CA"},
  "94131":{"lat":37.7455,"lng":-122.4386,"label":"Noe Valley, SF, CA"},
  "94132":{"lat":37.7228,"lng":-122.4750,"label":"Lakeside, SF, CA"},
  "94133":{"lat":37.8008,"lng":-122.4105,"label":"North Beach, SF, CA"},
  "94134":{"lat":37.7191,"lng":-122.4081,"label":"Visitacion Valley, SF, CA"},
  "94301":{"lat":37.4419,"lng":-122.1430,"label":"Palo Alto, CA"},
  "94303":{"lat":37.4544,"lng":-122.1163,"label":"Palo Alto, CA"},
  "94401":{"lat":37.5630,"lng":-122.3255,"label":"San Mateo, CA"},
  "94402":{"lat":37.5444,"lng":-122.3230,"label":"San Mateo, CA"},
  "94403":{"lat":37.5280,"lng":-122.3008,"label":"San Mateo, CA"},
  "94404":{"lat":37.5574,"lng":-122.2685,"label":"San Mateo, CA"},
  "94501":{"lat":37.7652,"lng":-122.2416,"label":"Alameda, CA"},
  "94502":{"lat":37.7374,"lng":-122.2388,"label":"Alameda, CA"},
  "94577":{"lat":37.6954,"lng":-122.1549,"label":"San Leandro, CA"},
  "94601":{"lat":37.7766,"lng":-122.2175,"label":"Oakland, CA"},
  "94602":{"lat":37.7993,"lng":-122.2155,"label":"Oakland, CA"},
  "94609":{"lat":37.8310,"lng":-122.2643,"label":"Oakland, CA"},
  "94610":{"lat":37.8088,"lng":-122.2388,"label":"Grand Lake, Oakland, CA"},
  "94611":{"lat":37.8310,"lng":-122.2175,"label":"Montclair, Oakland, CA"},
  "94618":{"lat":37.8385,"lng":-122.2496,"label":"Rockridge, Oakland, CA"},
  "94702":{"lat":37.8716,"lng":-122.2894,"label":"Berkeley, CA"},
  "94703":{"lat":37.8619,"lng":-122.2751,"label":"Berkeley, CA"},
  "94704":{"lat":37.8668,"lng":-122.2588,"label":"Berkeley, CA"},
  "94705":{"lat":37.8536,"lng":-122.2443,"label":"Berkeley, CA"},
  "94706":{"lat":37.8833,"lng":-122.3038,"label":"Albany, CA"},
  "94901":{"lat":37.9735,"lng":-122.5311,"label":"San Rafael, CA"},
  "94925":{"lat":37.9219,"lng":-122.5219,"label":"Corte Madera, CA"},
  "94939":{"lat":37.9374,"lng":-122.5547,"label":"Larkspur, CA"},
  "94941":{"lat":37.8905,"lng":-122.5311,"label":"Mill Valley, CA"},
  "94960":{"lat":37.9577,"lng":-122.5711,"label":"San Anselmo, CA"},
  "94965":{"lat":37.8543,"lng":-122.5047,"label":"Sausalito, CA"},
  "95008":{"lat":37.2858,"lng":-121.9513,"label":"Campbell, CA"},
  "95014":{"lat":37.3230,"lng":-122.0327,"label":"Cupertino, CA"},
  "95032":{"lat":37.2522,"lng":-121.9527,"label":"Los Gatos, CA"},
  "95050":{"lat":37.3541,"lng":-121.9552,"label":"Santa Clara, CA"},
  "95051":{"lat":37.3496,"lng":-121.9805,"label":"Santa Clara, CA"},
  "95112":{"lat":37.3541,"lng":-121.8863,"label":"San Jose, CA"},
  "95125":{"lat":37.2996,"lng":-121.8996,"label":"Willow Glen, San Jose, CA"},
  "95128":{"lat":37.3210,"lng":-121.9330,"label":"San Jose, CA"},
  // New York
  "10001":{"lat":40.7484,"lng":-73.9967,"label":"Chelsea, New York, NY"},
  "10002":{"lat":40.7157,"lng":-73.9863,"label":"Lower East Side, NY"},
  "10003":{"lat":40.7317,"lng":-73.9892,"label":"East Village, NY"},
  "10007":{"lat":40.7135,"lng":-74.0078,"label":"Lower Manhattan, NY"},
  "10011":{"lat":40.7421,"lng":-74.0004,"label":"Chelsea, NY"},
  "10013":{"lat":40.7211,"lng":-74.0052,"label":"Tribeca, NY"},
  "10014":{"lat":40.7336,"lng":-74.0027,"label":"West Village, NY"},
  "10021":{"lat":40.7698,"lng":-73.9597,"label":"Upper East Side, NY"},
  "10023":{"lat":40.7762,"lng":-73.9817,"label":"Upper West Side, NY"},
  "10024":{"lat":40.7873,"lng":-73.9748,"label":"Upper West Side, NY"},
  "10025":{"lat":40.7986,"lng":-73.9680,"label":"Morningside Heights, NY"},
  "10027":{"lat":40.8126,"lng":-73.9537,"label":"Harlem, NY"},
  "10036":{"lat":40.7580,"lng":-73.9855,"label":"Midtown, NY"},
  "10128":{"lat":40.7794,"lng":-73.9498,"label":"Upper East Side, NY"},
  "10301":{"lat":40.6404,"lng":-74.0940,"label":"Staten Island, NY"},
  "10451":{"lat":40.8193,"lng":-73.9254,"label":"South Bronx, NY"},
  "11201":{"lat":40.6928,"lng":-73.9905,"label":"Brooklyn Heights, NY"},
  "11205":{"lat":40.6954,"lng":-73.9621,"label":"Clinton Hill, Brooklyn, NY"},
  "11211":{"lat":40.7121,"lng":-73.9527,"label":"Williamsburg, Brooklyn, NY"},
  "11215":{"lat":40.6668,"lng":-73.9865,"label":"Park Slope, Brooklyn, NY"},
  "11217":{"lat":40.6826,"lng":-73.9799,"label":"Boerum Hill, Brooklyn, NY"},
  "11218":{"lat":40.6462,"lng":-73.9782,"label":"Kensington, Brooklyn, NY"},
  "11220":{"lat":40.6398,"lng":-74.0176,"label":"Sunset Park, Brooklyn, NY"},
  "11231":{"lat":40.6783,"lng":-74.0000,"label":"Red Hook, Brooklyn, NY"},
  "11238":{"lat":40.6783,"lng":-73.9659,"label":"Prospect Heights, Brooklyn, NY"},
  "11372":{"lat":40.7479,"lng":-73.8886,"label":"Jackson Heights, Queens, NY"},
  "11375":{"lat":40.7209,"lng":-73.8441,"label":"Forest Hills, Queens, NY"},
  // Texas
  "75201":{"lat":32.7891,"lng":-96.8067,"label":"Dallas, TX"},
  "75205":{"lat":32.8326,"lng":-96.7961,"label":"Highland Park, Dallas, TX"},
  "77002":{"lat":29.7522,"lng":-95.3678,"label":"Houston, TX"},
  "78201":{"lat":29.4241,"lng":-98.4936,"label":"San Antonio, TX"},
  "78701":{"lat":30.2672,"lng":-97.7431,"label":"Austin, TX"},
  "78704":{"lat":30.2424,"lng":-97.7716,"label":"South Austin, TX"},
  "78745":{"lat":30.2006,"lng":-97.7945,"label":"South Austin, TX"},
  // Washington
  "98101":{"lat":47.6101,"lng":-122.3421,"label":"Seattle, WA"},
  "98103":{"lat":47.6740,"lng":-122.3472,"label":"Fremont, Seattle, WA"},
  "98107":{"lat":47.6687,"lng":-122.3829,"label":"Ballard, Seattle, WA"},
  "98115":{"lat":47.6862,"lng":-122.2921,"label":"Wedgwood, Seattle, WA"},
  "98122":{"lat":47.6101,"lng":-122.2991,"label":"Capitol Hill, Seattle, WA"},
  // Oregon
  "97201":{"lat":45.5122,"lng":-122.6842,"label":"Portland, OR"},
  "97202":{"lat":45.4871,"lng":-122.6395,"label":"SE Portland, OR"},
  "97209":{"lat":45.5272,"lng":-122.6917,"label":"Pearl District, Portland, OR"},
  // Illinois
  "60601":{"lat":41.8827,"lng":-87.6233,"label":"Chicago, IL"},
  "60614":{"lat":41.9239,"lng":-87.6478,"label":"Lincoln Park, Chicago, IL"},
  "60657":{"lat":41.9400,"lng":-87.6536,"label":"Wrigleyville, Chicago, IL"},
  // Massachusetts
  "02101":{"lat":42.3601,"lng":-71.0589,"label":"Boston, MA"},
  "02134":{"lat":42.3534,"lng":-71.1329,"label":"Allston, Boston, MA"},
  "02139":{"lat":42.3656,"lng":-71.1045,"label":"Cambridge, MA"},
  // Colorado
  "80202":{"lat":39.7541,"lng":-104.9897,"label":"Denver, CO"},
  "80203":{"lat":39.7317,"lng":-104.9797,"label":"Capitol Hill, Denver, CO"},
  "80218":{"lat":39.7273,"lng":-104.9681,"label":"Cheesman Park, Denver, CO"},
  // Florida
  "33101":{"lat":25.7617,"lng":-80.1918,"label":"Miami, FL"},
  "33139":{"lat":25.7831,"lng":-80.1345,"label":"South Beach, Miami, FL"},
  "32801":{"lat":28.5383,"lng":-81.3792,"label":"Orlando, FL"},
  "33602":{"lat":27.9506,"lng":-82.4572,"label":"Tampa, FL"},
  // Georgia
  "30301":{"lat":33.7490,"lng":-84.3880,"label":"Atlanta, GA"},
  "30307":{"lat":33.7673,"lng":-84.3410,"label":"Inman Park, Atlanta, GA"},
  // Arizona
  "85001":{"lat":33.4484,"lng":-112.0740,"label":"Phoenix, AZ"},
  "85251":{"lat":33.4942,"lng":-111.9261,"label":"Scottsdale, AZ"},
  // Nevada
  "89101":{"lat":36.1699,"lng":-115.1398,"label":"Las Vegas, NV"},
  // North Carolina
  "27601":{"lat":35.7796,"lng":-78.6382,"label":"Raleigh, NC"},
  "28202":{"lat":35.2271,"lng":-80.8431,"label":"Charlotte, NC"},
};

const CITY_DB = [
  // Major cities
  {names:["new york","nyc","ny city","manhattan"],lat:40.7128,lng:-74.0060,label:"New York, NY"},
  {names:["los angeles","la","lax","socal"],lat:34.0522,lng:-118.2437,label:"Los Angeles, CA"},
  {names:["chicago","chi town","windy city"],lat:41.8781,lng:-87.6298,label:"Chicago, IL"},
  {names:["houston"],lat:29.7604,lng:-95.3698,label:"Houston, TX"},
  {names:["phoenix"],lat:33.4484,lng:-112.0740,label:"Phoenix, AZ"},
  {names:["philadelphia","philly"],lat:39.9526,lng:-75.1652,label:"Philadelphia, PA"},
  {names:["san antonio"],lat:29.4241,lng:-98.4936,label:"San Antonio, TX"},
  {names:["san diego"],lat:32.7157,lng:-117.1611,label:"San Diego, CA"},
  {names:["dallas"],lat:32.7767,lng:-96.7970,label:"Dallas, TX"},
  {names:["san jose"],lat:37.3382,lng:-121.8863,label:"San Jose, CA"},
  {names:["austin"],lat:30.2672,lng:-97.7431,label:"Austin, TX"},
  {names:["jacksonville"],lat:30.3322,lng:-81.6557,label:"Jacksonville, FL"},
  {names:["fort worth"],lat:32.7555,lng:-97.3308,label:"Fort Worth, TX"},
  {names:["columbus"],lat:39.9612,lng:-82.9988,label:"Columbus, OH"},
  {names:["charlotte"],lat:35.2271,lng:-80.8431,label:"Charlotte, NC"},
  {names:["san francisco","sf","the city","frisco"],lat:37.7749,lng:-122.4194,label:"San Francisco, CA"},
  {names:["seattle"],lat:47.6062,lng:-122.3321,label:"Seattle, WA"},
  {names:["denver"],lat:39.7392,lng:-104.9903,label:"Denver, CO"},
  {names:["boston"],lat:42.3601,lng:-71.0589,label:"Boston, MA"},
  {names:["nashville"],lat:36.1627,lng:-86.7816,label:"Nashville, TN"},
  {names:["portland"],lat:45.5051,lng:-122.6750,label:"Portland, OR"},
  {names:["las vegas","vegas"],lat:36.1699,lng:-115.1398,label:"Las Vegas, NV"},
  {names:["miami"],lat:25.7617,lng:-80.1918,label:"Miami, FL"},
  {names:["atlanta"],lat:33.7490,lng:-84.3880,label:"Atlanta, GA"},
  {names:["minneapolis"],lat:44.9778,lng:-93.2650,label:"Minneapolis, MN"},
  {names:["new orleans"],lat:29.9511,lng:-90.0715,label:"New Orleans, LA"},
  {names:["tampa"],lat:27.9506,lng:-82.4572,label:"Tampa, FL"},
  {names:["orlando"],lat:28.5383,lng:-81.3792,label:"Orlando, FL"},
  {names:["raleigh"],lat:35.7796,lng:-78.6382,label:"Raleigh, NC"},
  {names:["salt lake city","slc"],lat:40.7608,lng:-111.8910,label:"Salt Lake City, UT"},
  {names:["kansas city"],lat:39.0997,lng:-94.5786,label:"Kansas City, MO"},
  {names:["pittsburgh"],lat:40.4406,lng:-79.9959,label:"Pittsburgh, PA"},
  {names:["cincinnati"],lat:39.1031,lng:-84.5120,label:"Cincinnati, OH"},
  {names:["richmond"],lat:37.5407,lng:-77.4360,label:"Richmond, VA"},
  {names:["sacramento"],lat:38.5816,lng:-121.4944,label:"Sacramento, CA"},
  {names:["fresno"],lat:36.7378,lng:-119.7871,label:"Fresno, CA"},
  {names:["long beach"],lat:33.7701,lng:-118.1937,label:"Long Beach, CA"},
  {names:["oakland"],lat:37.8044,lng:-122.2712,label:"Oakland, CA"},
  {names:["berkeley"],lat:37.8716,lng:-122.2727,label:"Berkeley, CA"},
  {names:["palo alto"],lat:37.4419,lng:-122.1430,label:"Palo Alto, CA"},
  {names:["mountain view"],lat:37.3861,lng:-122.0839,label:"Mountain View, CA"},
  {names:["sunnyvale"],lat:37.3688,lng:-122.0363,label:"Sunnyvale, CA"},
  {names:["san mateo"],lat:37.5630,lng:-122.3255,label:"San Mateo, CA"},
  {names:["burlingame"],lat:37.5785,lng:-122.3480,label:"Burlingame, CA"},
  {names:["redwood city"],lat:37.4852,lng:-122.2364,label:"Redwood City, CA"},
  {names:["menlo park"],lat:37.4530,lng:-122.1817,label:"Menlo Park, CA"},
  {names:["cupertino"],lat:37.3230,lng:-122.0322,label:"Cupertino, CA"},
  {names:["campbell"],lat:37.2872,lng:-121.9400,label:"Campbell, CA"},
  {names:["los gatos"],lat:37.2358,lng:-121.9624,label:"Los Gatos, CA"},
  {names:["sausalito"],lat:37.8590,lng:-122.4852,label:"Sausalito, CA"},
  {names:["mill valley"],lat:37.9060,lng:-122.5450,label:"Mill Valley, CA"},
  {names:["tiburon"],lat:37.8874,lng:-122.4563,label:"Tiburon, CA"},
  {names:["san rafael"],lat:37.9735,lng:-122.5311,label:"San Rafael, CA"},
  {names:["pacifica"],lat:37.6138,lng:-122.4869,label:"Pacifica, CA"},
  {names:["daly city"],lat:37.6879,lng:-122.4702,label:"Daly City, CA"},
  {names:["south san francisco","ssf"],lat:37.6546,lng:-122.4077,label:"South San Francisco, CA"},
  {names:["san bruno"],lat:37.6305,lng:-122.4111,label:"San Bruno, CA"},
  {names:["millbrae"],lat:37.5985,lng:-122.3869,label:"Millbrae, CA"},
  {names:["belmont"],lat:37.5202,lng:-122.2758,label:"Belmont, CA"},
  {names:["san carlos"],lat:37.5070,lng:-122.2605,label:"San Carlos, CA"},
  {names:["foster city"],lat:37.5585,lng:-122.2711,label:"Foster City, CA"},
  {names:["half moon bay"],lat:37.4635,lng:-122.4286,label:"Half Moon Bay, CA"},
  // NYC neighborhoods
  {names:["brooklyn"],lat:40.6782,lng:-73.9442,label:"Brooklyn, NY"},
  {names:["queens"],lat:40.7282,lng:-73.7949,label:"Queens, NY"},
  {names:["bronx"],lat:40.8448,lng:-73.8648,label:"The Bronx, NY"},
  {names:["staten island"],lat:40.5795,lng:-74.1502,label:"Staten Island, NY"},
  {names:["harlem"],lat:40.8116,lng:-73.9465,label:"Harlem, NY"},
  {names:["williamsburg"],lat:40.7081,lng:-73.9571,label:"Williamsburg, NY"},
  {names:["park slope"],lat:40.6712,lng:-73.9814,label:"Park Slope, NY"},
  {names:["astoria"],lat:40.7721,lng:-73.9302,label:"Astoria, NY"},
  {names:["upper west side","uws"],lat:40.7870,lng:-73.9754,label:"Upper West Side, NY"},
  {names:["upper east side","ues"],lat:40.7736,lng:-73.9566,label:"Upper East Side, NY"},
  {names:["tribeca"],lat:40.7163,lng:-74.0086,label:"Tribeca, NY"},
  {names:["soho"],lat:40.7233,lng:-74.0030,label:"SoHo, NY"},
  {names:["west village"],lat:40.7338,lng:-74.0059,label:"West Village, NY"},
  {names:["east village"],lat:40.7265,lng:-73.9815,label:"East Village, NY"},
  // SF neighborhoods  
  {names:["mission","the mission","mission district"],lat:37.7599,lng:-122.4148,label:"The Mission, SF"},
  {names:["castro"],lat:37.7609,lng:-122.4350,label:"The Castro, SF"},
  {names:["haight","haight ashbury"],lat:37.7692,lng:-122.4481,label:"Haight-Ashbury, SF"},
  {names:["richmond","inner richmond","outer richmond"],lat:37.7795,lng:-122.4747,label:"Richmond District, SF"},
  {names:["sunset","inner sunset","outer sunset"],lat:37.7528,lng:-122.4823,label:"Sunset District, SF"},
  {names:["noe valley"],lat:37.7502,lng:-122.4337,label:"Noe Valley, SF"},
  {names:["potrero hill"],lat:37.7610,lng:-122.4006,label:"Potrero Hill, SF"},
  {names:["dogpatch"],lat:37.7574,lng:-122.3904,label:"Dogpatch, SF"},
  {names:["soma","south of market"],lat:37.7785,lng:-122.4056,label:"SoMa, SF"},
  {names:["north beach"],lat:37.8061,lng:-122.4103,label:"North Beach, SF"},
  {names:["marina","marina district"],lat:37.8030,lng:-122.4378,label:"Marina District, SF"},
  {names:["pacific heights","pac heights"],lat:37.7925,lng:-122.4382,label:"Pacific Heights, SF"},
  {names:["hayes valley"],lat:37.7764,lng:-122.4242,label:"Hayes Valley, SF"},
  {names:["tenderloin"],lat:37.7835,lng:-122.4141,label:"Tenderloin, SF"},
  {names:["chinatown"],lat:37.7941,lng:-122.4078,label:"Chinatown, SF"},
  {names:["excelsior"],lat:37.7238,lng:-122.4267,label:"Excelsior, SF"},
  {names:["bernal heights"],lat:37.7390,lng:-122.4153,label:"Bernal Heights, SF"},
  {names:["glen park"],lat:37.7334,lng:-122.4339,label:"Glen Park, SF"},
  // LA neighborhoods
  {names:["silverlake","silver lake"],lat:34.0869,lng:-118.2701,label:"Silver Lake, LA"},
  {names:["echo park"],lat:34.0778,lng:-118.2606,label:"Echo Park, LA"},
  {names:["los feliz"],lat:34.1080,lng:-118.2917,label:"Los Feliz, LA"},
  {names:["west hollywood","weho"],lat:34.0900,lng:-118.3617,label:"West Hollywood, LA"},
  {names:["santa monica"],lat:34.0195,lng:-118.4912,label:"Santa Monica, CA"},
  {names:["venice"],lat:33.9850,lng:-118.4695,label:"Venice, CA"},
  {names:["culver city"],lat:34.0211,lng:-118.3965,label:"Culver City, CA"},
  {names:["pasadena"],lat:34.1478,lng:-118.1445,label:"Pasadena, CA"},
  // Chicago neighborhoods
  {names:["wicker park"],lat:41.9081,lng:-87.6792,label:"Wicker Park, Chicago"},
  {names:["lincoln park"],lat:41.9239,lng:-87.6478,label:"Lincoln Park, Chicago"},
  {names:["logan square"],lat:41.9206,lng:-87.7036,label:"Logan Square, Chicago"},
  {names:["andersonville"],lat:41.9806,lng:-87.6689,label:"Andersonville, Chicago"},
];

function geocodeLocation(query) {
  const q = query.trim();
  const isZip = /^\d{5}$/.test(q);
  
  if (isZip) {
    const hit = ZIP_DB[q];
    if (hit) return Promise.resolve(hit);
    return Promise.reject(new Error(`Zip code ${q} not in database. Try typing your city name instead.`));
  }

  // Exact city/neighborhood/alias match only. This is a fast-path for known
  // (mostly US) names — anything that isn't an exact hit falls through to the
  // worldwide Nominatim search so it can disambiguate. The earlier fuzzy
  // prefix/substring matching here hijacked international towns and snapped
  // them onto a US city before Nominatim was ever consulted: "Berkeley
  // Springs" → Berkeley CA, "Bern" → Bernal Heights SF, "Oakland Gardens" →
  // Oakland CA, etc. Exact-only keeps the instant US fast-path while letting
  // every other place resolve correctly worldwide.
  const lower = q.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();
  for (const city of CITY_DB) {
    if (city.names.includes(lower)) {
      return Promise.resolve({ lat: city.lat, lng: city.lng, label: city.label });
    }
  }

  return Promise.reject(new Error(`"${q}" not recognized. Trying worldwide search…`));
}

// ── Indoor / paid detection ───────────────────────────────────────────────────
// Known chains — exact substring match
const KNOWN_INDOOR_PAID_CHAINS = [
  "chuck e. cheese", "chuck e cheese", "pump it up", "sky zone", "urban air",
  "altitude trampoline", "the little gym", "my gym", "kidville",
  "color me mine", "legoland discovery", "discovery children's museum",
  "children's museum", "kiddie academy", "creation station",
  "amazing jakes", "scene 75", "jump street", "rockin' jump", "rockin jump",
  "defy", "clip 'n climb", "clip n climb", "boundless adventures",
  "peek-a-boo", "peekaboo", "jumpity", "bumpity",
  "kidz play", "kiddie play", "lil' monkey", "lil monkey",
  "treehouse kids", "playtown", "play town",
];

// Generic keywords that strongly suggest an indoor pay-to-play venue
const INDOOR_PLAY_KEYWORDS = [
  "play factory", "play cafe", "play café", "play centre", "play center",
  "play zone", "play world", "play land", "playzone", "playworld",
  "soft play", "indoor play", "indoor playground", "indoor park",
  "bounce house", "bounce zone", "bounce world", "bounce land",
  "jump zone", "jump world", "jump land", "jump park",
  "trampoline park", "trampoline zone", "trampoline world",
  "fun factory", "fun zone", "fun world", "fun land",
  "adventure zone", "adventure play",
  "toddler gym", "kiddie gym", "kids gym",
  "inflatable park", "inflatable world",
  "party center", "party centre", "indoor party",
  "climbing gym", "bouldering gym", "climbing wall",
  "climbing centre", "climbing center",
];

// Types returned by Google Places v1 that indicate indoor/paid venues
const INDOOR_PAID_PLACE_TYPES = new Set([
  "amusement_center", "amusement_park", "bowling_alley",
  "gym", "fitness_center",
]);

function detectIndoorAndPaid(name, tags = {}, placeTypes = []) {
  const n = (name || "").toLowerCase();
  // Word-boundary check for standalone "indoor" (e.g. "Kiddiebeans Indoor Party Center")
  const hasIndoorWord = /\bindoor\b/.test(n);
  const isChain   = KNOWN_INDOOR_PAID_CHAINS.some(c => n.includes(c));
  const isKeyword = INDOOR_PLAY_KEYWORDS.some(k => n.includes(k));
  const isType    = placeTypes.some(t => INDOOR_PAID_PLACE_TYPES.has(t));
  const feeTag    = tags.fee === "yes";
  const admissionTag = tags.admission && !["no","free","0"].includes(tags.admission);
  const isIndoor  = isChain || isKeyword || hasIndoorWord || isType || tags.indoor === "yes" || tags.location === "indoor" || tags.access === "customers";
  const isPaid    = isChain || isKeyword || isType || feeTag || admissionTag || tags.access === "customers";
  return { isIndoor, isPaid };
}

// ── Feature icons / labels ────────────────────────────────────────────────────
// What's new banner. Bump WHATS_NEW_VERSION whenever WHATS_NEW_ITEMS changes;
// anyone who has not dismissed that exact version sees the banner once. Keep it
// to a handful of user-visible changes — this is a nudge, not a changelog.
const WHATS_NEW_VERSION = "2026-09-09";
const WHATS_NEW_ITEMS = [
  "🎠 Carousels now show up as a feature on playgrounds",
  "⛳ New Mini Golf mode — find courses near you",
  "🛝 Arcades and bars no longer show up as playgrounds",
  "🗺️ New map with clearer streets and labels",
];

const FEATURES = [
  { id: "climbing", label: "Climbing", icon: "🧗" },
  { id: "swings", label: "Swings", icon: "🪁" },
  { id: "baby_swing", label: "Baby Swing", icon: "🍼" },
  { id: "slide", label: "Slides", icon: "🛝" },
  { id: "spinner", label: "Spinners", icon: "🌀" },
  { id: "field", label: "Field", icon: "🌿" },
  { id: "sandbox", label: "Sandbox", icon: "🏖️" },
  { id: "water", label: "Splash", icon: "💧" },
  { id: "shade", label: "Shade", icon: "🌳" },
  { id: "restrooms", label: "Restrooms", icon: "🚻" },
  { id: "drinking_water", label: "Fountain", icon: "🚰" },
  { id: "pump_track", label: "Pump Track", icon: "🚵" },
  { id: "bmx", label: "BMX", icon: "🚴" },
  { id: "skatepark", label: "Skatepark", icon: "🛹" },
  { id: "seating", label: "Seating", icon: "🪑" },
  { id: "carousel", label: "Carousel", icon: "🎠" },
  { id: "mini_golf", label: "Mini Golf", icon: "⛳" },
];

const WEATHER_ICONS = {
  Clear: "☀️", PartlyCloudy: "🌤️", Clouds: "⛅", Rain: "🌧️", Drizzle: "🌦️",
  Thunderstorm: "⛈️", Snow: "❄️", Mist: "🌫️", Fog: "🌁", Haze: "🌫️",
};

// ── Claude AI analysis (server-side cached, shared across users) ──────────────
async function analyzePlaygroundWithClaude({ playground, weather, sunInfo, selectedFeatures, activityMode, visitTime }) {
  const t = visitTime instanceof Date ? visitTime : new Date();
  const localTimeStr = t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  const localHour = t.getHours();
  const res = await fetch("/api/analysis", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playground, weather, sunInfo, selectedFeatures, localTimeStr, localHour, activityMode }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Analysis error ${res.status}`);
  if (data.cached) dlog(`[Analysis] cache hit for ${playground.name}`);
  return data;
}

// ── Haversine distance (miles) ───────────────────────────────────────────────
function haversine(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 +
    Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// ── Point-in-polygon (ray casting) ───────────────────────────────────────────
function pointInPolygon(lat, lng, polygon) {
  // polygon: array of {lat, lon}
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lon, yi = polygon[i].lat;
    const xj = polygon[j].lon, yj = polygon[j].lat;
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

// ── Parse OSM tags → feature list ────────────────────────────────────────────
function osmTagsToFeatures(tags = {}) {
  const f = [];
  const t = (k) => (tags[k] || "").toLowerCase();
  if (t("playground") === "structure" || t("climbing_frame") === "yes" || tags["playground:climbing"] === "yes") f.push("climbing");
  if (tags["playground:swing"] === "yes" || t("swings") === "yes") f.push("swings");
  if (tags["playground:slide"] === "yes" || t("slide") === "yes") f.push("slide");
  if (tags["playground:roundabout"] === "yes" || tags["playground:spinner"] === "yes") f.push("spinner");
  if (tags["playground:sandpit"] === "yes" || t("sandbox") === "yes" || t("sandpit") === "yes") f.push("sandbox");
  if (tags["playground:waterplay"] === "yes" || t("water_play") === "yes") f.push("water");
  if (t("shade") === "yes" || tags["trees"] === "yes") f.push("shade");
  if (tags.toilets === "yes" || t("amenity") === "toilets") f.push("restrooms");
  if (tags.drinking_water === "yes" || t("amenity") === "drinking_water") f.push("drinking_water");
  if (t("sport") === "bmx" || t("sport") === "pump_track" || t("leisure") === "pump_track") f.push("pump_track");
  if (t("bench") === "yes" || t("picnic_table") === "yes" || t("leisure") === "picnic_table" || t("amenity") === "bench") f.push("seating");
  // Guess water from name
  const name = (tags.name || "").toLowerCase();
  if (name.includes("swim") || name.includes("splash") || name.includes("aquatic")) f.push("water");
  // Default: if no features detected, add common ones for a basic playground
  if (f.length === 0) { f.push("swings", "slide"); }
  return [...new Set(f)];
}

// ── Playground result cache (localStorage, 24-hour TTL, 7-day stale-while-revalidate) ──
const CACHE_TTL = 24 * 60 * 60 * 1000;
const CACHE_STALE_TTL = 7 * 24 * 60 * 60 * 1000; // keep stale entries up to 7 days
function cacheKey(lat, lng, radius) {
  // Round to 2dp (~1.1km grid) so minor GPS drift doesn't bust the cache
  return `pg_cache_${lat.toFixed(2)}_${lng.toFixed(2)}_${radius}`;
}
function cacheGet(lat, lng, radius) {
  try {
    const raw = localStorage.getItem(cacheKey(lat, lng, radius));
    if (!raw) return null;
    const { ts, results } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_STALE_TTL) { localStorage.removeItem(cacheKey(lat, lng, radius)); return null; }
    const fresh = Date.now() - ts <= CACHE_TTL;
    dlog(`[Cache] ${fresh ? "hit" : "stale"} for ${lat.toFixed(2)},${lng.toFixed(2)} r=${radius} (${results.length} results)`);
    return { results, fresh };
  } catch { return null; }
}
function cacheSet(lat, lng, radius, results) {
  try {
    localStorage.setItem(cacheKey(lat, lng, radius), JSON.stringify({ ts: Date.now(), results }));
  } catch (e) {
    for (const k of Object.keys(localStorage).filter(k => k.startsWith("pg_cache_"))) localStorage.removeItem(k);
    try { localStorage.setItem(cacheKey(lat, lng, radius), JSON.stringify({ ts: Date.now(), results })); } catch {}
  }
}

// ── Reverse geocode cache (localStorage, 30-day TTL) ─────────────────────────
const GEO_TTL = 30 * 24 * 60 * 60 * 1000;
function geoCacheKey(lat, lng) { return `pg_geo_${lat.toFixed(4)}_${lng.toFixed(4)}`; }
function geoCacheGet(lat, lng) {
  try {
    const raw = localStorage.getItem(geoCacheKey(lat, lng));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > GEO_TTL) { localStorage.removeItem(geoCacheKey(lat, lng)); return null; }
    return data;
  } catch { return null; }
}
function geoCacheSet(lat, lng, data) {
  try { localStorage.setItem(geoCacheKey(lat, lng), JSON.stringify({ ts: Date.now(), data })); } catch {}
}

// ── Activity modes config (Side Quests) ──────────────────────────────────────
const CONDITION_TAGS = [  // playground default
  { id: "well_maintained", label: "Well kept", icon: "✨" },
  { id: "needs_repair",    label: "Broken equipment", icon: "🔧" },
  { id: "rust_splinters",  label: "Rust / splinters", icon: "🪵" },
  { id: "trash",           label: "Trashy / dirty", icon: "🗑️" },
  { id: "crowded",         label: "Very crowded", icon: "👥" },
];
const CONDITION_TAGS_BY_MODE = {
  skatepark:    [{ id: "well_maintained", label: "Smooth & clean",      icon: "✨" }, { id: "cracked",        label: "Cracked surface",    icon: "🔧" }, { id: "wet_slippery",  label: "Wet / slippery",    icon: "💧" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  pump_track:   [{ id: "well_maintained", label: "Well groomed",        icon: "✨" }, { id: "rough_rutted",   label: "Rough / rutted",     icon: "🔧" }, { id: "muddy",         label: "Muddy / loose",     icon: "🌧️" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  bmx:          [{ id: "well_maintained", label: "Well maintained",     icon: "✨" }, { id: "rough_rutted",   label: "Rough / rutted",     icon: "🔧" }, { id: "muddy",         label: "Muddy / loose",     icon: "🌧️" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  mtb:          [{ id: "well_maintained", label: "Well maintained",     icon: "✨" }, { id: "overgrown",      label: "Overgrown",          icon: "🌿" }, { id: "muddy",         label: "Muddy / washed out",icon: "🌧️" }, { id: "trail_damage",  label: "Trail damage",       icon: "⚠️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  hiking:       [{ id: "well_maintained", label: "Well maintained",     icon: "✨" }, { id: "overgrown",      label: "Overgrown",          icon: "🌿" }, { id: "muddy",         label: "Muddy / slippery",  icon: "🌧️" }, { id: "trail_damage",  label: "Trail damage",       icon: "⚠️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  climbing:     [{ id: "well_maintained", label: "Clean & dry",         icon: "✨" }, { id: "holds_damaged",  label: "Holds damaged",      icon: "🔧" }, { id: "overgrown",     label: "Overgrown",         icon: "🌿" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  dog_park:     [{ id: "well_maintained", label: "Well maintained",     icon: "✨" }, { id: "poop",           label: "Lots of poop",       icon: "💩" }, { id: "muddy",         label: "Muddy / puddly",    icon: "🌧️" }, { id: "needs_repair",  label: "Fence issues",       icon: "🔧" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  basketball:   [{ id: "well_maintained", label: "Court is great",      icon: "✨" }, { id: "hoop_damaged",   label: "Hoop damaged",       icon: "🏀" }, { id: "cracked",       label: "Cracked surface",   icon: "🔧" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  splash_pad:   [{ id: "well_maintained", label: "All working",         icon: "✨" }, { id: "not_working",    label: "Not working",        icon: "🚿" }, { id: "wet_slippery",  label: "Slippery",          icon: "💧" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  pool:         [{ id: "well_maintained", label: "Great condition",     icon: "✨" }, { id: "water_murky",    label: "Water murky",        icon: "🌊" }, { id: "needs_repair",  label: "Features broken",   icon: "🔧" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  nature_play:  [{ id: "well_maintained", label: "Beautiful",           icon: "✨" }, { id: "overgrown",      label: "Overgrown",          icon: "🌿" }, { id: "muddy",         label: "Muddy / wet",       icon: "🌧️" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  beach:        [{ id: "well_maintained", label: "Beautiful",           icon: "✨" }, { id: "rough_surf",     label: "Rough surf",         icon: "🌊" }, { id: "seaweed",       label: "Seaweed / jellyfish",icon: "🪼" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  soccer:       [{ id: "well_maintained", label: "Great field",         icon: "✨" }, { id: "rough_patchy",   label: "Rough / patchy",     icon: "⚽" }, { id: "muddy",         label: "Muddy",             icon: "🌧️" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  baseball:     [{ id: "well_maintained", label: "Great field",         icon: "✨" }, { id: "rough_infield",  label: "Rough infield",      icon: "⚾" }, { id: "overgrown",     label: "Overgrown",         icon: "🌿" }, { id: "trash",         label: "Trashy",             icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  bowling:      [{ id: "well_maintained", label: "Lanes are great",      icon: "✨" }, { id: "needs_repair",   label: "Lane / pin issues",  icon: "🔧" }, { id: "no_supplies",   label: "Out of rental shoes",icon: "👟" }, { id: "trash",         label: "Dirty",              icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  adult_play:   [{ id: "well_maintained", label: "Great vibe",          icon: "✨" }, { id: "packed_house",   label: "Packed house",       icon: "🍺" }, { id: "too_noisy",     label: "Too noisy",         icon: "🔊" }, { id: "trash",         label: "Dirty",              icon: "🗑️" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
  psearch:      [{ id: "well_maintained", label: "Spotless",            icon: "✨" }, { id: "no_supplies",    label: "Out of paper / soap",icon: "🧻" }, { id: "locked",        label: "Locked",            icon: "🔒" }, { id: "needs_repair",  label: "Broken / gross",     icon: "🔧" }, { id: "crowded", label: "Very crowded", icon: "👥" }],
};
// All unique tags across all modes — used to resolve historical report IDs
const ALL_CONDITION_TAGS = Object.values(CONDITION_TAGS_BY_MODE).flat()
  .concat(CONDITION_TAGS)
  .filter((t, i, a) => a.findIndex(x => x.id === t.id) === i);
function getConditionTags(mode) {
  return CONDITION_TAGS_BY_MODE[mode] || CONDITION_TAGS;
}

const ACTIVITY_MODES = [
  {
    id: "skatepark", label: "Skatepark", icon: "🛹", header: "Skateparks Near",
    featureId: "skatepark", defaultRadius: 16000,
    osmTags: [["leisure","skateboard_park"]],
    placesQueries: ["skatepark","skate park"],
  },
  {
    id: "pump_track", label: "Pump Track", icon: "🚵", header: "Pump Tracks Near",
    featureId: "pump_track", bikeMode: true, defaultRadius: 16000,
    osmTags: [["sport","pump_track"],["leisure","pump_track"]],
    placesQueries: ["pump track"],
    requireKeywords: ["pump", "bmx", "bike park", "bike skills", "mountain bike", "mtb", "dirt jump", "flow trail", "skills park", "cycle track"],
  },
  {
    id: "bmx", label: "BMX Track", icon: "🚴", header: "BMX Tracks Near",
    featureId: "bmx", bikeMode: true, defaultRadius: 16000, hidden: true,
    osmTags: [["sport","bmx"]],
    placesQueries: ["bmx track","bmx park"],
  },
  {
    id: "mtb", label: "MTB Trails", icon: "🏔️", header: "Mountain Bike Trails Near",
    featureId: "mtb", bikeMode: true, defaultRadius: 40000, hidden: true,
    osmTags: [["highway","trailhead"]],
    placesQueries: ["mountain bike trailhead","MTB trailhead","bike trail entrance"],
  },
  {
    id: "hiking", label: "Family Hikes", icon: "🥾", header: "Family Hikes Near",
    featureId: "hiking", defaultRadius: 40000, hidden: true,
    osmTags: [["highway","trailhead"]],
    placesQueries: ["hiking trailhead","nature trailhead","trail access point"],
  },
  {
    id: "climbing", label: "Climbing", icon: "🧗", header: "Climbing Near",
    findingLabel: "climbing spots", defaultRadius: 16000, hidden: true,
    featureId: "climbing",
    osmTags: [["sport","climbing"],["natural","cliff"]],
    placesQueries: ["rock climbing area","outdoor bouldering","climbing crag","climbing gym","bouldering gym","indoor climbing wall"],
  },
  {
    id: "dog_park", label: "Dog Park", icon: "🐕", header: "Dog Parks Near",
    featureId: "dog_park", defaultRadius: 8000, hidden: true,
    osmTags: [["leisure","dog_park"]],
    placesQueries: ["dog park","off leash dog area"],
  },
  {
    id: "basketball", label: "Basketball", icon: "🏀", header: "Basketball Courts Near",
    featureId: "basketball", defaultRadius: 8000, hidden: true,
    osmTags: [["sport","basketball"]],
    placesQueries: ["basketball court","outdoor basketball"],
  },
  {
    id: "splash_pad", label: "Splash Pad", icon: "💦", header: "Splash Pads Near", hidden: true, defaultRadius: 8000,
    featureId: "water",
    osmTags: [["amenity","water_park"],["leisure","water_park"]],
    placesQueries: ["splash pad","spray park","water playground"],
  },
  {
    id: "pool", label: "Public Pool", icon: "🏊", header: "Public Pools Near", hidden: true, defaultRadius: 16000,
    featureId: "pool",
    osmTags: [["leisure","swimming_pool"]],
    filterPrivateAccess: true, // skip OSM elements with access=private/customers/members
    placesQueries: ["public swimming pool","aquatic center","outdoor pool"],
    requireKeywords: ["pool", "aquatic", "swim", "natatorium"],
    excludeKeywords: ["hotel", "inn", "motel", "resort", "lodge", "suites", "apartment", "condo", "residence", "country club", "yacht club", "private"],
  },
  {
    id: "nature_play", label: "Nature Play", icon: "🌳", header: "Nature Play Areas Near", hidden: true, defaultRadius: 8000,
    featureId: "nature_play",
    osmTags: [["playground:theme","nature"],["leisure","nature_reserve"]],
    placesQueries: ["nature play area","forest school","nature playground"],
  },
  {
    id: "beach", label: "Beach", icon: "🏖️", header: "Beaches Near", findingLabel: "beaches",
    featureId: "beach", defaultRadius: 40000,
    osmTags: [["natural","beach"]],
    placesQueries: ["beach","sandy beach"],
    dedupMiles: 0.5,
  },
  {
    id: "soccer", label: "Soccer Field", icon: "⚽", header: "Soccer Fields Near", hidden: true, defaultRadius: 8000,
    featureId: "soccer",
    osmTags: [["sport","soccer"],["sport","football"]],
    placesQueries: ["soccer field","soccer pitch","football field"],
  },
  {
    id: "baseball", label: "Baseball", icon: "⚾", header: "Baseball Fields Near", hidden: true, defaultRadius: 8000,
    findingLabel: "baseball fields",
    featureId: "baseball",
    osmTags: [["sport","baseball"]],
    placesQueries: ["baseball field","baseball diamond","little league field","softball field"],
  },
  {
    id: "tennis", label: "Tennis", icon: "🎾", header: "Tennis Courts Near", hidden: true, defaultRadius: 8000,
    findingLabel: "tennis courts",
    featureId: "tennis",
    // Anchored regex so multi-value tags (sport=tennis;pickleball) match but
    // sport=table_tennis doesn't.
    osmTags: [["sport","(^|;)tennis($|;)","~"]],
    placesQueries: ["tennis court","public tennis courts"],
    // Text search ranks plain parks for "tennis court" queries (e.g.
    // Frontierland Park, no courts) — only keep Places results that say so.
    placesRequireKeywords: ["tennis"],
    detectSchools: true,
  },
  {
    id: "pickleball", label: "Pickleball", icon: "🏓", header: "Pickleball Courts Near", hidden: true, defaultRadius: 8000,
    findingLabel: "pickleball courts",
    featureId: "pickleball",
    osmTags: [["sport","(^|;)pickleball($|;)","~"]],
    placesQueries: ["pickleball court","pickleball courts"],
    placesRequireKeywords: ["pickleball"],
    detectSchools: true,
  },
  {
    id: "bowling", label: "Bowling", icon: "🎳", header: "Bowling Alleys Near",
    findingLabel: "bowling alleys",
    featureId: "bowling", hidden: true, defaultRadius: 16000,
    osmTags: [["leisure","bowling_alley"]],
    placesQueries: ["bowling alley","bowling center"],
  },
  {
    id: "mini_golf", label: "Mini Golf", icon: "⛳", header: "Mini Golf Near",
    findingLabel: "mini golf courses",
    featureId: "mini_golf", hidden: true, defaultRadius: 16000,
    osmTags: [["leisure","miniature_golf"]],
    placesQueries: ["mini golf"],
    // Google types most real mini golf venues miniature_golf_course, and files
    // family entertainment centres under amusement_park (Boomers Livermore) or
    // amusement_center (Scandia Fun Center) — all three verified against live
    // Places results across five metros. An indoor golf simulator carries none
    // of them, which is what keeps it out.
    requireTypes: ["miniature_golf_course", "amusement_park", "amusement_center"],
    // ...and some venues Google types as nothing useful at all: "Mini Putt
    // Putt" is only point_of_interest + establishment, so no positive type
    // gate can keep it. A name that says mini golf outright is its own
    // evidence, so it redeems a venue the types would drop. Deliberately
    // narrow — it wants an explicit mini-golf phrase, so "100 Yards Golf" and
    // "Haggin Oaks Golf Complex" still fall out. Unlike #140's arcade bug this
    // can't admit a whole unrelated category: it never overrides excludeTypes,
    // so an adults-only "Playground Mini Golf" is still dropped on its bar tag.
    requireTypesNameRe: /\b(mini|miniature)[\s-]*golf\b|\bminigolf\b|\bputt[\s-]?putt\b/i,
    // indoor_golf_course is never mini golf, on either path — belt and braces
    // so a simulator can't ride in on a "mini golf" in its name.
    excludeTypes: ["bar", "pub", "night_club", "indoor_golf_course"],
  },
  {
    id: "picnic", label: "Picnic Spots", icon: "🧺", header: "Picnic Spots Near", hidden: true, defaultRadius: 8000,
    findingLabel: "picnic spots",
    featureId: "seating",
    osmTags: [["tourism","picnic_site"],["leisure","picnic_area"],["leisure","picnic_table"]],
    placesQueries: ["picnic area","picnic tables","picnic shelter"],
  },
  {
    id: "adult_play", label: "Brewery Patios", icon: "🍻", header: "Family-Friendly Spots Near",
    featureId: "adult_play", secret: true, defaultRadius: 16000,
    // amenity=biergarten is the standard OSM tag for a beer garden (e.g. Humble
    // Sea Brewing, Pacifica); leisure=beer_garden is the less-common variant.
    // Include both so biergarten-tagged taprooms aren't missed.
    osmTags: [["amenity","biergarten"],["leisure","beer_garden"],["amenity","pub"],["amenity","bar"]],
    placesQueries: ["brewery outdoor seating","beer garden","winery family friendly","taproom patio","restaurant outdoor kids"],
    excludeKeywords: ["nightclub","strip","gentlemen","lounge bar","sports bar"],
    dedupMiles: 0.15,
  },
  {
    id: "psearch", label: "P-Search", icon: "🚻", header: "Relief Near",
    featureId: "restrooms", secret: true, defaultRadius: 5000,
    osmTags: [["amenity","toilets"]],
    placesQueries: ["public restroom","public bathroom","public toilet"],
    findingLabel: "restrooms",
    dedupMiles: 0.05,
  },
];

// ── Spot label/emoji helper — used throughout detail view ────────────────────
const SPOT_INFO = {
  skatepark:   { noun: "skatepark",     facing: "skatepark", emoji: "🛹", rateLabel: "Skatepark" },
  pump_track:  { noun: "track",         facing: "track",     emoji: "🚴", rateLabel: "Track" },
  bmx:         { noun: "track",         facing: "track",     emoji: "🚴", rateLabel: "Track" },
  mtb:         { noun: "trail",         facing: "trail",     emoji: "🚵", rateLabel: "Trail" },
  hiking:      { noun: "trail",         facing: "trail",     emoji: "🥾", rateLabel: "Trail" },
  climbing:    { noun: "climbing spot", facing: "crag",      emoji: "🧗", rateLabel: "Climbing Spot" },
  dog_park:    { noun: "park",          facing: "park",      emoji: "🐕", rateLabel: "Park" },
  basketball:  { noun: "court",         facing: "court",     emoji: "🏀", rateLabel: "Court" },
  splash_pad:  { noun: "splash pad",    facing: "splash pad",emoji: "💦", rateLabel: "Splash Pad" },
  pool:        { noun: "pool",          facing: "pool",      emoji: "🏊", rateLabel: "Pool" },
  disc_golf:   { noun: "course",        facing: "course",    emoji: "🥏", rateLabel: "Course" },
  nature_play: { noun: "nature area",   facing: "area",      emoji: "🌳", rateLabel: "Nature Area" },
  bowling:     { noun: "alley",         facing: "alley",     emoji: "🎳", rateLabel: "Alley" },
  ice_skating: { noun: "rink",          facing: "rink",      emoji: "⛸️", rateLabel: "Rink" },
  beach:       { noun: "beach",         facing: "beach",     emoji: "🏖️", rateLabel: "Beach" },
  soccer:      { noun: "field",         facing: "field",     emoji: "⚽", rateLabel: "Field" },
  baseball:    { noun: "field",         facing: "field",     emoji: "⚾", rateLabel: "Field" },
  tennis:      { noun: "court",         facing: "court",     emoji: "🎾", rateLabel: "Court" },
  pickleball:  { noun: "court",         facing: "court",     emoji: "🏓", rateLabel: "Court" },
  adult_play:  { noun: "spot",          facing: "patio",     emoji: "🍻", rateLabel: "Spot" },
  psearch:     { noun: "restroom",      facing: "restroom",  emoji: "🚻", rateLabel: "Restroom" },
};
const PLAYGROUND_SPOT = { noun: "playground", facing: "playground", emoji: "🛝", rateLabel: "Playground" };
function getSpotInfo(activityMode, name) {
  if (activityMode && SPOT_INFO[activityMode]) return SPOT_INFO[activityMode];
  // Name-based detection for regular playground mode
  const n = (name || "").toLowerCase();
  if (n.includes("skate")) return SPOT_INFO.skatepark;
  if (n.includes("basketball") || n.includes(" court")) return SPOT_INFO.basketball;
  if (n.includes("soccer") || n.includes("football field")) return SPOT_INFO.soccer;
  if (n.includes("baseball") || n.includes("softball") || n.includes("diamond")) return SPOT_INFO.baseball;
  if (n.includes("pool") || n.includes("swim")) return SPOT_INFO.pool;
  if (n.includes("splash") || n.includes("spray pad")) return SPOT_INFO.splash_pad;
  if (n.includes("disc golf")) return SPOT_INFO.disc_golf;
  if (n.includes("bmx")) return SPOT_INFO.bmx;
  if (n.includes("pump track")) return SPOT_INFO.pump_track;
  if (n.includes("dog park") || n.includes("off leash") || n.includes("off-leash")) return SPOT_INFO.dog_park;
  if (n.includes("bowling")) return SPOT_INFO.bowling;
  return PLAYGROUND_SPOT;
}

// Same keyword tier the server uses for playground school detection
const SCHOOL_NAME_RE = /\b(school|elementary|middle|high\s+school|academy|kindergarten)\b/i;

// ── Generic activity spot fetcher ─────────────────────────────────────────────
async function fetchActivitySpots(modeId, lat, lng, minRadius = 8000, onBatch = null) {
  const mode = ACTIVITY_MODES.find(m => m.id === modeId);
  if (!mode) return [];

  // act3: school detection + Places keyword gate changed result shape
  const CACHE_KEY = `act3_${modeId}_${lat.toFixed(2)}_${lng.toFixed(2)}_${minRadius}`;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const { ts, results } = JSON.parse(raw);
      if (Date.now() - ts < 15 * 60 * 1000) { dlog(`[Activity:${modeId}] cache hit`); return results; }
    }
  } catch {}

  const dirs = ["North","Northeast","East","Southeast","South","Southwest","West","Northwest"];
  const toResult = (elLat, elLng, name, featureId, source, id) => {
    const dist = haversine(lat, lng, elLat, elLng);
    const bearing = Math.atan2(elLng - lng, elLat - lat) * 180 / Math.PI;
    const facing = dirs[Math.round(((bearing + 360) % 360) / 45) % 8];
    return { id, osmId: id, osmType: "node", name: name || (mode.secret ? null : `${mode.label} (${facing})`), lat: elLat, lng: elLng, distance: dist.toFixed(1), facing, features: [featureId || mode.featureId], isSchool: false, source };
  };


  let results = [];
  // School campus polygons for detectSchools modes (tennis, pickleball) —
  // same approach as the playground path: way geometry + point-in-polygon.
  let schoolPolygons = [];

  // OSM query
  if (mode.osmTags.length > 0) {
    try {
      // A third tuple element of "~" switches the tag match to an Overpass
      // regex — needed for multi-value tags like sport=tennis;pickleball.
      const lines = mode.osmTags.flatMap(([k, v, op]) => [
        `node["${k}"${op === "~" ? "~" : "="}"${v}"](around:${minRadius},${lat},${lng});`,
        `way["${k}"${op === "~" ? "~" : "="}"${v}"](around:${minRadius},${lat},${lng});`,
        `relation["${k}"${op === "~" ? "~" : "="}"${v}"](around:${minRadius},${lat},${lng});`,
      ]).join("\n  ");
      const schoolQuery = mode.detectSchools
        ? `\nway["amenity"~"school|kindergarten"](around:${minRadius},${lat},${lng});\nout geom;`
        : "";
      const query = `[out:json][timeout:30];\n(\n  ${lines}\n);\nout center tags;${schoolQuery}`;
      const res = await fetch("/api/overpass", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ data: query }) });
      const data = await res.json();
      schoolPolygons = (data.elements || []).filter(el => {
        const t = el.tags || {};
        return (t.amenity === "school" || t.amenity === "kindergarten") &&
          el.type === "way" && Array.isArray(el.geometry) && el.geometry.length > 2;
      }).map(el => ({ name: el.tags?.name || null, geometry: el.geometry }));
      results = (data.elements || []).map(el => {
        // School campus ways are reference data, not venues
        if (el.tags?.amenity === "school" || el.tags?.amenity === "kindergarten") return null;
        const elLat = el.lat ?? el.center?.lat;
        const elLng = el.lon ?? el.center?.lon;
        if (!elLat || !elLng) return null;
        const elName = (el.tags?.name || "").toLowerCase();
        if (mode.excludeKeywords?.some(k => elName.includes(k))) return null;
        // Skip private/members-only facilities when mode requires public access
        const access = el.tags?.access;
        if (mode.filterPrivateAccess && access && !["yes","public","permissive"].includes(access)) return null;
        // Skip unnamed results when requireKeywords is set (no name to verify)
        if (mode.requireKeywords && el.tags?.name && !mode.requireKeywords.some(k => elName.includes(k))) return null;
        const { isIndoor: detectedIndoor, isPaid: detectedPaid } = detectIndoorAndPaid(el.tags?.name || "", el.tags || {});
        // For climbing: sports_centre + any building tag are reliable indoor
        // signals (climbing gyms in OSM rarely have indoor=yes but almost
        // always have one of these). A website is also a strong indoor signal
        // — commercial gyms have sites; outdoor crags rarely do.
        const website = el.tags?.website || el.tags?.["contact:website"] || null;
        const climbingGymSignal = modeId === "climbing" && (
          el.tags?.leisure === "sports_centre" ||
          el.tags?.leisure === "fitness_centre" ||
          (el.tags?.building && el.tags.building !== "no") ||
          !!website
        );
        const isIndoor = detectedIndoor || climbingGymSignal;
        // Indoor climbing gyms charge admission.
        const isPaid = detectedPaid || (modeId === "climbing" && isIndoor);
        let isSchool = false, nearestSchool = null;
        if (mode.detectSchools) {
          const campus = schoolPolygons.find(s => pointInPolygon(elLat, elLng, s.geometry));
          if (campus) { isSchool = true; nearestSchool = campus.name; }
          else if (el.tags?.name && SCHOOL_NAME_RE.test(el.tags.name)) isSchool = true;
        }
        return { ...toResult(elLat, elLng, el.tags?.name, mode.featureId, "osm", `${modeId}-${el.type}-${el.id}`), isIndoor, isPaid, website, isSchool, nearestSchool };
      }).filter(Boolean);
    } catch (e) { console.warn(`[Activity:${modeId}] OSM failed:`, e.message); }
  }

  // Show OSM results immediately while Places loads in the background
  if (onBatch && results.length > 0) onBatch([...results]);

  // Fuzzy dedup helpers — merge same-venue duplicates that the strict
  // proximity check (dedupMiles, default 0.1 mi) misses. Two results within
  // fuzzyDedupMiles that share a multi-letter type token from placesQueries
  // (e.g. "skatepark", "pump track", "soccer field") are treated as the same
  // physical venue, and the longer/more descriptive name wins.
  const fuzzyDedupMiles = mode.fuzzyDedupMiles ?? 0.25;
  const TYPE_TOKENS = (mode.placesQueries || []).map(q => q.toLowerCase()).filter(t => t.length >= 5);
  const sharesTypeToken = (a, b) => {
    if (!a || !b) return false;
    const aN = a.toLowerCase(), bN = b.toLowerCase();
    return TYPE_TOKENS.some(t => aN.includes(t) && bN.includes(t));
  };

  // OSM self-dedup: same physical venue tagged twice (node + way, or two
  // adjacent ways for the same park).
  const dedupedOsm = [];
  for (const r of results) {
    const dupIdx = dedupedOsm.findIndex(d =>
      d.name && r.name &&
      haversine(d.lat, d.lng, r.lat, r.lng) < fuzzyDedupMiles &&
      sharesTypeToken(d.name, r.name)
    );
    if (dupIdx === -1) dedupedOsm.push(r);
    else if ((r.name?.length || 0) > (dedupedOsm[dupIdx].name?.length || 0)) dedupedOsm[dupIdx] = r;
  }
  results = dedupedOsm;

  // Google Places text search
  try {
    const responses = await Promise.all(mode.placesQueries.map(q =>
      fetch("/api/places/text", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q, lat, lng, radiusMeters: minRadius }) })
    ));
    for (const res of responses) {
      const data = res.ok ? await res.json() : {};
      const dedupMiles = mode.dedupMiles ?? 0.1;
      for (const p of (data.places || [])) {
        const pLat = p.location?.latitude, pLng = p.location?.longitude;
        if (!pLat || !pLng) continue;
        const spotName = p.displayName?.text || "";
        const nameLower = spotName.toLowerCase();
        const isExcluded = mode.excludeKeywords?.some(k => nameLower.includes(k));
        // Type-based sibling to excludeKeywords: a venue can be a genuine
        // instance of the mode and still be adults-only (e.g. "Tipsy Putt" is
        // typed miniature_golf_course AND pub/bar). Names don't reveal that
        // reliably; Google's types do.
        const isExcludedType = mode.excludeTypes?.some(t => (p.types || []).includes(t));
        // Positive type gate. excludeTypes alone is whack-a-mole: a "mini golf"
        // text query also matches indoor golf simulators, driving ranges and
        // anything else golf-ish, and no blocklist enumerates them all. When
        // requireTypes is set the venue must carry one of them, so the default
        // is to reject rather than to admit.
        // A name that states the category outright redeems a venue whose types
        // are too thin to judge (see requireTypesNameRe). Checked only after
        // the type gate fails, and never against excludeTypes above.
        const isRequiredType = !mode.requireTypes ||
          mode.requireTypes.some(t => (p.types || []).includes(t)) ||
          !!mode.requireTypesNameRe?.test(spotName);
        const isRequired = !mode.requireKeywords || mode.requireKeywords.some(k => nameLower.includes(k));
        if (isExcluded || isExcludedType || !isRequiredType || !isRequired || !spotName) continue;
        // Places-only keyword gate — OSM results are verified by the sport
        // tag, but text search returns loosely-related parks.
        if (mode.placesRequireKeywords && !mode.placesRequireKeywords.some(k => nameLower.includes(k))) continue;
        // For secret modes: if there's a nearby unnamed OSM result, give it this Places name
        const unnamedNearby = mode.secret
          ? results.find(r => !r.name && haversine(r.lat, r.lng, pLat, pLng) < dedupMiles)
          : null;
        if (unnamedNearby) {
          unnamedNearby.name = spotName;
          continue;
        }
        const isProximityDupe = results.some(r => haversine(r.lat, r.lng, pLat, pLng) < dedupMiles);
        const isNameDupe = results.some(r => {
          const rn = (r.name || "").toLowerCase();
          return rn === nameLower || rn.includes(nameLower) || nameLower.includes(rn);
        });
        if (isProximityDupe || isNameDupe) continue;
        // Same physical venue with different names from OSM vs Google
        // (e.g. OSM "South SF Skatepark" vs Google "Centennial Way Park ... Skate Park").
        const fuzzyDupeIdx = results.findIndex(r =>
          r.name &&
          haversine(r.lat, r.lng, pLat, pLng) < fuzzyDedupMiles &&
          sharesTypeToken(r.name, spotName)
        );
        if (fuzzyDupeIdx !== -1) {
          const existing = results[fuzzyDupeIdx];
          if ((spotName?.length || 0) > (existing.name?.length || 0)) existing.name = spotName;
          if (!existing.address && p.formattedAddress) existing.address = p.formattedAddress;
          if (!existing.website && p.websiteUri) existing.website = p.websiteUri;
          if (!existing.googleMapsUri && p.googleMapsUri) existing.googleMapsUri = p.googleMapsUri;
          // Inherit photos from Google when OSM didn't have any
          if (!existing.photoRef && p.photos?.[0]?.name) existing.photoRef = p.photos[0].name;
          if ((!existing.photoRefs || existing.photoRefs.length === 0) && p.photos?.length) {
            existing.photoRefs = p.photos.slice(0, 5).map(ph => ph.name).filter(Boolean);
          }
          continue;
        }
        const { isIndoor: detectedIndoor, isPaid: detectedPaid } = detectIndoorAndPaid(spotName, {}, p.types || []);
        // Climbing spot with a website is almost always a commercial gym → indoor + paid.
        const isIndoor = detectedIndoor || (modeId === "climbing" && !!p.websiteUri);
        const isPaid = detectedPaid || (modeId === "climbing" && isIndoor);
        // Photos from Google (added per origin/staging — gives activity spots
        // photos in result cards + hero images).
        const photoRef = p.photos?.[0]?.name || null;
        const photoRefs = p.photos?.slice(0, 5).map(ph => ph.name).filter(Boolean) || [];
        let gIsSchool = false, gNearestSchool = null;
        if (mode.detectSchools) {
          const campus = schoolPolygons.find(s => pointInPolygon(pLat, pLng, s.geometry));
          if (campus) { gIsSchool = true; gNearestSchool = campus.name; }
          else if (SCHOOL_NAME_RE.test(spotName)) gIsSchool = true;
        }
        results.push({ ...toResult(pLat, pLng, spotName, mode.featureId, "google", `${modeId}-google-${p.id}`), isIndoor, isPaid, photoRef, photoRefs, address: p.formattedAddress || null, website: p.websiteUri || null, googleMapsUri: p.googleMapsUri || null, openNow: p.currentOpeningHours?.openNow ?? null, isSchool: gIsSchool, nearestSchool: gNearestSchool });
      }
    }
    // Drop any OSM results that never got a name resolved (secret modes only)
    if (mode.secret) results.splice(0, results.length, ...results.filter(r => r.name));
    results.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
  } catch (e) { console.warn(`[Activity:${modeId}] Places failed:`, e.message); }

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), results }));
  } catch {
    // Quota exceeded — evict old activity caches and retry
    for (const k of Object.keys(localStorage).filter(k => k.startsWith("act"))) localStorage.removeItem(k);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), results })); } catch {}
  }
  return results;
}

// ── Fetch real playgrounds — server does enrichment + caches result ───────────
async function fetchRealPlaygrounds(lat, lng, minRadius = 8000, onBatch = null) {
  const entry = cacheGet(lat, lng, minRadius);
  // Fresh cache hit — no server call needed
  if (entry?.fresh) { if (onBatch) onBatch(entry.results); return entry.results; }
  // Stale cache — show immediately so there's no splash wait, then revalidate
  if (entry?.results) { if (onBatch) onBatch(entry.results); }

  // 3 attempts: immediate, +4s, +10s — gives Overpass time to recover between tries
  const delays = [0, 4000, 10000];
  let lastErr;
  for (let attempt = 0; attempt < delays.length; attempt++) {
    if (delays[attempt] > 0) await new Promise(r => setTimeout(r, delays[attempt]));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 28000); // 28s per attempt
    try {
      const res = await fetch("/api/playgrounds", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lat, lng, radius: minRadius }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`Playgrounds API ${res.status}`);
      const results = await res.json();
      cacheSet(lat, lng, minRadius, results);
      return results;
    } catch (e) { clearTimeout(timeout); lastErr = e; }
  }
  // If revalidation failed but we have stale data, return it rather than throwing
  if (entry?.results) return entry.results;
  throw lastErr;
}

// ── Legacy: kept for reference only (logic now lives in server/index.js) ─────
async function _fetchRealPlaygrounds_UNUSED(lat, lng, minRadius = 8000, onBatch = null) {
  const endpoints = [
    "/api/overpass",
    "https://overpass.kumi.systems/api/interpreter",
    "https://overpass.private.coffee/api/interpreter",
    "https://overpass-api.de/api/interpreter",
  ];

  async function queryOverpass(radiusMeters) {
    const query = `
[out:json][timeout:12];
(
  node["leisure"="playground"](around:${radiusMeters},${lat},${lng});
  way["leisure"="playground"](around:${radiusMeters},${lat},${lng});
  way["leisure"="park"](around:${radiusMeters},${lat},${lng});
  way["landuse"="recreation_ground"](around:${radiusMeters},${lat},${lng});
  relation["leisure"="park"](around:${radiusMeters},${lat},${lng});
  way["amenity"~"school|kindergarten"](around:${radiusMeters},${lat},${lng});
  node["amenity"="toilets"](around:${radiusMeters},${lat},${lng});
  way["amenity"="toilets"](around:${radiusMeters},${lat},${lng});
  node["amenity"="drinking_water"](around:${radiusMeters},${lat},${lng});
  way["amenity"="drinking_water"](around:${radiusMeters},${lat},${lng});
  node["sport"="bmx"](around:${radiusMeters},${lat},${lng});
  way["sport"="bmx"](around:${radiusMeters},${lat},${lng});
  node["sport"="pump_track"](around:${radiusMeters},${lat},${lng});
  way["sport"="pump_track"](around:${radiusMeters},${lat},${lng});
  way["leisure"="pump_track"](around:${radiusMeters},${lat},${lng});
);
out center tags;`;

    for (const endpoint of endpoints) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 25000);
        const res = await fetch(endpoint, {
          method: "POST",
          body: "data=" + encodeURIComponent(query),
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (res.ok) return res.json();
      } catch (e) { /* try next endpoint */ }
    }
    throw new Error("All Overpass endpoints failed");
  }

  // Always search at least minRadius; expand further if results are sparse
  const radii = [minRadius, minRadius * 2, minRadius * 4].filter((r, i, arr) => i === 0 || r !== arr[i-1]);
  let data = null;
  for (const radius of radii) {
    data = await queryOverpass(radius);
    const pgCount = (data.elements || []).filter(el => el.tags?.leisure === "playground").length;
    if (pgCount >= 3) break;
    dlog(`[Overpass] only ${pgCount} playgrounds at ${radius}m, expanding…`);
  }

  // Nearby amenity nodes/ways for restrooms / drinking water
  const pumpTrackNodes = (data.elements || []).filter(el =>
    el.tags?.sport === "bmx" || el.tags?.sport === "pump_track" || el.tags?.leisure === "pump_track"
  ).map(el => ({ lat: el.lat ?? el.center?.lat, lng: el.lon ?? el.center?.lon })).filter(n => n.lat && n.lng);

  const toiletNodes = (data.elements || []).filter(el => el.tags?.amenity === "toilets")
    .map(el => ({ lat: el.lat ?? el.center?.lat, lng: el.lon ?? el.center?.lon })).filter(n => n.lat && n.lng);
  const waterNodes = (data.elements || []).filter(el => el.tags?.amenity === "drinking_water")
    .map(el => ({ lat: el.lat ?? el.center?.lat, lng: el.lon ?? el.center?.lon })).filter(n => n.lat && n.lng);

  // School polygons for point-in-polygon school detection
  const schoolPolygons = (data.elements || []).filter(el => {
    const tags = el.tags || {};
    return (tags.amenity === "school" || tags.amenity === "kindergarten") &&
      el.type === "way" && Array.isArray(el.geometry) && el.geometry.length > 2;
  }).map(el => ({ name: el.tags?.name || null, geometry: el.geometry }));

  // Park polygons for point-in-polygon "open field" detection
  const parkPolygons = (data.elements || []).filter(el => {
    const tags = el.tags || {};
    return (tags.leisure === "park" || tags.landuse === "recreation_ground") &&
      el.type === "way" && Array.isArray(el.geometry) && el.geometry.length > 2;
  }).map(el => el.geometry); // each is [{lat, lon}, ...]

  const namedParks = (data.elements || []).filter(el => {
    const tags = el.tags || {};
    return tags.leisure === "park" && tags.name && (el.type === "way" || el.type === "relation");
  }).map(el => ({
    name: el.tags.name,
    lat: el.center?.lat ?? el.lat,
    lng: el.center?.lon ?? el.lon,
  })).filter(p => p.lat && p.lng);

  const results = (data.elements || []).filter(el => {
    const tags = el.tags || {};
    return tags.leisure === "playground";
  }).map((el, i) => {
    const elLat = el.lat ?? el.center?.lat;
    const elLng = el.lon ?? el.center?.lon;
    if (!elLat || !elLng) return null;
    const tags = el.tags || {};
    const dist = haversine(lat, lng, elLat, elLng);
    const bearing = Math.atan2(elLng - lng, elLat - lat) * 180 / Math.PI;
    const dirs = ["North","Northeast","East","Southeast","South","Southwest","West","Northwest"];
    const facing = dirs[Math.round(((bearing + 360) % 360) / 45) % 8];
    const rawName = tags.name || tags["name:en"] || "";

    let name = rawName;
    let isSchool = false;

    // Check if playground is inside a school polygon
    const containingSchool = schoolPolygons.find(s => pointInPolygon(elLat, elLng, s.geometry));
    if (containingSchool) {
      isSchool = true;
      if (!name) name = containingSchool.name ? `${containingSchool.name} Playground` : `Playground ${dist.toFixed(1)} mi ${facing}`;
    }

    if (!name) {
      // Fall back to nearest named park within ~400m
      const nearestPark = namedParks
        .map(p => ({ ...p, d: haversine(elLat, elLng, p.lat, p.lng) }))
        .filter(p => p.d < 0.25)
        .sort((a, b) => a.d - b.d)[0];
      name = nearestPark ? `${nearestPark.name} Playground` : `Playground ${dist.toFixed(1)} mi ${facing}`;
    }

    const features = osmTagsToFeatures(tags);
    const insidePark = parkPolygons.some(poly => pointInPolygon(elLat, elLng, poly));
    if (insidePark && !features.includes("field")) features.push("field");
    // Check for nearby restrooms / drinking water within ~150m
    if (!features.includes("pump_track") && pumpTrackNodes.some(n => haversine(elLat, elLng, n.lat, n.lng) < 0.15)) features.push("pump_track");
    if (!features.includes("restrooms") && toiletNodes.some(n => haversine(elLat, elLng, n.lat, n.lng) < 0.2)) features.push("restrooms");
    if (!features.includes("drinking_water") && waterNodes.some(n => haversine(elLat, elLng, n.lat, n.lng) < 0.2)) features.push("drinking_water");

    const { isIndoor, isPaid: _isPaid } = detectIndoorAndPaid(name, tags);
    const isPaid = isSchool ? false : _isPaid; // public school playgrounds are always free
    return {
      id: el.id || i,
      name,
      lat: elLat,
      lng: elLng,
      distance: dist.toFixed(1),
      features,
      elevation: null,
      facing,
      isSchool,
      nearestSchool: containingSchool?.name || null,
      isIndoor,
      isPaid,
      osmId: el.id,
      osmType: el.type || "node",
      address: [tags["addr:street"], tags["addr:city"]].filter(Boolean).join(", ") || null,
    };
  }).filter(Boolean);

  // Pass 1: deduplicate by proximity (within ~50m = same physical playground)
  const deduped = [];
  for (const pg of results) {
    const isDupe = deduped.some(d => haversine(d.lat, d.lng, pg.lat, pg.lng) < 0.1);
    if (!isDupe) deduped.push(pg);
  }

  // Pass 2: deduplicate by name — keep only the closest per unique name
  const byName = new Map();
  for (const pg of deduped) {
    const existing = byName.get(pg.name);
    if (!existing || parseFloat(pg.distance) < parseFloat(existing.distance)) {
      byName.set(pg.name, pg);
    }
  }

  const osmResults = [...byName.values()].sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));

  // Show OSM results immediately while Places loads in the background
  if (onBatch && osmResults.length > 0) onBatch(osmResults);

  // ── Google Places — always run, merge missing + upgrade generic names ─────
  dlog(`[Places] OSM returned ${osmResults.length} results, augmenting with Places…`);
  try {
    // Run playground, park, and school searches in parallel
    const [placesRes, parksRes, schoolsRes] = await Promise.all([
      fetch("/api/places", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lat, lng, radiusMeters: minRadius }),
      }),
      fetch("/api/places", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lat, lng, radiusMeters: minRadius * 0.5, types: ["park"] }),
      }),
      fetch("/api/places", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lat, lng, radiusMeters: minRadius, types: ["school", "primary_school", "secondary_school"] }),
      }),
    ]);
    if (!placesRes.ok) {
      console.warn("[Places] non-ok response", placesRes.status, await placesRes.text());
    } else {
      const placesData = await placesRes.json();
      dlog(`[Places] got ${(placesData.places || []).length} results`, placesData);
      const googlePlaces = (placesData.places || []).map((place, i) => {
          const pLat = place.location?.latitude;
          const pLng = place.location?.longitude;
          if (!pLat || !pLng) return null;
          const dist = haversine(lat, lng, pLat, pLng);
          const bearing = Math.atan2(pLng - lng, pLat - lat) * 180 / Math.PI;
          const dirs = ["North","Northeast","East","Southeast","South","Southwest","West","Northwest"];
          const facing = dirs[Math.round(((bearing + 360) % 360) / 45) % 8];
          const name = place.displayName?.text || `Playground ${dist.toFixed(1)} mi ${facing}`;
          const { isIndoor: gIsIndoor, isPaid: gIsPaid } = detectIndoorAndPaid(name, {}, place.types || []);
          return {
            id: `google-${place.id || i}`,
            name,
            lat: pLat,
            lng: pLng,
            distance: dist.toFixed(1),
            features: [],
            elevation: null,
            facing,
            isSchool: false,
            isIndoor: gIsIndoor,
            isPaid: gIsPaid,
            osmId: `google-${place.id || i}`,
            osmType: "google",
            address: place.formattedAddress || null,
            source: "google",
          };
        }).filter(Boolean);

        // Merge: add new Google results; upgrade generic OSM names if Google has a real one
        for (const gp of googlePlaces) {
          const dupeIdx = osmResults.findIndex(o => haversine(o.lat, o.lng, gp.lat, gp.lng) < 0.1);
          if (dupeIdx === -1) {
            dlog(`[Places] adding missing playground: ${gp.name}`);
            osmResults.push(gp);
          } else {
            const existing = osmResults[dupeIdx];
            const isGeneric = existing.name.startsWith("Playground ");
            const googleHasRealName = gp.name && !gp.name.startsWith("Playground ");
            if ((isGeneric || existing.isSchool) && googleHasRealName) {
              dlog(`[Places] upgrading name: "${existing.name}" → "${gp.name}"`);
              osmResults[dupeIdx] = { ...existing, name: gp.name, address: existing.address || gp.address };
            } else {
              // Even if name is fine, grab the address if OSM didn't have one
              if (!existing.address && gp.address) osmResults[dupeIdx] = { ...existing, address: gp.address };
            }
          }
        }
    }

    // Use nearby parks to upgrade names of generic OSM playgrounds
    dlog("[Places] parks response status:", parksRes.status);
    if (parksRes.ok) {
      const parksData = await parksRes.json();
      dlog("[Places] parks raw:", parksData.places?.map(p => ({ name: p.displayName?.text, lat: p.location?.latitude, lng: p.location?.longitude })));
      const nearbyParks = (parksData.places || []).map(p => ({
        name: p.displayName?.text,
        lat: p.location?.latitude,
        lng: p.location?.longitude,
      })).filter(p => p.name && p.lat && p.lng);

      dlog("[Places] generic/school names to upgrade:", osmResults.filter(pg => pg.name.startsWith("Playground ") || pg.name === "Playground" || pg.isSchool).map(pg => pg.name));
      for (let i = 0; i < osmResults.length; i++) {
        const pg = osmResults[i];
        if (!pg.name.startsWith("Playground") && !pg.isSchool) continue;
        const nearestPark = nearbyParks
          .map(p => ({ ...p, d: haversine(pg.lat, pg.lng, p.lat, p.lng) }))
          .filter(p => p.d < 0.2)
          .sort((a, b) => a.d - b.d)[0];
        if (nearestPark) {
          dlog(`[Places] park name upgrade: "${pg.name}" → "${nearestPark.name}"`);
          osmResults[i] = { ...pg, name: nearestPark.name };
        }
      }
    }
    // Use Google school names to upgrade school playground names
    if (schoolsRes.ok) {
      const schoolsData = await schoolsRes.json();
      const nearbySchools = (schoolsData.places || []).map(p => ({
        name: p.displayName?.text,
        lat: p.location?.latitude,
        lng: p.location?.longitude,
      })).filter(p => p.name && p.lat && p.lng);
      dlog("[Places] schools:", nearbySchools.map(s => s.name));

      for (let i = 0; i < osmResults.length; i++) {
        const pg = osmResults[i];
        // Upgrade name for polygon-confirmed school playgrounds
        if (!pg.isSchool) continue;
        const nearest = nearbySchools
          .map(s => ({ ...s, d: haversine(pg.lat, pg.lng, s.lat, s.lng) }))
          .filter(s => s.d < 0.08)
          .sort((a, b) => a.d - b.d)[0];
        if (nearest) {
          dlog(`[Places] school name upgrade: "${pg.name}" → "${nearest.name}"`);
          osmResults[i] = { ...pg, name: nearest.name, nearestSchool: nearest.name };
        }
      }
      // Proximity-based school detection: mark playgrounds within 0.15mi of a
      // Google Places school as isSchool even if OSM polygon containment missed them
      for (let i = 0; i < osmResults.length; i++) {
        const pg = osmResults[i];
        if (pg.isSchool) continue;
        const nearest = nearbySchools
          .map(s => ({ ...s, d: haversine(pg.lat, pg.lng, s.lat, s.lng) }))
          .filter(s => s.d < 0.15)
          .sort((a, b) => a.d - b.d)[0];
        if (nearest) {
          dlog(`[Places] proximity school: "${pg.name}" is ${nearest.d.toFixed(2)}mi from "${nearest.name}"`);
          const isGeneric = pg.name.startsWith("Playground ") || pg.name === "Playground";
          osmResults[i] = { ...pg, isSchool: true, nearestSchool: nearest.name, isPaid: false, name: isGeneric ? `${nearest.name} Playground` : pg.name };
        }
      }
    }
  } catch (e) {
    console.warn("[Places] failed:", e.message);
  }

  // ── Google Places text search for pump tracks / BMX ──────────────────────
  try {
    const [ptRes1, ptRes2] = await Promise.all([
      fetch("/api/places/text", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: "pump track", lat, lng, radiusMeters: minRadius }) }),
      fetch("/api/places/text", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: "bmx track", lat, lng, radiusMeters: minRadius }) }),
    ]);
    const pumpPlaces = [
      ...((ptRes1.ok ? await ptRes1.json() : {}).places || []),
      ...((ptRes2.ok ? await ptRes2.json() : {}).places || []),
    ].map(p => ({ lat: p.location?.latitude, lng: p.location?.longitude })).filter(p => p.lat && p.lng);

    if (pumpPlaces.length > 0) {
      dlog(`[Places] ${pumpPlaces.length} pump track/BMX locations found`);
      for (let i = 0; i < osmResults.length; i++) {
        if (osmResults[i].features.includes("pump_track")) continue;
        const distances = pumpPlaces.map(p => haversine(osmResults[i].lat, osmResults[i].lng, p.lat, p.lng));
        const minDist = Math.min(...distances);
        if (minDist < 0.15) {
          dlog(`[Places] tagging ${osmResults[i].name} with pump_track (nearest pump track: ${minDist.toFixed(2)}mi)`);
          osmResults[i] = { ...osmResults[i], features: [...osmResults[i].features, "pump_track"] };
        } else {
          dlog(`[Places] ${osmResults[i].name}: nearest pump track is ${minDist.toFixed(2)}mi away`);
        }
      }
    }
  } catch (e) {
    console.warn("[Places] pump track search failed:", e.message);
  }

  const final = osmResults.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance)).slice(0, 20);
  cacheSet(lat, lng, minRadius, final);
  return final;
} // end _fetchRealPlaygrounds_UNUSED

// ── Score bar component ───────────────────────────────────────────────────────
function ScoreRing({ score, size = 56 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  const color = score >= 75 ? "#4ade80" : score >= 45 ? "#fbbf24" : "#f87171";
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="5" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color}
        strokeWidth="5" strokeDasharray={`${fill} ${circ}`}
        strokeLinecap="round" style={{ transition: "stroke-dasharray 1s ease" }} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        fill="white" fontSize="13" fontWeight="700"
        style={{ transform: "rotate(90deg)", transformOrigin: `${size/2}px ${size/2}px` }}>
        {score}
      </text>
    </svg>
  );
}

// ── Fun rating component (replaces stars with playground slides) ──────────────
function FunMeter({ score, spotEmoji = "🛝" }) {
  const slides = Math.round(score / 20); // 0–5
  const levels = [
    { min: 0,  max: 19,  emoji: "😴", label: "Total snoozefest" },
    { min: 20, max: 39,  emoji: "😐", label: "Kinda meh" },
    { min: 40, max: 59,  emoji: "🙂", label: "Not bad!" },
    { min: 60, max: 79,  emoji: "😄", label: "Pretty fun!" },
    { min: 80, max: 100, emoji: "🤩", label: "Epic!" },
  ];
  const { emoji, label } = levels.find(l => score >= l.min && score <= l.max) || levels[4];
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 72 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>Conditions</div>
      <div style={{ fontSize: 32, lineHeight: 1 }}>{emoji}</div>
      <div style={{ display: "flex", gap: 3 }}>
        {[1,2,3,4,5].map(i => (
          <span key={i} style={{ fontSize: 16, opacity: i <= slides ? 1 : 0.2, filter: i <= slides ? "none" : "grayscale(1)" }}>{spotEmoji}</span>
        ))}
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: score >= 75 ? "#4ade80" : score >= 45 ? "#fbbf24" : "#f87171" }}>{label}</div>
    </div>
  );
}

// ── CacheCleanButton — extracted component to avoid IIFE useState violation ───
function CacheCleanButton({ onCleared }) {
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState("");
  const clearCache = async (scope) => {
    setClearing(true); setClearMsg("");
    try {
      const r = await fetch("/api/cache/clear", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope }),
      });
      const d = await r.json();
      if (!r.ok) { setClearMsg(`✗ ${d.error}`); return; }
      const bits = [];
      if (d.cleared) bits.push(`${d.cleared} geo`);
      if (d.clearedLive) bits.push(`${d.clearedLive} live`);
      if (d.clearedStatic) bits.push(`${d.clearedStatic} static`);
      setClearMsg(`✓ ${bits.join(" + ") || "nothing"} cleared`);
      if (onCleared) onCleared();
    } catch { setClearMsg("✗ Request failed"); }
    finally { setClearing(false); }
  };
  const btnStyle = { background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#f87171", fontSize: 13, padding: "6px 14px", cursor: clearing ? "default" : "pointer" };
  return (
    <div style={{ marginBottom: 16, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 12, padding: "10px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#f87171", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>🗑️ Cache</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => clearCache("geo")} disabled={clearing} style={btnStyle}>Clear geo</button>
        <button onClick={() => clearCache("analysis-live")} disabled={clearing} style={btnStyle}>Clear live AI</button>
        <button onClick={() => clearCache("all")} disabled={clearing} style={btnStyle}>Clear server (all)</button>
        <button
          onClick={() => {
            const USER_DATA = new Set(["pg_favorites", "pg_school_overrides", "pg_hidden"]);
            try { Object.keys(localStorage).filter(k => k.startsWith("pg_") && !USER_DATA.has(k)).forEach(k => localStorage.removeItem(k)); } catch {}
            window.location.reload();
          }}
          style={{ ...btnStyle, background: "rgba(251,146,60,0.15)", border: "1px solid rgba(251,146,60,0.3)", color: "#fb923c" }}
          title="Wipe all pg_* localStorage keys on this device, then reload"
        >
          Clear this device
        </button>
        {clearMsg && <span style={{ fontSize: 12, color: "#94a3b8" }}>{clearMsg}</span>}
      </div>
    </div>
  );
}

// Admin token (pasted into the debug panel, stored in localStorage) is sent on
// protected endpoints — backup trigger and usage stats. Empty when unset.
function adminHeaders() {
  try { const t = localStorage.getItem("pg_admin_token"); return t ? { "x-admin-token": t } : {}; }
  catch { return {}; }
}

// ── BackupNowButton — trigger a manual backup of user content ────────────────
function BackupNowButton() {
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");
  const runNow = async () => {
    setRunning(true); setMsg("");
    try {
      const r = await fetch("/api/admin/backup", { method: "POST", headers: adminHeaders() });
      const d = await r.json();
      if (!r.ok) { setMsg(`✗ ${d.error || r.status}`); return; }
      if (d.skipped) setMsg(`⚠️ ${d.skipped}`);
      else setMsg(`✓ ${d.key.split("/").pop()} · ${(d.bytes / 1024 / 1024).toFixed(1)} MB · ${d.elapsed}s${d.pruned ? ` · pruned ${d.pruned}` : ""}`);
    } catch (e) { setMsg(`✗ ${e.message}`); }
    finally { setRunning(false); }
  };
  return (
    <div style={{ marginTop: 12, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 12, padding: "10px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#4ade80", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>💾 Backup</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button onClick={runNow} disabled={running}
          style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 8, color: "#4ade80", fontSize: 13, padding: "6px 14px", cursor: running ? "default" : "pointer" }}>
          {running ? "Backing up…" : "Back up now"}
        </button>
        {msg && <span style={{ fontSize: 12, color: "#94a3b8" }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── InspectorBlock — collapsible JSON viewer with copy-to-clipboard ──────────
function InspectorBlock({ label, value }) {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(value, null, 2);
  const copy = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };
  return (
    <details style={{ marginTop: 8 }}>
      <summary style={{ cursor: "pointer", color: "#93c5fd", fontSize: 13, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
        <span>{label}</span>
        <button onClick={copy} style={{ background: "rgba(59,130,246,0.2)", border: "1px solid rgba(59,130,246,0.4)", borderRadius: 6, color: "#93c5fd", fontSize: 11, padding: "2px 8px", cursor: "pointer" }}>
          {copied ? "✓ copied" : "copy"}
        </button>
      </summary>
      <pre style={{ fontSize: 11, color: "#cbd5e1", whiteSpace: "pre-wrap", wordBreak: "break-all", background: "rgba(0,0,0,0.2)", padding: 10, borderRadius: 8, margin: 0 }}>{json}</pre>
    </details>
  );
}

// ── PhotoSection — Google hero + user uploads + flag ─────────────────────────
// Lives in the debug panel; persists ADMIN_TOKEN to localStorage so PhotoSection
// (and any future mod surface) can send it as the x-admin-token header.
function AdminTokenInput() {
  const [value, setValue] = useState(() => { try { return localStorage.getItem("pg_admin_token") || ""; } catch { return ""; } });
  const [saved, setSaved] = useState(false);
  const save = () => {
    try {
      if (value.trim()) localStorage.setItem("pg_admin_token", value.trim());
      else localStorage.removeItem("pg_admin_token");
    } catch {}
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };
  return (
    <div style={{ marginBottom: 16, background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.25)", borderRadius: 12, padding: "10px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#c084fc", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>🔑 Admin Token</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="password"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="Paste ADMIN_TOKEN (matches Fly secret)"
          style={{ flex: 1, background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#e2e8f0", fontSize: 13, padding: "8px 10px" }}
        />
        <button onClick={save} style={{ background: "rgba(168,85,247,0.25)", border: "1px solid rgba(168,85,247,0.5)", borderRadius: 8, color: "#e9d5ff", fontSize: 12, fontWeight: 700, padding: "0 14px", cursor: "pointer" }}>
          {saved ? "✓ Saved" : "Save"}
        </button>
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: "#64748b" }}>Enables Unflag on user-uploaded photos. Stored in localStorage on this device only.</div>
    </div>
  );
}

function VenueRenamePanel({ nameOverrides, onSaved }) {
  const [osmId, setOsmId] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");
  const adminToken = (() => { try { return localStorage.getItem("pg_admin_token") || ""; } catch { return ""; } })();
  const save = async () => {
    if (!osmId.trim()) { setStatus("osmId required"); return; }
    const res = await fetch("/api/admin/name-override", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-token": adminToken },
      body: JSON.stringify({ osmId: osmId.trim(), name: name.trim() }),
    });
    if (!res.ok) { setStatus(res.status === 403 ? "Admin token required" : "Save failed"); return; }
    const data = await res.json();
    setStatus(data.name ? `✓ Saved as "${data.name}"` : "✓ Override cleared");
    if (onSaved) onSaved();
    setTimeout(() => setStatus(""), 3000);
  };
  const clearOverride = async (id) => {
    await fetch("/api/admin/name-override", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-token": adminToken },
      body: JSON.stringify({ osmId: id, name: "" }),
    });
    if (onSaved) onSaved();
  };
  const entries = Object.entries(nameOverrides);
  return (
    <div style={{ marginBottom: 16, background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.25)", borderRadius: 12, padding: "10px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#fb923c", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>✏️ Rename Venue</div>
      {entries.length > 0 && (
        <div style={{ marginBottom: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          {entries.map(([id, n]) => (
            <div key={id} style={{ display: "flex", gap: 6, alignItems: "center", background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "4px 8px" }}>
              <div style={{ flex: 1, fontSize: 11, color: "#cbd5e1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span style={{ color: "#94a3b8" }}>{id}</span> → <b>{n}</b>
              </div>
              <button onClick={() => clearOverride(id)} style={{ background: "none", border: "none", color: "#f87171", fontSize: 14, cursor: "pointer", padding: "0 2px" }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <input value={osmId} onChange={e => setOsmId(e.target.value)} placeholder="osmId (e.g. psearch-node-123)" style={{ flex: 1, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#e2e8f0", fontSize: 12, padding: "6px 10px", minWidth: 0 }} />
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Display name (blank to clear)" style={{ flex: 1, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#e2e8f0", fontSize: 12, padding: "6px 10px", minWidth: 0 }} />
        <button onClick={save} style={{ background: "rgba(249,115,22,0.25)", border: "1px solid rgba(249,115,22,0.5)", borderRadius: 8, color: "#fed7aa", fontSize: 12, fontWeight: 700, padding: "0 12px", cursor: "pointer", whiteSpace: "nowrap" }}>Save</button>
      </div>
      {status && <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>{status}</div>}
    </div>
  );
}

function PhotoSection({ playgroundId, googlePhotoRefs, playgroundName, onUserPhotoAdded }) {
  const [userPhotos, setUserPhotos] = useState(null); // null = loading
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [flagged, setFlagged] = useState({}); // photoId → true
  const [activeIdx, setActiveIdx] = useState(0);
  const fileInputRef = useRef(null);
  const swipeRef = useRef({});
  const adminToken = (() => { try { return localStorage.getItem("pg_admin_token") || ""; } catch { return ""; } })();
  const adminHeaders = adminToken ? { "x-admin-token": adminToken } : {};

  useEffect(() => {
    setUserPhotos(null);
    setActiveIdx(0);
    fetch(`/api/photos/${encodeURIComponent(playgroundId)}`, { headers: adminHeaders })
      .then(r => r.ok ? r.json() : [])
      .then(setUserPhotos)
      .catch(() => setUserPhotos([]));
  }, [playgroundId]); // eslint-disable-line

  const allPhotos = [
    ...(userPhotos || []).map(p => ({ ...p, isGoogle: false })),
    ...(googlePhotoRefs || []).map((ref, i) => ({ id: `google-${i}`, url: `/api/photo?ref=${encodeURIComponent(ref)}`, isGoogle: true })),
  ];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    const form = new FormData();
    form.append("photo", file);
    form.append("playgroundId", playgroundId);
    try {
      const r = await fetch("/api/photos/upload", { method: "POST", body: form });
      const data = await r.json();
      if (!r.ok) { setUploadError(data.error || "Upload failed"); return; }
      setUserPhotos(prev => [{ id: data.id, url: data.url, uploadedAt: Date.now(), flagCount: 0 }, ...(prev || [])]);
      setActiveIdx(0);
      // Tell the parent so the list card can show this upload as a thumbnail
      // immediately, without waiting for the next /api/playgrounds round-trip.
      if (onUserPhotoAdded) onUserPhotoAdded(data.url);
    } catch { setUploadError("Upload failed, try again."); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleFlag = async (photoId) => {
    if (flagged[photoId]) return;
    setFlagged(f => ({ ...f, [photoId]: true }));
    await fetch(`/api/photos/${photoId}/flag`, { method: "POST" });
    setUserPhotos(prev => (prev || []).filter(p => p.flagCount < 2 || p.id !== photoId));
  };

  const handleUnflag = async (photoId) => {
    if (!adminToken) return;
    try {
      const r = await fetch(`/api/photos/${photoId}/unflag`, { method: "POST", headers: adminHeaders });
      if (!r.ok) { alert("Unflag failed — check admin token."); return; }
      const data = await r.json();
      setFlagged(f => { const next = { ...f }; delete next[photoId]; return next; });
      setUserPhotos(prev => (prev || []).map(p =>
        p.id === photoId ? { ...p, flagCount: data.flagCount, hidden: data.hidden } : p
      ));
    } catch { alert("Unflag failed — network error."); }
  };

  const handleDelete = async (photoId) => {
    if (!adminToken) return;
    if (!window.confirm("Permanently delete this photo? This can't be undone.")) return;
    try {
      const r = await fetch(`/api/photos/${photoId}`, { method: "DELETE", headers: adminHeaders });
      if (!r.ok) { alert("Delete failed — check admin token."); return; }
      setUserPhotos(prev => (prev || []).filter(p => p.id !== photoId));
      setActiveIdx(0);
    } catch { alert("Delete failed — network error."); }
  };

  const current = allPhotos[activeIdx] || null;

  return (
    <div style={{ margin: "0 16px 12px" }}>
      {/* Photo display */}
      {(allPhotos.length > 0 || userPhotos === null) && (
        <div
          style={{ position: "relative", borderRadius: 16, overflow: "hidden", background: "rgba(255,255,255,0.05)", marginBottom: 6 }}
          onTouchStart={e => { swipeRef.current.x = e.touches[0].clientX; }}
          onTouchEnd={e => {
            const dx = e.changedTouches[0].clientX - (swipeRef.current.x || 0);
            if (Math.abs(dx) > 40) setActiveIdx(i => Math.max(0, Math.min(allPhotos.length - 1, i + (dx < 0 ? 1 : -1))));
          }}
        >
          {current ? (
            <img
              key={current.id}
              src={current.url}
              alt={playgroundName}
              style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
              onError={e => { e.target.parentElement.style.display = "none"; }}
            />
          ) : (
            <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 13 }}>Loading…</div>
          )}
          {/* Flag / unflag / delete controls on user photos */}
          {current && !current.isGoogle && (
            <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
              {adminToken && (current.flagCount > 0 || current.hidden) && (
                <button onClick={() => handleUnflag(current.id)} style={{ background: "rgba(34,197,94,0.7)", border: "none", borderRadius: 8, color: "#fff", fontSize: 12, padding: "4px 8px", cursor: "pointer", fontWeight: 700 }}>
                  ✓ Unflag{current.flagCount > 0 ? ` (${current.flagCount})` : ""}
                </button>
              )}
              {adminToken && (
                <button onClick={() => handleDelete(current.id)} style={{ background: "rgba(239,68,68,0.75)", border: "none", borderRadius: 8, color: "#fff", fontSize: 12, padding: "4px 8px", cursor: "pointer", fontWeight: 700 }} title="Permanently delete this photo">
                  🗑 Delete
                </button>
              )}
              <button onClick={() => handleFlag(current.id)} style={{ background: "rgba(0,0,0,0.55)", border: "none", borderRadius: 8, color: flagged[current.id] ? "#f87171" : "#94a3b8", fontSize: 12, padding: "4px 8px", cursor: "pointer" }}>
                {flagged[current.id] ? "🚩 Flagged" : "🚩 Flag"}
              </button>
            </div>
          )}
          {/* Admin-only "hidden" badge — shown when this photo is past the auto-hide threshold */}
          {current && !current.isGoogle && adminToken && current.hidden && (
            <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(248,113,113,0.85)", color: "#fff", borderRadius: 8, fontSize: 11, fontWeight: 700, padding: "4px 8px" }}>
              🙈 Hidden (3+ flags)
            </div>
          )}
          {/* Dot navigation */}
          {allPhotos.length > 1 && (
            <div style={{ position: "absolute", bottom: 8, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 6 }}>
              {allPhotos.map((_, i) => (
                <button key={i} onClick={() => setActiveIdx(i)} style={{ width: 7, height: 7, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0, background: i === activeIdx ? "#fff" : "rgba(255,255,255,0.35)" }} />
              ))}
            </div>
          )}
        </div>
      )}
      {/* Upload button */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ flex: 1, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#94a3b8", fontSize: 13, padding: "7px 12px", cursor: uploading ? "default" : "pointer" }}>
          {uploading ? "⏳ Uploading…" : "📷 Add photo"}
        </button>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={handleUpload} />
        {uploadError && <span style={{ fontSize: 12, color: "#f87171" }}>{uploadError}</span>}
      </div>
    </div>
  );
}

// ── PlaygroundCard (memoized) ─────────────────────────────────────────────────
// Sunny redesign — sticker card with ink outline + offset shadow. The
// `kidsMode` prop is now redundant with the warm cream/sun aesthetic but
// kept on the props interface so existing callers don't break.
const CARD_S = {
  card: {
    background: "#fff",
    border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
    borderRadius: SUNNY.radiusCard,
    boxShadow: SUNNY.shadowCard,
    padding: 14,
    position: "relative",
    cursor: "pointer",
    transition: "transform 0.12s",
    fontFamily: SUNNY.fontBody,
    color: SUNNY.ink,
  },
  cardName: {
    fontFamily: SUNNY.fontDisplay,
    fontSize: 19,
    fontWeight: 700,
    letterSpacing: -0.3,
    lineHeight: 1.15,
    margin: 0,
    color: SUNNY.ink,
    paddingRight: 56,  // clear the score stamp
  },
  cardMetaRow: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 6,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    color: SUNNY.inkSoft,
  },
  metaChip: { display: "inline-flex", alignItems: "center", gap: 3 },
  distBadge: {
    color: SUNNY.tomato,
    fontWeight: 800,
    fontSize: 13,
  },
  // Score-stamp colors per band — green for great, sun for ok, tomato for poor
  scoreColor: (s) => s >= 70 ? SUNNY.grass : s >= 40 ? SUNNY.sun : SUNNY.tomato,
  // Small cream-tile feature chip — 2px ink border, emoji centered
  featChip: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
    minWidth: 28, height: 28, padding: "0 8px",
    borderRadius: 8, background: SUNNY.cream,
    border: `2px solid ${SUNNY.ink}`,
    fontSize: 11, fontWeight: 800,
    color: SUNNY.ink,
  },
  featChipActive: {
    background: SUNNY.sun,
    boxShadow: `2px 2px 0 ${SUNNY.ink}`,
  },
};

function isSchoolHours() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 6=Sat
  const hour = now.getHours();
  return day >= 1 && day <= 5 && hour >= 8 && hour < 15; // Mon-Fri 8am-3pm
}

// A venue whose OSM start_date / opening_date (or a curated yearBuilt) lands in
// the current or previous calendar year is treated as brand-new or freshly
// upgraded, so it gets a "New" badge instead of the plain date chip. This is how
// a just-opened build — e.g. a new Magical Bridge inclusive playground replacing
// an older structure — stands out in the list the moment it's mapped.
function isRecentlyBuilt(yearBuilt) {
  if (!yearBuilt) return false;
  return yearBuilt >= new Date().getFullYear() - 1;
}

const ACTIVITY_EMOJI = {
  playground: "🛝", skatepark: "🛹", "pump-track": "🚵", bmx: "🚲",
  mtb: "🚵", hiking: "🥾", climbing: "🧗", "dog-park": "🐕",
  basketball: "🏀", "splash-pad": "💦", pool: "🏊", "disc-golf": "🥏",
  "nature-play": "🌿", bowling: "🎳", "ice-skating": "⛸️",
  beach: "🏖️", soccer: "⚽", baseball: "⚾", "happy-hour": "🍺",
};

const RESTROOM_TIERS = [
  { value: 1, emoji: "😱", label: "Gas station" },
  { value: 2, emoji: "🍟", label: "Fast food" },
  { value: 3, emoji: "🏬", label: "Mall" },
  { value: 4, emoji: "🍽️", label: "Restaurant" },
  { value: 5, emoji: "🏨", label: "5-star" },
];
const tierFromAvg = (avg) => RESTROOM_TIERS[Math.min(4, Math.max(0, Math.round(avg) - 1))];

const PlaygroundCard = React.memo(function PlaygroundCard({
  pg, isFavorite, isSchool, nearestSchool, schoolOverridden,
  selectedFeatures, activityMode, walkTime, roadDist, animIndex,
  conditionsScore, useMetric, kidsMode,
  windSheltered, showWindBadge, userLat, userLng,
  onOpen, onToggleFavorite, onHide, onDistanceTap,
}) {
  const tollBridge = tollBridgeForTrip(userLat, userLng, pg.lat, pg.lng);
  // Use real road distance when OSRM has responded; fall back to haversine.
  const displayDist = roadDist ?? pg.distance;
  const distIsRoad  = roadDist != null;
  const schoolClosed = isSchool && !schoolOverridden && isSchoolHours();
  // Prefer Google's photo when we have one; fall back to the user-uploaded
  // thumbnail the server attaches when no Google ref exists (e.g. tiny
  // community-built playgrounds Google doesn't index). If the Google photo
  // 404s (stale ref, Google removed it), auto-fall-back to the user upload
  // before giving up entirely.
  const googleRef = pg.photoRefs?.[0] || pg.photoRef || null;
  const googleUrl = googleRef ? `/api/photo?ref=${encodeURIComponent(googleRef)}&w=320` : null;
  const userUrl = pg.userPhotoUrl || null;
  const [thumbFailed, setThumbFailed] = useState({ google: false, user: false });
  // Reset per-URL failure state when the underlying URLs change (new search,
  // successful upload propagated into list state, etc.).
  useEffect(() => { setThumbFailed({ google: false, user: false }); }, [googleUrl, userUrl]);
  let thumbUrl = null;
  let thumbKind = null;
  if (googleUrl && !thumbFailed.google) { thumbUrl = googleUrl; thumbKind = "google"; }
  else if (userUrl && !thumbFailed.user) { thumbUrl = userUrl; thumbKind = "user"; }
  // Subtle deterministic per-card rotation (-0.6° to +0.6°) — sticker feel
  // without making a long list look chaotic.
  const rotation = ((animIndex % 7) - 3) * 0.2;
  const travelLabel = formatTravelTime(displayDist, ACTIVITY_MODES.find(m => m.id === activityMode)?.bikeMode, walkTime, activityMode);
  return (
    <div className="pg-card" style={{ ...CARD_S.card, transform: `rotate(${rotation}deg)`, animationDelay: `${animIndex * 60}ms` }}
      onClick={() => onOpen(pg)}>

      {/* Score stamp top-right — color cycles by score band */}
      {conditionsScore != null && (
        <div style={{
          position: "absolute", top: -12, right: 12, zIndex: 1,
          background: CARD_S.scoreColor(conditionsScore), color: SUNNY.ink,
          border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`, borderRadius: "50%",
          width: 48, height: 48,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          fontFamily: SUNNY.fontDisplay, fontWeight: 700,
          boxShadow: SUNNY.shadowSticker, transform: "rotate(8deg)",
        }}>
          <div style={{ fontSize: 16, lineHeight: 1 }}>{conditionsScore}</div>
          <div style={{ fontSize: 7, fontWeight: 900, letterSpacing: 0.5, marginTop: 2 }}>SUN·FIT</div>
        </div>
      )}

      {/* Header: thumb + title block — Sunny redesign card */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {thumbUrl ? (
          <img
            key={thumbUrl}
            src={thumbUrl}
            alt=""
            loading="lazy"
            style={{
              width: 64, height: 64, borderRadius: 12, objectFit: "cover", flexShrink: 0,
              border: `2px solid ${SUNNY.ink}`,
              boxShadow: SUNNY.shadowSticker,
              background: SUNNY.cream,
            }}
            onError={() => setThumbFailed(prev => ({ ...prev, [thumbKind]: true }))}
          />
        ) : (
          <div style={{
            width: 64, height: 64, borderRadius: 12, flexShrink: 0,
            border: `2px solid ${SUNNY.ink}`,
            boxShadow: SUNNY.shadowSticker,
            background: SUNNY.cream,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28,
          }}>
            {/bathroom|restroom|toilet|lavatory|\bwc\b|porta.?pott/i.test(pg.name || "") ? "🚻" : (ACTIVITY_EMOJI[activityMode] || "🛝")}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={CARD_S.cardName}>
            {isSchool && "🏫 "}
            {displayName(pg.name, pg.address)}
          </div>
          {(pg.address || pg.neighborhood) && (
            <div style={{ fontSize: 11, color: SUNNY.inkSoft, marginTop: 3, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", lineHeight: 1.3 }}>
              {pg.address && <span style={{ opacity: 0.85 }}>📍 {pg.address}</span>}
              {pg.neighborhood && !pg.address?.toLowerCase().includes(pg.neighborhood.toLowerCase()) && (
                <span style={{ color: SUNNY.tomato, fontWeight: 800 }}>· {pg.neighborhood}</span>
              )}
            </div>
          )}
        </div>
        {/* Action buttons (heart + hide) — small, top-right of header, below score stamp */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, marginTop: 36 }}>
          <button onClick={e => onToggleFavorite(pg, e)}
            aria-label={isFavorite ? "Unfavorite" : "Favorite"}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: 2, lineHeight: 1 }}>
            {isFavorite ? "❤️" : "🤍"}
          </button>
          <button onClick={e => { e.stopPropagation(); if (window.confirm(`Hide "${pg.name}"?`)) onHide(pg.osmId); }}
            aria-label="Not a playground"
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: 2, lineHeight: 1, opacity: 0.4 }}
            title="Not a playground">🚫</button>
        </div>
      </div>

      {/* Distance + facing — Tomato accent matches the design language */}
      <div style={CARD_S.cardMetaRow}>
        <span style={CARD_S.distBadge}
          title={!distIsRoad ? "Straight-line estimate — road distance loading…" : undefined}
          onClick={onDistanceTap ? e => { e.stopPropagation(); onDistanceTap(); } : undefined}>
          {fmtDist(displayDist, useMetric)} · {travelLabel}
        </span>
        {pg.facing && (
          <>
            <span style={{ opacity: 0.3 }}>·</span>
            <span>🧭 {pg.facing}</span>
          </>
        )}
      </div>

      {/* Status pills — open/closed, indoor, paid, private, school-closed, etc. */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {pg.openNow === true && <StatusChip variant="grass">🟢 Open</StatusChip>}
        {pg.openNow === false && <StatusChip variant="tomato">🔴 Closed</StatusChip>}
        {schoolClosed && <StatusChip variant="tomato">⚠️ School hours</StatusChip>}
        {isSchool && !schoolClosed && <StatusChip variant="sun">🏫 {nearestSchool ? `Near ${nearestSchool}` : "School"}</StatusChip>}
        {pg.isIndoor && <StatusChip variant="sky">🏠 Indoors</StatusChip>}
        {!pg.isIndoor && activityMode === "climbing" && <StatusChip variant="grass">🌲 Outdoor</StatusChip>}
        {pg.isPaid && <StatusChip variant="sun">💰 Admission</StatusChip>}
        {pg.access && ["private","no","members"].includes(pg.access) && <StatusChip variant="closed">🔒 Private</StatusChip>}
        {tollBridge && <StatusChip variant="sun" title={`Likely requires the ${tollBridge} (toll)`}>🌉 Toll bridge</StatusChip>}
        {pg.condition?.count >= 2 && pg.condition.average >= 4 && <StatusChip variant="grass">✨ Well kept</StatusChip>}
        {pg.condition?.count >= 2 && pg.condition.average <= 2.5 && <StatusChip variant="tomato">🔧 Needs TLC</StatusChip>}
        {showWindBadge && windSheltered != null && (
          <StatusChip variant={windSheltered ? "grass" : "tomato"}>
            {windSheltered ? "🌿 Sheltered" : "💨 Exposed"}
          </StatusChip>
        )}
        {!isSchool && pg.nearestPark && <StatusChip variant="grass">🌳 {pg.nearestPark}</StatusChip>}
        {pg.yearBuilt && (isRecentlyBuilt(pg.yearBuilt)
          ? <StatusChip variant="grass" title={`New or recently upgraded (${pg.yearBuilt})`}>✨ New ({pg.yearBuilt})</StatusChip>
          : <StatusChip variant="info">📅 {pg.yearBuilt}</StatusChip>)}
      </div>

      {/* Feature chips — cream squares with ink border. Selected ones get a sun fill. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
        {(() => {
          const FEATURE_CAP = 4;
          const sorted = [
            ...pg.features.filter(f => selectedFeatures.includes(f)),
            ...pg.features.filter(f => !selectedFeatures.includes(f)),
          ];
          const visible = sorted.slice(0, FEATURE_CAP);
          const overflow = sorted.length - visible.length;
          return (
            <>
              {visible.map(f => {
                const feat = FEATURES.find(x => x.id === f);
                const active = selectedFeatures.includes(f);
                return (
                  <span key={f} style={{ ...CARD_S.featChip, ...(active ? CARD_S.featChipActive : {}) }} title={feat?.label || f}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>{feat?.icon}</span>
                  </span>
                );
              })}
              {overflow > 0 && (
                <span style={{ ...CARD_S.featChip, color: SUNNY.inkSoft }}>+{overflow}</span>
              )}
            </>
          );
        })()}
      </div>

      {DEBUG && pg.osmId && (
        <div style={{ marginTop: 8, fontSize: 11, color: SUNNY.inkSoft }}>
          <a href={`https://www.openstreetmap.org/${pg.osmType}/${pg.osmId}`} target="_blank" rel="noreferrer"
            style={{ color: SUNNY.inkSoft, textDecoration: "underline" }} onClick={e => e.stopPropagation()}>
            🗺 OSM {pg.osmType}/{pg.osmId}
          </a>
          {" · "}
          <a href={`https://www.google.com/maps?q=${pg.lat},${pg.lng}`} target="_blank" rel="noreferrer"
            style={{ color: SUNNY.inkSoft, textDecoration: "underline" }} onClick={e => e.stopPropagation()}>
            Google Maps
          </a>
        </div>
      )}
    </div>
  );
});

// Inline status chip helper — uppercase, ink border, color variant.
// Lives next to the card so it doesn't have to be exported separately.
function StatusChip({ variant = "info", children, title }) {
  const variants = {
    sun:    { bg: SUNNY.sun,    fg: SUNNY.ink },
    grass:  { bg: SUNNY.grass,  fg: "#fff" },
    tomato: { bg: SUNNY.tomato, fg: "#fff" },
    sky:    { bg: SUNNY.sky,    fg: "#fff" },
    closed: { bg: SUNNY.ink,    fg: SUNNY.cream },
    info:   { bg: SUNNY.cream,  fg: SUNNY.ink },
  };
  const v = variants[variant] || variants.info;
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", borderRadius: 999,
      background: v.bg, color: v.fg,
      border: `2px solid ${SUNNY.ink}`,
      fontSize: 10, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase",
      lineHeight: 1.1, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function PlaygroundFinder() {
  const [step, setState] = useState("splash"); // splash | locating | manual | loading | results | detail
  const [location, setLocation] = useState(null);
  const [locationLabel, setLocationLabel] = useState("");
  const [locationSource, setLocationSource] = useState(null); // "gps" | "ip" | "manual"
  const [manualInput, setManualInput] = useState("");
  const [manualResults, setManualResults] = useState([]);
  const [geocodeError, setGeocodeError] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [gpsError, setGpsError] = useState("");
  const [gpsLoading, setGpsLoading] = useState(false);
  const [weather, setWeather] = useState(null);
  const [nameOverrides, setNameOverrides] = useState({}); // { [osmId]: displayName } — admin-set corrections
  const nameOverridesRef = useRef({});
  const [playgrounds, setPlaygrounds] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selected, setSelected] = useState(null);
  // Outing builder (phase 7) — a planned trip starting from `outingAnchor`
  // (a playground) plus 0+ added stops (cafes, viewpoints, etc.).
  const [outingAnchor, setOutingAnchor] = useState(null);
  const [outingStops, setOutingStops] = useState([]);  // [{kind, name, lat, lng}]
  const [outingStartTime, setOutingStartTime] = useState(new Date());
  const [outingSearchRadius, setOutingSearchRadius] = useState(3200); // meters; expands via "Search wider"
  const [outingSuggestions, setOutingSuggestions] = useState({ items: [] });
  // Suggestions for the "Pair this with" section on the venue detail screen.
  // Populated when a venue is opened — one entry from each of cafe / view /
  // food. Independent from the outing screen's suggestions list.
  const [pairSuggestions, setPairSuggestions] = useState([]);
  const [outingSuggestLoading, setOutingSuggestLoading] = useState(false);
  const [restroomRatings, setRestroomRatings] = useState({}); // conditionKey → {average, count, tags}
  const [detailGeoInfo, setDetailGeoInfo] = useState(null); // reverse-geocoded address/name for detail view
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState("");
  const analysisCache = useRef(new Map()); // osmId+hour → analysis result
  const [sunInfo, setSunInfo] = useState(null);
  const [visitTime, setVisitTime] = useState(new Date());
  const [analyzing, setAnalyzing] = useState(false);
  const [pgRating, setPgRating] = useState(null); // { average, count }
  const [pgCondition, setPgCondition] = useState(null); // { average, count, tags }
  const [userCondition, setUserCondition] = useState(null); // 1-5 the user picked
  const [userConditionTags, setUserConditionTags] = useState([]); // tag ids
  const [submittingCondition, setSubmittingCondition] = useState(false);
  const [sessionRatingLocked, setSessionRatingLocked] = useState(false); // true after first submit this session
  const [userRating, setUserRating] = useState(null); // 1-5 the user picked
  const [communityFeatures, setCommunityFeatures] = useState([]); // user-reported features for selected spot
  const [showAddFeatures, setShowAddFeatures] = useState(false); // "add missing feature" panel
  const [hoverRating, setHoverRating] = useState(null); // for hover preview
  const [submittingRating, setSubmittingRating] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Search radius: persisted per-activity. The user's chosen radius for, say,
  // playgrounds (close to home) is independent of their preference for MTB
  // trails (further out). On init, look up the saved activity's last-used
  // radius; if missing, fall back to that activity's defaultRadius from
  // ACTIVITY_MODES; ultimately fall back to 3200 (2mi) for the no-mode
  // playground experience. Persisted in `pg_search_radius:<activityId>`,
  // or `pg_search_radius:_default` when no activity is selected.
  const [searchRadius, setSearchRadius] = useState(() => {
    try {
      const savedMode = localStorage.getItem("pg_exp_activity") || "";
      const key = `pg_search_radius:${savedMode || "_default"}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const n = parseInt(stored, 10);
        if (RADIUS_OPTIONS.some(o => o.meters === n)) return n;
      }
      if (savedMode) {
        const mode = ACTIVITY_MODES.find(m => m.id === savedMode);
        if (mode?.defaultRadius) return mode.defaultRadius;
      }
    } catch {}
    return 3200; // 2mi default for first-time playground users
  });
  const [fetchedRadius, setFetchedRadius] = useState(0); // actual radius of last fetch
  // Sunny redesign defaults to map-first. localStorage persists the user's
  // choice across sessions so anyone who switched to list keeps list.
  const [mapView, setMapView] = useState(() => {
    try {
      const stored = localStorage.getItem("pg_mapview");
      if (stored !== null) return stored === "1";
    } catch {}
    return false;  // default to list view — surfaces the AI "Best Now"
                   // verdicts/ranking up front; map is one tap away and the
                   // choice persists per-user in pg_mapview.
  });
  useEffect(() => {
    try { localStorage.setItem("pg_mapview", mapView ? "1" : "0"); } catch {}
  }, [mapView]);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  // On desktop the map is always in the right panel, so the left panel always
  // shows the list regardless of the user's mapView preference.
  const effectiveMapView = isDesktop ? false : mapView;
  const [mapCenter, setMapCenter] = useState(null); // { lat, lng, viewRadius } set when user pans map
  // Bumped on every "Search this area" tap. MapView watches it to know the
  // incoming search was framed by the user and must not be refitted.
  const [viewportSearchToken, setViewportSearchToken] = useState(0);
  const [sheetCollapsed, setSheetCollapsed] = useState(false);
  // Default unit system from browser locale on first visit; persist manual
  // overrides. Imperial countries: US, Liberia, Myanmar — everyone else
  // gets metric. The temperature toggle in the header lets users override.
  const [useMetric, setUseMetric] = useState(() => {
    try {
      const stored = localStorage.getItem("pg_metric");
      if (stored !== null) return stored === "1";
      const locale = navigator.language || (Intl.NumberFormat && Intl.NumberFormat().resolvedOptions().locale) || "";
      const country = (locale.split("-")[1] || "").toUpperCase();
      if (country) return !["US", "LR", "MM"].includes(country);
      // No country in locale ("en" alone) — assume metric (UK/AU/etc are
      // more common globally than US-only "en")
      return true;
    } catch { return false; }
  });
  const toggleMetric = () => setUseMetric(v => { const n = !v; try { localStorage.setItem("pg_metric", n ? "1" : "0"); } catch {} return n; });
  const [manualUseCount, setManualUseCount] = useState(() => { try { return parseInt(localStorage.getItem("pg_manual_count") || "0", 10); } catch { return 0; } });
  const bumpManualCount = () => setManualUseCount(c => { const n = c + 1; try { localStorage.setItem("pg_manual_count", String(n)); } catch {} return n; });
  const [kidsMode, setKidsMode] = useState(() => { try { const v = localStorage.getItem("pg_kids_mode"); return v === "1"; } catch { return false; } });
  const toggleKidsMode = () => setKidsMode(v => { const n = !v; try { localStorage.setItem("pg_kids_mode", n ? "1" : "0"); } catch {} return n; });
  useEffect(() => {
    document.body.style.background = kidsMode
      ? "linear-gradient(160deg, #fef9c3 0%, #dcfce7 50%, #dbeafe 100%)"
      : "linear-gradient(160deg, #0f1923 0%, #0d2535 50%, #0f1f30 100%)";
    return () => { document.body.style.background = ""; };
  }, [kidsMode]);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pg_favorites") || "{}"); } catch { return {}; }
  });
  const [schoolOverrides, setSchoolOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pg_school_overrides") || "{}"); } catch { return {}; }
  });
  const [hidden, setHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pg_hidden") || "{}"); } catch { return {}; }
  });
  const hidePlayground = useCallback((osmId) => {
    setHidden(prev => {
      const next = { ...prev, [osmId]: true };
      localStorage.setItem("pg_hidden", JSON.stringify(next));
      return next;
    });
  }, []);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showOpenOnly, setShowOpenOnly] = useState(false);
  const [indoorOnly, setIndoorOnly] = useState(false);
  const [outdoorOnly, setOutdoorOnly] = useState(false);
  const [hideSchools, setHideSchools] = useState(() => {
    try { return localStorage.getItem("pg_hide_schools") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("pg_hide_schools", hideSchools ? "1" : "0"); } catch {}
  }, [hideSchools]);
  const [displayLimit, setDisplayLimit] = useState(20);
  useEffect(() => { setDisplayLimit(20); }, [playgrounds]);
  // Fetch name overrides once on mount; keep ref in sync for use in closures.
  useEffect(() => {
    fetch("/api/name-overrides")
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        nameOverridesRef.current = data;
        setNameOverrides(data);
        if (Object.keys(data).length) {
          setPlaygrounds(prev => prev.map(p => {
            const override = data[String(p.osmId)];
            return override ? { ...p, name: override } : p;
          }));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line
  // Reset scroll to the top whenever the view changes. Without this, opening
  // a card from mid-way down the results list drops the user partway through
  // the detail page, and Back-to-results then lands them at the top instead
  // of where they were. The detail view is long enough that not seeing the
  // name + hero photo on entry is disorienting.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, selected?.osmId]);
  const [ageRange, setAgeRange] = useState(null); // null | "toddler" — only applies in playground mode
  const [sortBy, setSortBy] = useState("distance"); // "distance" | "conditions" | "leastsun"
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [windShelterFilter, setWindShelterFilter] = useState(false);
  const [avoidTolls, setAvoidTolls] = useState(false);
  const [walkTimes, setWalkTimes] = useState({});   // osmId → secs (drive) | null
  const [roadDists, setRoadDists] = useState({});   // osmId → miles (drive) | null
  // Shown once per release to people who have used the app before. Deliberately
  // not shown to first-time users: nothing is "new" to them, it would just be
  // noise on the first run. Reads pg_visit_count but never writes it — the
  // homescreen-tip initializer below owns that counter, and incrementing here
  // too would double-count every visit.
  const [showWhatsNew, setShowWhatsNew] = useState(() => {
    try {
      if (localStorage.getItem("pg_whatsnew_seen") === WHATS_NEW_VERSION) return false;
      return parseInt(localStorage.getItem("pg_visit_count") || "0", 10) > 0;
    } catch { return false; }
  });

  const [showHomescreenTip, setShowHomescreenTip] = useState(() => {
    try {
      if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) return false;
      if (localStorage.getItem("pg_tip_homescreen") === "1") return false;
      const vc = parseInt(localStorage.getItem("pg_visit_count") || "0", 10) + 1;
      localStorage.setItem("pg_visit_count", vc);
      // Nudge after a few uses, not on the first visit — someone who's come
      // back 3+ times has shown retention and is far likelier to install.
      // Stop nagging after visit 5 (the × dismiss kills it permanently sooner).
      return vc >= 3 && vc <= 5;
    } catch { return false; }
  });
  const pullRef = useRef({ startY: 0, pulling: false });
  const detailSwipe = useRef(null);
  const sheetDragRef = useRef({ startY: 0, didDrag: false });
  const inputRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const deepLinkRef = useRef(null); // pending deep-linked spot to open once results load
  const prefetchedCoordsRef = useRef(null); // "pending" | { lat, lng } | "denied"
  const ipCityHintRef = useRef(""); // mirror of IP city for sync code paths
  const splashModeRef = useRef(undefined); // mode set by splash activity buttons, consumed by applyLocation
  const [ipCityHint, setIpCityHint] = useState(""); // re-renderable copy for the suggestion chip
  const [recentLocations, setRecentLocations] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pg_recent_locations") || "[]"); } catch { return []; }
  });
  // Add a location to the recents list, dedup by label, cap at 5, newest first.
  // Excludes the non-searchable "Your location" GPS label so it doesn't
  // pollute the dropdown.
  const saveRecentLocation = useCallback((label, lat, lng) => {
    if (!label || label === "Your location") return;
    const entry = { label, lat, lng };
    setRecentLocations(prev => {
      const filtered = prev.filter(r => r.label !== label);
      const next = [entry, ...filtered].slice(0, 5);
      try { localStorage.setItem("pg_recent_locations", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  const locationCallIdRef = useRef(0); // incremented on each applyLocation call to cancel stale invocations
  const secretTapCount = useRef(0);
  const secretTapTimer = useRef(null);
  const afterHoursTapCount = useRef(0);
  const afterHoursTapTimer = useRef(null);
  const [showAfterHours, setShowAfterHours] = useState(() => { try { return localStorage.getItem("pg_after_hours") === "1"; } catch { return false; } });
  const handleAfterHoursTap = useCallback(() => {
    afterHoursTapCount.current += 1;
    clearTimeout(afterHoursTapTimer.current);
    afterHoursTapTimer.current = setTimeout(() => { afterHoursTapCount.current = 0; }, 3000);
    if (afterHoursTapCount.current >= 3) {
      afterHoursTapCount.current = 0;
      setShowAfterHours(true);
      try { localStorage.setItem("pg_after_hours", "1"); } catch {}
    }
  }, []);
  const handleSecretTap = useCallback(() => {
    secretTapCount.current += 1;
    clearTimeout(secretTapTimer.current);
    secretTapTimer.current = setTimeout(() => { secretTapCount.current = 0; }, 1500);
    if (secretTapCount.current >= 5) {
      secretTapCount.current = 0;
      Object.keys(localStorage).filter(k => k.startsWith("pg_cache_")).forEach(k => localStorage.removeItem(k));
      alert("🧹 Cache cleared!");
    }
  }, []);

  const [showExperimental, setShowExperimental] = useState(false);
  const [activityMode, setActivityMode] = useState(() => { try { return localStorage.getItem("pg_exp_activity") || null; } catch { return null; } });
  // Favorite activities — the user's chosen top-row chips. Playgrounds is
  // always pinned implicitly (the null mode), so it's never stored here.
  // Default seeds the legacy hardcoded set so existing users see no change.
  // Secret modes (Brewery Patios, P-Search) stay easter-egg-only and are
  // filtered out if they somehow land in storage.
  const [favActivities, setFavActivities] = useState(() => {
    try {
      const raw = localStorage.getItem("pg_fav_activities");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr.filter(id => ACTIVITY_MODES.some(m => m.id === id && !m.secret));
      }
    } catch {}
    return ["skatepark", "pump_track", "beach"];
  });
  useEffect(() => { try { localStorage.setItem("pg_fav_activities", JSON.stringify(favActivities)); } catch {} }, [favActivities]);
  const toggleFavActivity = useCallback((id) => {
    if (!id) return; // Playgrounds is always pinned
    setFavActivities(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);
  const [showActivityPicker, setShowActivityPicker] = useState(false);
  // The top-row chips: Playgrounds first, then favorites in their saved order.
  // The active mode is always appended if it isn't already a favorite, so a
  // remembered last-used activity (e.g. Dog Park) still shows a highlighted
  // chip even when the user hasn't starred it.
  const PLAYGROUNDS_CHIP = { id: null, icon: "🛝", label: "Playgrounds" };
  const chipModes = useMemo(() => {
    const ids = [null, ...favActivities];
    if (activityMode && !ids.includes(activityMode)) ids.push(activityMode);
    return ids
      .map(id => id === null ? PLAYGROUNDS_CHIP : ACTIVITY_MODES.find(m => m.id === id))
      .filter(Boolean);
  }, [favActivities, activityMode]); // eslint-disable-line
  // Persist searchRadius keyed by current activity. Writing on either
  // dependency changing means switchMode (which sets both atomically) ends
  // up storing the new mode's preferred radius under its own key.
  useEffect(() => {
    try {
      const key = `pg_search_radius:${activityMode || "_default"}`;
      localStorage.setItem(key, String(searchRadius));
    } catch {}
  }, [searchRadius, activityMode]);
  // Mirror searchRadius into a ref so applyLocation (which is wrapped in
  // useCallback with a stable dep array) can read the latest value without
  // re-creating the callback. Several call sites omit the radius arg —
  // those should fall back to the user's actual preference, not the
  // function-default sentinel of 8000m.
  const searchRadiusRef = useRef(searchRadius);
  useEffect(() => { searchRadiusRef.current = searchRadius; }, [searchRadius]);
  const weatherTapCount = useRef(0);
  const weatherTapTimer = useRef(null);
  const handleWeatherTap = useCallback(() => {
    weatherTapCount.current += 1;
    clearTimeout(weatherTapTimer.current);
    weatherTapTimer.current = setTimeout(() => { weatherTapCount.current = 0; }, 1500);
    if (weatherTapCount.current >= 3) {
      weatherTapCount.current = 0;
      setShowExperimental(v => !v);
    }
  }, []);

  const psearchTapCount = useRef(0);
  const psearchTapTimer = useRef(null);
  const handlePsearchTap = useCallback(() => {
    psearchTapCount.current += 1;
    clearTimeout(psearchTapTimer.current);
    psearchTapTimer.current = setTimeout(() => { psearchTapCount.current = 0; }, 1500);
    if (psearchTapCount.current >= 3) {
      psearchTapCount.current = 0;
      switchMode(activityMode === "psearch" ? null : "psearch");
    }
  }, [activityMode]);

  const [showDebug, setShowDebug] = useState(false);
  const [debugRadius, setDebugRadius] = useState("15");
  const [debugResults, setDebugResults] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  // Curated venues panel — no password gate; access is controlled client-side
  // by the hidden 5-tap gesture that opens the debug panel.
  const [debugInspector, setDebugInspector] = useState(() => {
    try { return localStorage.getItem("pg_debug_inspector") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("pg_debug_inspector", debugInspector ? "1" : "0"); } catch {}
  }, [debugInspector]);
  const [curatedVenues, setCuratedVenues] = useState([]);
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [addVenueForm, setAddVenueForm] = useState({ name: "", lat: "", lng: "", features: [], address: "", yearBuilt: "", neighborhood: "" });
  const [editingVenueId, setEditingVenueId] = useState(null);
  const [addVenueLoading, setAddVenueLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);

  const LOADING_PHASES = kidsMode ? [
    { icon: "🔭", text: "Looking for adventures nearby…" },
    { icon: "☀️", text: "Checking if the sun is out…" },
    { icon: "🎯", text: "Finding the good stuff…" },
    { icon: "🏆", text: "Picking your best options…" },
    { icon: "🎉", text: "Almost there!" },
  ] : [
    { icon: "🗺️", text: "Scouting the area…" },
    { icon: "🌤️", text: "Reading the weather…" },
    { icon: "📍", text: "Pinning the spots…" },
    { icon: "✨", text: "Picking the best ones…" },
    { icon: "🧭", text: "Almost ready!" },
  ];

  useEffect(() => {
    if (step !== "loading") { setLoadingPhase(0); return; }
    const interval = setInterval(() => {
      setLoadingPhase(p => Math.min(p + 1, LOADING_PHASES.length - 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [step]);

  useEffect(() => {
    const up = () => setIsOnline(true);
    const down = () => setIsOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  // Prefetch GPS on mount so coords are ready when user taps Find
  useEffect(() => {
    if (!navigator.geolocation) return;
    prefetchedCoordsRef.current = "pending";
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => { prefetchedCoordsRef.current = { lat: coords.latitude, lng: coords.longitude }; },
      () => { prefetchedCoordsRef.current = "denied"; },
      { timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  // Deep link: read ?spot= params on first load, auto-start geolocation.
  // Skipped when running as a standalone PWA (home screen icon) — in that
  // context a stale ?spot= URL would re-open the same venue every launch.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const spotOsmId = params.get("spot");
    if (!spotOsmId) return;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) { window.history.replaceState({}, "", "/"); return; }
    const dl = {
      osmId: spotOsmId,
      name: params.get("name") || "Shared Spot",
      mode: params.get("mode") || null,
      lat: parseFloat(params.get("lat")),
      lng: parseFloat(params.get("lng")),
    };
    deepLinkRef.current = dl;
    window.history.replaceState({}, "", "/");
    if (dl.mode) {
      setActivityMode(dl.mode);
      try { localStorage.setItem("pg_exp_activity", dl.mode); } catch {}
    }
    // Auto-start — will trigger geolocation or IP fallback
    if (navigator.geolocation) {
      tryGeolocation();
    } else {
      tryIPLocation().then(ok => { if (!ok) { setState("manual"); } });
    }
  }, []); // eslint-disable-line

  // GPS retry after page reload: skip splash and go straight to GPS attempt
  useEffect(() => {
    if (!sessionStorage.getItem("pg_gps_retry")) return;
    sessionStorage.removeItem("pg_gps_retry");
    if (navigator.geolocation) {
      tryGeolocation();
    } else {
      tryIPLocation().then(ok => { if (!ok) { setState("manual"); } });
    }
  }, []); // eslint-disable-line

  // ?go=1 — from the compare/about page CTA; skip splash, auto-start search
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get("go")) return;
    window.history.replaceState({}, "", "/");
    start();
  }, []); // eslint-disable-line

  // Auto-run analysis when the detail screen opens with no cached result
  // and no error from a prior attempt. Keeps the screen self-explanatory
  // (Verdict card populates without user action) while the analysis-cache
  // ensures repeat opens are free.
  useEffect(() => {
    if (step !== "detail" || !selected) return;
    if (analysis || analysisError || analyzing) return;
    runAnalysis();
    // eslint-disable-next-line
  }, [step, selected?.osmId, analysis, analysisError, analyzing]);

  // When the user shifts the visit time, invalidate the current analysis so
  // the auto-run effect above re-fires with the new sun context.
  useEffect(() => {
    if (step !== "detail" || !selected) return;
    setAnalysis(null);
    setAnalysisError("");
    // eslint-disable-next-line
  }, [visitTime]);

  // Fetch "Pair this with" suggestions when a venue is opened — one cafe,
  // one view, one food spot near it. Drives the section the same way the
  // bottom sheet works on the home screen.
  useEffect(() => {
    if (step !== "detail" || !selected) return;
    let cancelled = false;
    (async () => {
      try {
        const [cafe, view, food] = await Promise.all([
          fetchOutingSuggestions("cafe", selected.lat, selected.lng),
          fetchOutingSuggestions("view", selected.lat, selected.lng),
          fetchOutingSuggestions("food", selected.lat, selected.lng),
        ]);
        if (cancelled) return;
        // Take the closest of each kind, dedup by 80m proximity.
        const picks = [cafe[0], view[0], food[0]].filter(Boolean);
        const out = [];
        for (const p of picks) {
          if (out.find(x => haversine(x.lat, x.lng, p.lat, p.lng) < 0.05)) continue;
          out.push(p);
        }
        setPairSuggestions(out);
      } catch {
        if (!cancelled) setPairSuggestions([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [step, selected?.osmId]);

  // Shifted sun info for the detail view — recalculated when the user
  // picks a future visit time. Falls back to current sunInfo when near "now".
  const visitSunInfo = useMemo(() => {
    if (!location || !sunInfo) return sunInfo;
    if (Math.abs(visitTime.getTime() - Date.now()) < 120000) return sunInfo;
    const sun = getSunPosition(location.lat, location.lng, visitTime);
    const sunScoreData = getSunScore(sun.altitude, sun.azimuth, sunInfo.sunrise, sunInfo.sunset, visitTime);
    return { ...sun, sunrise: sunInfo.sunrise, sunset: sunInfo.sunset, score: sunScoreData.score, label: sunScoreData.label };
  }, [visitTime, location, sunInfo]);

  // 30-min future slots from now until min(sunset, 9 PM) for the time picker.
  const timeSlotOptions = useMemo(() => {
    const now = new Date();
    const ninepm = new Date(now); ninepm.setHours(21, 0, 0, 0);
    const ceiling = sunInfo?.sunset && sunInfo.sunset < ninepm ? sunInfo.sunset : ninepm;
    const slots = [];
    let cursor = new Date(now);
    cursor.setMinutes(cursor.getMinutes() < 30 ? 30 : 60, 0, 0);
    while (cursor <= ceiling) { slots.push(new Date(cursor)); cursor = new Date(cursor.getTime() + 30 * 60 * 1000); }
    return slots;
  }, [sunInfo?.sunset]);

  // Analysis runs automatically on first open of the detail view (per the
  // Sunny redesign spec — no opt-in button). Cached results still
  // auto-populate from openDetail so repeat visits within a session are
  // free, and a manual retry button remains for failed analyses.
  const runAnalysis = useCallback(async () => {
    if (!selected || analyzing) return;
    const pg = selected;
    setAnalysisError("");
    const cacheKey = `${activityMode || "playground"}_${pg.osmId}_${visitTime.toISOString().slice(0, 13)}`;
    if (analysisCache.current.has(cacheKey)) {
      setAnalysis(analysisCache.current.get(cacheKey));
      return;
    }
    setAnalyzing(true);
    try {
      const result = await analyzePlaygroundWithClaude({ playground: pg, weather, sunInfo: visitSunInfo, selectedFeatures, activityMode, visitTime });
      analysisCache.current.set(cacheKey, result);
      setAnalysis(result);
    } catch (err) {
      const msg = err?.message || String(err);
      console.error("[Analysis] failed:", msg);
      setAnalysis(null);
      setAnalysisError(msg);
    } finally {
      setAnalyzing(false);
    }
  }, [selected, analyzing, weather, sunInfo, visitSunInfo, selectedFeatures, activityMode, visitTime]);

  useEffect(() => {
    const restrooms = (outingSuggestions.items || []).filter(s => s.kind === "restroom" && s.osmId);
    if (!restrooms.length) return;
    const keys = restrooms.map(s => s.hasPlayground ? `${s.osmId}-restroom` : String(s.osmId));
    Promise.all(keys.map(k => fetch(`/api/condition/${k}`).then(r => r.json()).catch(() => null)))
      .then(results => {
        const map = {};
        keys.forEach((k, i) => { if (results[i]?.count) map[k] = results[i]; });
        if (Object.keys(map).length) setRestroomRatings(prev => ({ ...prev, ...map }));
      });
  }, [outingSuggestions.items]); // eslint-disable-line

  useEffect(() => {
    const dl = deepLinkRef.current;
    if (!dl || step !== "results" || playgrounds.length === 0) return;
    const found = playgrounds.find(pg => String(pg.osmId) === String(dl.osmId));
    deepLinkRef.current = null;
    if (found) {
      openDetail(found);
    } else if (!isNaN(dl.lat) && !isNaN(dl.lng)) {
      // Spot not in local results (user may be far away) — open stub immediately,
      // then upgrade with full venue data (features, community reports) from a
      // targeted search near the venue's own coordinates.
      openDetail({ osmId: dl.osmId, name: dl.name, lat: dl.lat, lng: dl.lng, distance: "?", features: [], facing: "N", isIndoor: false, source: "shared" });
      fetchRealPlaygrounds(dl.lat, dl.lng, 1000)
        .then(pgs => {
          const rich = pgs.find(pg => String(pg.osmId) === String(dl.osmId));
          if (!rich) return;
          setSelected(prev => {
            if (!prev || String(prev.osmId) !== String(dl.osmId)) return prev;
            return {
              ...rich, source: "shared",
              // Preserve enrichment already loaded by the /api/nearby callback
              photoRef: prev.photoRef || rich.photoRef || null,
              photoRefs: prev.photoRefs?.length ? prev.photoRefs : (rich.photoRefs || []),
              nearbyFood: prev.nearbyFood ?? rich.nearbyFood,
              parking: prev.parking || rich.parking || null,
              website: prev.website || rich.website || null,
              googleMapsUri: prev.googleMapsUri || rich.googleMapsUri || null,
            };
          });
        })
        .catch(() => {});
    }
  }, [step, playgrounds]); // eslint-disable-line

  const [locationSearch, setLocationSearch] = useState("");
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);

  const [debugLoading, setDebugLoading] = useState(false);
  const debugTapCount = useRef(0);
  const debugTapTimer = useRef(null);
  const handleDebugTap = useCallback(() => {
    debugTapCount.current += 1;
    clearTimeout(debugTapTimer.current);
    debugTapTimer.current = setTimeout(() => { debugTapCount.current = 0; }, 1500);
    if (debugTapCount.current >= 5) {
      debugTapCount.current = 0;
      setShowDebug(true);
      fetch("/api/cache-stats").then(r => r.json()).then(setCacheStats).catch(() => {});
      fetch("/api/admin/stats", { headers: adminHeaders() }).then(r => r.ok ? r.json() : null).then(setUsageStats).catch(() => {});
      fetch("/api/curated-venues").then(r => r.ok ? r.json() : []).then(setCuratedVenues).catch(() => {});
    }
  }, []);

  const runDebugSearch = useCallback(async () => {
    if (!location) return;
    setDebugLoading(true);
    setDebugResults(null);
    const t0 = Date.now();
    try {
      const miles = parseFloat(debugRadius) || 15;
      const meters = Math.round(miles * 1609.34);
      const pgs = await fetchRealPlaygrounds(location.lat, location.lng, meters);
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      const counts = {};
      FEATURES.forEach(f => { counts[f.id] = 0; });
      pgs.forEach(pg => pg.features.forEach(f => { if (counts[f] !== undefined) counts[f]++; }));
      // Per-playground breakdown
      const pgList = pgs.map(pg => ({
        name: pg.name,
        distance: pg.distance,
        source: pg.source || "osm",
        isSchool: pg.isSchool,
        features: pg.features,
      }));
      setDebugResults({ total: pgs.length, counts, miles, elapsed, pgList });
    } catch (e) {
      setDebugResults({ error: e.message, elapsed: ((Date.now() - t0) / 1000).toFixed(1) });
    }
    setDebugLoading(false);
  }, [location, debugRadius]);

  const toggleFavorite = useCallback((pg, e) => {
    if (e) e.stopPropagation();
    setFavorites(prev => {
      const next = { ...prev };
      if (next[pg.osmId]) { delete next[pg.osmId]; }
      else { next[pg.osmId] = { name: pg.name, lat: pg.lat, lng: pg.lng, osmId: pg.osmId }; }
      localStorage.setItem("pg_favorites", JSON.stringify(next));
      return next;
    });
  }, []);

  const applyLocation = useCallback(async (lat, lng, label, fetchWeatherFn, radiusMeters, modeOverride) => {
    // Resolve the effective radius: explicit arg wins, then the user's
    // current persisted preference (via ref so we don't need state in deps),
    // then a 3200m (2mi) safety net that matches the playground default.
    const effectiveRadius = radiusMeters ?? searchRadiusRef.current ?? 3200;
    // Cancellation token: if a newer applyLocation call starts while this one is still
    // running async operations (Overpass fetch, geocoding loop), we drop stale state updates.
    const callId = ++locationCallIdRef.current;
    const isCurrent = () => locationCallIdRef.current === callId;

    const sun = getSunPosition(lat, lng);
    const { sunrise, sunset } = getSunTimes(lat, lng);
    const sunScoreData = getSunScore(sun.altitude, sun.azimuth, sunrise, sunset);
    // Stash sunrise/sunset on sunInfo so the SunArcBar in the header can
    // render without redoing the per-day math.
    setSunInfo({ ...sun, sunrise, sunset, score: sunScoreData.score, label: sunScoreData.label });
    setLocation({ lat, lng });
    setLocationLabel(label);
    setState("loading");
    setWalkTimes({});
    setRoadDists({});
    // Clear the previous location's results up front. Without this, a failed or
    // slow new-location search (the 35s safety net below flips back to "results"
    // with loadError set) would leave the PRIOR city's playgrounds on screen —
    // e.g. searching "San Jose" after a Pacifica GPS load showed Pacifica's
    // playgrounds under the San Jose label. onBatch repopulates from this
    // location's own stale cache if one exists; otherwise the error state shows
    // empty instead of misleading wrong-city data. The "loading" view is shown
    // during the fetch, so this clear isn't visible on a normal fast search.
    setPlaygrounds([]);
    fetchWeatherFn(lat, lng);
    setLoadError(false);
    setSearchRadius(effectiveRadius);
    // Safety net: if loading takes longer than 35s, bail out to results (or error)
    const loadingTimeout = setTimeout(() => {
      setState(s => {
        if (s === "loading") { setLoadError("network"); return "results"; }
        return s;
      });
    }, 35000);
    try {
      const useMode = modeOverride !== undefined ? modeOverride :
                      splashModeRef.current !== undefined ? (() => { const m = splashModeRef.current; splashModeRef.current = undefined; return m; })() :
                      activityMode;
      let gotBatch = false;
      const onBatch = (batch) => { gotBatch = true; setPlaygrounds(batch); setState(s => s === "loading" ? "results" : s); };
      const pgs = await (useMode ? fetchActivitySpots(useMode, lat, lng, effectiveRadius, onBatch) : fetchRealPlaygrounds(lat, lng, effectiveRadius, onBatch));
      clearTimeout(loadingTimeout);
      if (!isCurrent()) return; // a newer search started while we were fetching — discard
      if (pgs.length === 0) setLoadError("no_results");
      setFetchedRadius(effectiveRadius);

      // Deprioritize results from a different city (crow-flies distance can be
      // misleading when a mountain range or bay separates two areas)
      const cityHint = (label || "").split(",")[0].trim().toLowerCase();
      const isLocalResult = (pg) => {
        if (!cityHint || cityHint === "your location") return true;
        if (!pg.address) return true; // no address yet (pre-geocode) — assume local, sort by distance
        return pg.address.toLowerCase().includes(cityHint) ||
               (pg.name || "").toLowerCase().includes(cityHint);
      };
      pgs.sort((a, b) => {
        const aLocal = isLocalResult(a), bLocal = isLocalResult(b);
        if (aLocal !== bLocal) return aLocal ? -1 : 1;
        return parseFloat(a.distance) - parseFloat(b.distance);
      });

      setPlaygrounds(pgs);
      setState(s => s === "loading" ? "results" : s); // don't clobber "detail" (e.g. deep-link opened venue mid-fetch)
      // Check which spots have community-uploaded photos (fire-and-forget).
      // Sets userPhotoUrl so the card thumbnail shows immediately, covering
      // activity-mode searches that don't go through overlayDynamic.
      const batchIds = pgs.map(pg => String(pg.osmId)).filter(Boolean);
      if (batchIds.length) {
        fetch(`/api/photos/batch?ids=${batchIds.map(encodeURIComponent).join(",")}`)
          .then(r => r.ok ? r.json() : {})
          .then(urlMap => {
            const overrides = nameOverridesRef.current;
            const hasUrls = urlMap && Object.keys(urlMap).length > 0;
            const hasOverrides = Object.keys(overrides).length > 0;
            if (!hasUrls && !hasOverrides) return;
            setPlaygrounds(prev => prev.map(p => {
              const id = String(p.osmId);
              const url = urlMap?.[id];
              const name = overrides[id];
              if (!url && !name) return p;
              return { ...p, ...(url && { userPhotoUrl: url, hasUserPhoto: true }), ...(name && { name }) };
            }));
          })
          .catch(() => {});
      }
      // Fetch real walking times from OSRM in the background (fire-and-forget)
      fetch("/api/walk-times", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userLat: lat, userLng: lng,
          destinations: pgs.map(pg => ({ id: pg.osmId, lat: pg.lat, lng: pg.lng })),
        }),
      }).then(r => r.ok ? r.json() : null).then(data => {
        if (!data) return;
        // Server returns { osmId: { secs, meters } }. Unpack into two maps
        // and re-sort playgrounds by road distance (ascending) so the order
        // matches what you'd actually drive, not the straight-line crow-flies.
        const times = {}, dists = {};
        for (const [id, v] of Object.entries(data)) {
          // Accept both legacy number shape and new { secs, meters } shape.
          if (v != null && typeof v === "object") {
            times[id] = v.secs;
            dists[id] = v.meters != null ? v.meters / 1609.344 : null; // meters → miles
          } else {
            times[id] = v; // legacy fallback
          }
        }
        setWalkTimes(times);
        if (Object.keys(dists).length > 0) {
          // Patch road distances in place so the card/hero/detail labels refine
          // to real driving distance — but DON'T re-sort the rendered list. The
          // results paint in haversine order; reshuffling them (and the top
          // pick) a beat later when OSRM responds was visibly jarring. Road and
          // straight-line order are near-identical in practice, so the labels
          // updating in place reads as a refinement rather than a reshuffle.
          setRoadDists(dists);
        }
      }).catch(() => {});
      // Reverse geocode in the background (sequential, 1/s — Nominatim rate limit)
      // - Unnamed playgrounds: get neighbourhood name
      // - Any result without an address: get road + city + park name
      const modeLabel = useMode ? ACTIVITY_MODES.find(m => m.id === useMode)?.label : null;
      const isGenericActivityName = (pg) => modeLabel && pg.name.startsWith(`${modeLabel} (`);
      // Also rename spots in activity mode whose name is a mismatched type (e.g. OSM node named "Playground" in climbing mode)
      const isMismatchedName = (pg) => useMode && GENERIC_NAME_RE.test(pg.name) && !pg.isSchool;
      const needsRename = (pg) => isGenericActivityName(pg) || isMismatchedName(pg);
      const needsGeocode = pgs.filter(pg =>
        (GENERIC_NAME_RE.test(pg.name) && !pg.isSchool) ||
        (pg.isSchool && GENERIC_NAME_RE.test(pg.name)) ||
        !pg.address || needsRename(pg)
      ).slice(0, 15); // cap at 15 to keep background time reasonable
      const geocoded = {};
      let needNominatimDelay = false;
      for (const pg of needsGeocode) {
        const cached = geoCacheGet(pg.lat, pg.lng);
        // Skip cache for spots that need renaming — need fresh zoom=13 lookup
        if (cached && !needsRename(pg)) { geocoded[pg.osmId] = cached; continue; }
        if (needNominatimDelay) await new Promise(res => setTimeout(res, 1100));
        needNominatimDelay = true;
        try {
          // Use zoom=13 for spots needing rename — gets broader area name (park, reserve, headland)
          const zoom = needsRename(pg) ? 13 : 16;
          const r = await fetch(`/api/geocode/reverse?lat=${pg.lat}&lng=${pg.lng}&zoom=${zoom}`);
          const d = await r.json();
          const hood = d.address?.neighbourhood || d.address?.suburb || d.address?.hamlet || d.address?.village;
          const road = d.address?.road;
          const city = d.address?.city || d.address?.town || d.address?.village;
          const park = d.address?.leisure; // Nominatim returns named parks here
          // At zoom=13, d.name is the broader area name (e.g. "Marin Headlands", "Grey Whale Cove")
          // Also check specific address fields for natural/protected areas
          const areaName = d.name && d.name !== city && d.name !== hood ? d.name : null;
          const nearbyFeature = areaName ||
            d.address?.natural || d.address?.tourism ||
            d.address?.nature_reserve || d.address?.state_park || d.address?.national_park ||
            d.address?.beach || d.address?.cliff || d.address?.bay;
          const addr = [road, city].filter(Boolean).join(", ");
          const entry = { hood, road, city, park, nearbyFeature, addr };
          geoCacheSet(pg.lat, pg.lng, entry);
          geocoded[pg.osmId] = entry;
        } catch {}
      }
      // Disambiguate duplicates by appending street name
      const hoodCounts = {};
      Object.values(geocoded).forEach(({ hood }) => { if (hood) hoodCounts[hood] = (hoodCounts[hood] || 0) + 1; });
      if (!isCurrent()) return; // a newer search started during geocoding — discard name updates
      setPlaygrounds(prev => prev.map(p => {
        const g = geocoded[p.osmId];
        if (!g) return p;
        const updates = {};
        // Update name for unnamed playgrounds
        if (GENERIC_NAME_RE.test(p.name) && !needsRename(p)) {
          if (p.isSchool && p.nearestSchool) {
            updates.name = `${p.nearestSchool} Playground`;
          } else if (p.isSchool && (g.hood || g.road)) {
            updates.name = `${g.road || g.hood} School Playground`;
          } else if (!p.isSchool) {
            if (g.hood) {
              updates.name = hoodCounts[g.hood] > 1 && g.road
                ? `${g.hood} Playground (${g.road})`
                : `${g.hood} Playground`;
            } else if (g.park) {
              // Nominatim returned a park name (address.leisure) — use it directly
              updates.name = g.park;
            }
          }
        }
        // Update name for generic/mismatched activity spot names (e.g., "Climbing (Southeast)", OSM "Playground" in climbing mode)
        if (modeLabel && needsRename(p)) {
          const spotNoun = SPOT_INFO[useMode]?.noun || modeLabel.toLowerCase();
          const label = spotNoun.charAt(0).toUpperCase() + spotNoun.slice(1);
          const byName = g.nearbyFeature || g.park || g.hood || (g.city ? `${g.city} area` : null);
          if (byName) updates.name = `${label} by ${byName}`;
        }
        // Fill in missing address
        if (!p.address && g.addr) updates.address = g.addr;
        // Associate with containing park (e.g. McLaren Park)
        if (g.park && !p.nearestPark) updates.nearestPark = g.park;
        return Object.keys(updates).length ? { ...p, ...updates } : p;
      }));
    } catch (e) {
      console.error("Overpass error:", e);
      clearTimeout(loadingTimeout);
      if (gotBatch) {
        // onBatch already showed partial results — keep them, don't wipe
        console.warn("Fetch failed after partial results; keeping batch");
      } else {
        setLoadError("network");
        setPlaygrounds([]);
      }
    }
    if (isCurrent()) {
      clearTimeout(loadingTimeout);
      setState(s => s === "loading" ? "results" : s); // don't clobber "detail" (e.g. deep-link)
    }
  }, [activityMode]); // eslint-disable-line

  // ── Fetch weather via Open-Meteo (free, no key needed) ──
  const fetchWeather = useCallback(async (lat, lng) => {
    const WEATHER_CACHE_KEY = `wx_${lat.toFixed(2)}_${lng.toFixed(2)}`;
    const WEATHER_TTL = 3 * 60 * 60 * 1000; // 3 hours
    const url = `/api/weather?lat=${lat}&lng=${lng}`;
    // Server allows up to ~10s (5s open-meteo + 5s wttr.in fallback) so cap
    // the client at 12s. On a first failure, retry once — transient upstream
    // blips are the most common cause of weather errors.
    const fetchOnce = () => fetch(url, { signal: AbortSignal.timeout(12000) });
    let data;
    try {
      let res;
      try {
        res = await fetchOnce();
        if (!res.ok) throw new Error(`Weather ${res.status}`);
      } catch {
        await new Promise(r => setTimeout(r, 800));
        res = await fetchOnce();
        if (!res.ok) throw new Error(`Weather ${res.status}`);
      }
      data = await res.json();
      const c = data.current;
      const code = c.weather_code;
      let condition = "Clear";
      if (code >= 45 && code <= 48) condition = "Fog";
      else if (code >= 51 && code <= 57) condition = "Drizzle";
      else if (code >= 61 && code <= 67) condition = "Rain";
      else if (code >= 71 && code <= 77) condition = "Snow";
      else if (code >= 80 && code <= 82) condition = "Rain";
      else if (code >= 85 && code <= 86) condition = "Snow";
      else if (code >= 95) condition = "Thunderstorm";
      else if (code === 1) condition = "Clear";
      else if (code === 2) condition = "PartlyCloudy";
      else if (code === 3) condition = "Clouds";
      // Override when precipitation is actually falling but model code says clear/cloudy
      if (c.precipitation > 0 && (condition === "Clear" || condition === "PartlyCloudy" || condition === "Clouds")) {
        condition = c.precipitation >= 2 ? "Rain" : "Drizzle";
      }
      // Override icon using cloud_cover when weather_code says clearer than it is
      if (condition === "Clear" && c.cloud_cover >= 50) condition = "Clouds";
      else if (condition === "Clear" && c.cloud_cover >= 20) condition = "PartlyCloudy";
      else if (condition === "PartlyCloudy" && c.cloud_cover >= 50) condition = "Clouds";
      const fog = condition === "Fog" || c.visibility < 1609;
      const w = {
        temp: Math.round(c.temperature_2m),
        humidity: c.relative_humidity_2m,
        wind: Math.round(c.wind_speed_10m),
        windDirection: c.wind_direction_10m ?? null,
        visibility: c.visibility ? (c.visibility / 1609).toFixed(1) : "10+",
        condition, fog, cloud: c.cloud_cover,
        precipitation: c.precipitation || 0,
        icon: WEATHER_ICONS[condition] || "🌤️",
        // Today's hourly forecast (open-meteo only; absent on the wttr.in
        // fallback) — powers the "best window today" banner on detail.
        hourly: data.hourly || null,
      };
      try { localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify({ ts: Date.now(), w })); } catch {}
      setWeather(w);
    } catch {
      // Try cached weather before falling back to "unavailable"
      try {
        const raw = localStorage.getItem(WEATHER_CACHE_KEY);
        if (raw) {
          const { ts, w } = JSON.parse(raw);
          if (Date.now() - ts < WEATHER_TTL) { setWeather({ ...w, stale: true }); return; }
        }
      } catch {}
      setWeather({ temp: null, humidity: null, wind: null, visibility: null, condition: "Unknown", fog: false, cloud: null, icon: "🌡️", unavailable: true });
    }
  }, []);

  const runLocationSearch = useCallback(async (q) => {
    if (!q.trim()) return;
    setLocationSearching(true);
    setLocationSearchResults([]);
    try {
      const latLngMatch = q.trim().match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
      if (latLngMatch) {
        const lat = parseFloat(latLngMatch[1]);
        const lng = parseFloat(latLngMatch[2]);
        setShowDebug(false);
        applyLocation(lat, lng, `${lat.toFixed(4)}, ${lng.toFixed(4)}`, fetchWeather, searchRadius);
        setLocationSearching(false);
        return;
      }
      const res = await fetch(`/api/geocode/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setLocationSearchResults(Array.isArray(data) ? data.slice(0, 5) : []);
    } catch (e) {
      setLocationSearchResults([]);
    }
    setLocationSearching(false);
  }, [applyLocation, fetchWeather, searchRadius]); // eslint-disable-line

  const expandSearch = useCallback(() => {
    if (!location) return;
    const nextOpt = RADIUS_OPTIONS.find(opt => opt.meters > searchRadius);
    if (!nextOpt) return;
    setSearchRadius(nextOpt.meters);
    applyLocation(location.lat, location.lng, locationLabel, fetchWeather, nextOpt.meters);
  }, [location, locationLabel, searchRadius, applyLocation, fetchWeather]);

  // "Search this area" — search the map view the user framed. The radius
  // follows the viewport (snapped to a preset) so a zoomed-out view searches
  // wide instead of returning "nothing within 2mi", and a zoomed-in one stays
  // tight. Falls back to the current radius if the map never reported a view.
  const searchMapArea = useCallback(() => {
    if (!mapCenter) return;
    const { lat, lng, viewRadius } = mapCenter;
    const nextRadius = radiusForViewport(viewRadius) ?? searchRadius;
    setMapCenter(null);
    setViewportSearchToken(t => t + 1);
    applyLocation(lat, lng, "Map area", fetchWeather, nextRadius, activityMode);
  }, [mapCenter, searchRadius, applyLocation, fetchWeather, activityMode]);

  const tryIPLocation = useCallback(async () => {
    try {
      const r = await fetch("/api/ip-location");
      if (!r.ok) return false;
      const { lat, lng, label } = await r.json();
      if (label) { ipCityHintRef.current = label; setIpCityHint(label); }
      if (lat && lng) { setLocationSource("ip"); applyLocation(lat, lng, label, fetchWeather); return true; }
    } catch {}
    return false;
  }, [applyLocation, fetchWeather]);

  const tryGeolocation = useCallback(() => {
    setState("locating");
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        setLocationSource("gps");
        applyLocation(coords.latitude, coords.longitude, "Your location", fetchWeather);
        try {
          const r = await fetch(`/api/geocode/reverse?lat=${coords.latitude}&lng=${coords.longitude}`);
          const d = await r.json();
          const a = d.address || {};
          const place = a.neighbourhood || a.suburb || a.quarter || a.village || a.town || a.city;
          if (place) setLocationLabel(place);
        } catch {}
      },
      async () => {
        // GPS denied — try IP-based location before falling back to manual
        const ok = await tryIPLocation();
        if (!ok) { setState("manual"); setTimeout(() => inputRef.current?.focus(), 100); }
      },
      { timeout: 7000 }
    );
  }, [applyLocation, fetchWeather, tryIPLocation]);

  const start = async () => {
    if (!navigator.geolocation) {
      const ok = await tryIPLocation();
      if (!ok) { setState("manual"); }
      return;
    }
    const prefetched = prefetchedCoordsRef.current;
    // GPS already resolved — use immediately, no wait
    if (prefetched && prefetched !== "pending" && prefetched !== "denied") {
      const { lat, lng } = prefetched;
      setLocationSource("gps");
      applyLocation(lat, lng, "Your location", fetchWeather);
      fetch(`/api/geocode/reverse?lat=${lat}&lng=${lng}`)
        .then(r => r.json()).then(d => {
          const a = d.address || {};
          const place = a.neighbourhood || a.suburb || a.quarter || a.village || a.town || a.city;
          if (place) setLocationLabel(place);
        }).catch(() => {});
      return;
    }
    // GPS denied during prefetch — skip straight to IP fallback
    if (prefetched === "denied") {
      const ok = await tryIPLocation();
      if (!ok) { setState("manual"); setTimeout(() => inputRef.current?.focus(), 100); }
      return;
    }
    // Prefetch still pending or not started — normal flow (GPS already in progress)
    tryGeolocation();
  };

  const applyManualResult = (r) => {
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    const label = r.display_name?.split(",").slice(0, 2).join(",").trim() || manualInput.trim();
    setManualResults([]);
    setLocationSource("manual");
    bumpManualCount();
    saveRecentLocation(label, lat, lng);
    applyLocation(lat, lng, label, fetchWeather);
  };

  // Apply a previously-used location directly without re-geocoding.
  const applyRecentLocation = (r) => {
    setManualResults([]);
    setGeocodeError("");
    setLocationSource("manual");
    bumpManualCount();
    saveRecentLocation(r.label, r.lat, r.lng);
    applyLocation(r.lat, r.lng, r.label, fetchWeather);
  };

  const submitManual = async () => {
    if (!manualInput.trim()) return;
    setGeocodeError("");
    setManualResults([]);
    setGeocoding(true);
    try {
      // Try local DB first (instant, no API call)
      try {
        const { lat, lng, label } = await geocodeLocation(manualInput.trim());
        setLocationSource("manual");
        bumpManualCount();
        saveRecentLocation(label, lat, lng);
        applyLocation(lat, lng, label, fetchWeather);
        return;
      } catch {}
      // Fall back to Nominatim — works for any city, neighborhood, or address worldwide
      const res = await fetch(`/api/geocode/search?q=${encodeURIComponent(manualInput.trim())}`);
      const data = await res.json();
      if (!res.ok) {
        // Server surfaces Nominatim failures as non-200 with {error, status}.
        // Don't confuse users with a "not found" message when the lookup
        // itself broke.
        setGeocodeError(
          data?.status === 429
            ? "Location lookup is rate-limited. Wait a few seconds and try again."
            : "Location service unavailable. Try again in a moment."
        );
      } else if (Array.isArray(data) && data.length > 0) {
        if (data.length === 1) {
          applyManualResult(data[0]);
        } else {
          setManualResults(data.slice(0, 5));
        }
      } else {
        setGeocodeError(`Couldn't find "${manualInput.trim()}". Try adding a country or zip code.`);
      }
    } catch {
      setGeocodeError("Couldn't connect. Check your network and try again.");
    } finally {
      setGeocoding(false);
    }
  };

  const openDetail = async (pg) => {
    setVisitTime(new Date()); // reset time shift when opening a new playground
    setSelected(pg);
    setDetailGeoInfo(null);
    setAnalysis(null);
    setAnalysisError("");
    // Lazy reverse-geocode if address is missing or name is generic
    if (!pg.address || GENERIC_NAME_RE.test(pg.name)) {
      fetch(`/api/geocode/reverse?lat=${pg.lat}&lng=${pg.lng}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          const a = data.address || {};
          const road = a.road || a.pedestrian || a.path || null;
          const hood = a.neighbourhood || a.suburb || a.quarter || a.village || a.town || null;
          const city = a.city || a.town || a.village || null;
          const address = [road, city].filter(Boolean).join(", ") || null;
          const suggestedName = road ? `${road} Playground` : hood ? `${hood} Playground` : null;
          setDetailGeoInfo({ address, suggestedName, hood });
          // Backfill the improved name into results so the card shows the same name
          if (suggestedName) {
            setPlaygrounds(prev => prev.map(p =>
              String(p.osmId) === String(pg.osmId) ? { ...p, name: suggestedName, address: address || p.address } : p
            ));
          }
        })
        .catch(() => {});
    }
    // Fetch parking + food + Google photos for spots that don't already have them:
    // - activity-mode spots never have them server-side
    // - curated playgrounds come from the store with parking=null, nearbyFood=[],
    //   and no photo refs
    // OSM/Google playgrounds in playground mode get these in buildEnrichedPlaygrounds.
    const needsNearby =
      pg.source === "shared" ||
      (activityMode && pg.nearbyFood === undefined) ||
      (pg.source === "curated" && !pg.parking && (!pg.nearbyFood || pg.nearbyFood.length === 0));
    if (needsNearby) {
      const nameQuery = pg.name ? `&name=${encodeURIComponent(pg.name)}` : "";
      fetch(`/api/nearby?lat=${pg.lat}&lng=${pg.lng}${nameQuery}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          setSelected(prev => {
            if (!prev || String(prev.osmId) !== String(pg.osmId)) return prev;
            const vm = data.venueMatch || {};
            const newPhotoRef  = prev.photoRef  || vm.photoRef  || null;
            const newPhotoRefs = (prev.photoRefs && prev.photoRefs.length) ? prev.photoRefs : (vm.photoRefs || []);
            // Propagate discovered photo refs back into the results list so the
            // card thumbnail shows after viewing the detail (without a re-fetch).
            if ((newPhotoRef && !prev.photoRef) || (newPhotoRefs.length && !prev.photoRefs?.length)) {
              setPlaygrounds(pList => pList.map(p =>
                String(p.osmId) === String(pg.osmId)
                  ? { ...p, photoRef: newPhotoRef, photoRefs: newPhotoRefs }
                  : p
              ));
            }
            return {
              ...prev,
              parking: data.parking || prev.parking,
              nearbyFood: data.nearbyFood,
              photoRef:  newPhotoRef,
              photoRefs: newPhotoRefs,
              address: prev.address || vm.address || null,
              website: prev.website || vm.website || null,
              googleMapsUri: prev.googleMapsUri || vm.googleMapsUri || null,
            };
          });
        })
        .catch(() => {});
    }
    setPgRating(null);
    setPgCondition(pg.condition || null);
    setCommunityFeatures([]);
    setShowAddFeatures(selectedFeatures.some(f => !(pg.features || []).includes(f)));
    const savedRating = (() => { try { return JSON.parse(localStorage.getItem("pg_ratings") || "{}")[pg.osmId] || null; } catch { return null; } })();
    setUserRating(savedRating);
    const savedCondition = (() => { try { return JSON.parse(localStorage.getItem("pg_condition") || "{}")[pg.osmId] || null; } catch { return null; } })();
    setUserCondition(savedCondition?.rating || null);
    setUserConditionTags(savedCondition?.tags || []);
    const sessionRatings = (() => { try { return JSON.parse(sessionStorage.getItem("pg_session_ratings") || "{}"); } catch { return {}; } })();
    setSessionRatingLocked(!!sessionRatings[String(pg.osmId)]);
    setState("detail");
    // Fetch community-reported features (fire-and-forget)
    fetch(`/api/features/${pg.osmId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.features?.length) return;
        setCommunityFeatures(data.features);
        // Propagate into card list so filters + badges see them too
        setPlaygrounds(prev => prev.map(p =>
          String(p.osmId) === String(pg.osmId)
            ? { ...p, features: [...new Set([...p.features, ...data.features])] }
            : p
        ));
      })
      .catch(() => {});
    fetch(`/api/ratings/${pg.osmId}`).then(r => r.json()).then(setPgRating).catch(() => {});
    fetch(`/api/condition/${pg.osmId}`).then(r => r.json()).then(d => {
      if (d && (d.count || 0) > 0) setPgCondition(d);
    }).catch(() => {});
    if ((pg.features || []).includes("restrooms")) {
      const rKey = `${pg.osmId}-restroom`;
      const savedR = (() => { try { return JSON.parse(localStorage.getItem("restroom_ratings") || "{}")[rKey] || null; } catch { return null; } })();
      if (savedR) setRestroomRatings(prev => ({ ...prev, [rKey]: { _local: savedR } }));
      fetch(`/api/condition/${rKey}`).then(r => r.json()).then(d => {
        if (d?.count > 0) setRestroomRatings(prev => ({ ...prev, [rKey]: d }));
      }).catch(() => {});
    }
    // Auto-populate analysis ONLY from the in-memory cache. A miss shows the
    // "Analyze this spot" button so the user opts in to spending an API call.
    const cacheKey = `${activityMode || "playground"}_${pg.osmId}_${new Date().toISOString().slice(0, 13)}`; // uses real "now" since visitTime just reset
    if (analysisCache.current.has(cacheKey)) {
      setAnalysis(analysisCache.current.get(cacheKey));
    }
    setAnalyzing(false);
  };

  // Outing categories — chips on the outing screen for user-driven
  // suggestions. Each category can declare a Google Places text query
  // and/or a list of OSM tag pairs. The fetch function fires both in
  // parallel and merges, with OSM winning ties (better for natural
  // features and parks; Google better for businesses with addresses).
  const OUTING_CATEGORIES = [
    { kind: "cafe",       icon: "☕",   label: "Cafe",       query: "coffee shop OR cafe" },
    { kind: "food",       icon: "🍽",    label: "Food",       query: "family restaurant" },
    { kind: "icecream",   icon: "🍦",   label: "Ice cream",  query: "ice cream shop" },
    { kind: "playground", icon: "🛝",   label: "Playground", query: "playground", osm: [["leisure", "playground"]] },
    { kind: "park",       icon: "🌳",   label: "Park",       query: "public park",       osm: [["leisure", "park"]] },
    { kind: "picnic",     icon: "🧺",   label: "Picnic",     query: "picnic area",       osm: [["tourism", "picnic_site"]] },
    { kind: "view",       icon: "🌅",   label: "View",       query: "viewpoint",         osm: [["tourism", "viewpoint"], ["natural", "peak"]] },
    { kind: "beach",      icon: "🏖️",   label: "Beach",      query: "public beach",      osm: [["natural", "beach"]] },
    { kind: "skatepark",  icon: "🛹",   label: "Skatepark",  query: "skatepark",         osm: [["leisure", "skateboard_park"]] },
    { kind: "pump_track", icon: "🚵",   label: "Pump track", query: "pump track",        osm: [["sport", "pump_track"], ["leisure", "pump_track"]] },
    { kind: "restroom",   icon: "🚻",   label: "Restroom",   query: "public restroom",   osm: [["amenity", "toilets"], [["leisure", "playground"], ["toilets", "yes"]]] },
  ];

  // Pull suggestions for one category, anchored on lat/lng. Fires both a
  // Google Places text search AND an OSM Overpass query (when the category
  // has osm tags), then merges, dedups, distance-filters, and sorts.
  //
  // Why both: Google is biased toward businesses with addresses (cafes,
  // restaurants), while OSM has way better coverage of natural features
  // (viewpoints, beaches) and recreational facilities (skateparks,
  // playgrounds, pump tracks). Either alone misses ~half the spots.
  //
  // Distance filter is hard at 2 mi — Google's "location bias" is a ranking
  // preference, not a radius cap, so it can return famous spots 30+ mi
  // away if they match the query.
  const fetchOutingSuggestions = async (kind, lat, lng, radiusM = 3200) => {
    const cat = OUTING_CATEGORIES.find(c => c.kind === kind);
    if (!cat) return [];
    const MAX_MI = radiusM / 1609.34;
    const RADIUS_M = radiusM;

    const googlePromise = cat.query
      ? fetch("/api/places/text", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: cat.query, lat, lng, radiusMeters: RADIUS_M }),
        }).then(r => r.ok ? r.json() : { places: [] }).catch(() => ({ places: [] }))
      : Promise.resolve({ places: [] });

    const osmPromise = (cat.osm && cat.osm.length > 0)
      ? fetch("/api/overpass", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            data: `[out:json][timeout:15];\n(\n${cat.osm.flatMap(tagSpec => {
              if (Array.isArray(tagSpec[0])) {
                const filters = tagSpec.map(([k, v]) => `["${k}"="${v}"]`).join("");
                return [`  node${filters}(around:${RADIUS_M},${lat},${lng});`, `  way${filters}(around:${RADIUS_M},${lat},${lng});`];
              }
              const [k, v] = tagSpec;
              return [`  node["${k}"="${v}"](around:${RADIUS_M},${lat},${lng});`, `  way["${k}"="${v}"](around:${RADIUS_M},${lat},${lng});`];
            }).join("\n")}\n);\nout center tags;`,
          }),
        }).then(r => r.ok ? r.json() : { elements: [] }).catch(() => ({ elements: [] }))
      : Promise.resolve({ elements: [] });

    const [googleData, osmData] = await Promise.all([googlePromise, osmPromise]);

    const googleResults = (googleData?.places || []).map(p => {
      const pLat = p.location?.latitude, pLng = p.location?.longitude;
      if (!pLat || !pLng || !p.displayName?.text) return null;
      const dMi = haversine(lat, lng, pLat, pLng);
      return { kind, name: p.displayName.text, lat: pLat, lng: pLng, dMi, src: "g" };
    }).filter(Boolean);

    // Helper: best-effort name for an unnamed OSM element. Tries tag
    // variations first (name:en, brand, operator, addr:street), then falls
    // back to compass bearing + distance so we never show three generic
    // "Playground" rows the user can't tell apart.
    const compass = (fromLat, fromLng, toLat, toLng) => {
      const bearing = Math.atan2(toLng - fromLng, toLat - fromLat) * 180 / Math.PI;
      return ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(((bearing + 360) % 360) / 45) % 8];
    };
    const osmName = (el, anchorLat, anchorLng, dMi) => {
      const t = el.tags || {};
      if (t.name) return t.name;
      if (t["name:en"]) return t["name:en"];
      if (t.brand) return t.brand;
      if (t.operator) return `${t.operator} ${cat.label}`;
      if (t["addr:street"]) return `${t["addr:street"]} ${cat.label}`;
      const pLat = el.lat ?? el.center?.lat;
      const pLng = el.lon ?? el.center?.lon;
      if (pLat != null && pLng != null) {
        return `${cat.label} · ${compass(anchorLat, anchorLng, pLat, pLng)} ${dMi.toFixed(1)} mi`;
      }
      return cat.label;
    };

    const osmResults = (osmData?.elements || []).map(el => {
      const pLat = el.lat ?? el.center?.lat;
      const pLng = el.lon ?? el.center?.lon;
      if (!pLat || !pLng) return null;
      const dMi = haversine(lat, lng, pLat, pLng);
      return { kind, name: osmName(el, lat, lng, dMi), lat: pLat, lng: pLng, dMi, src: "osm",
        osmId: el.id,
        accessKey: !!(el.tags?.access === "key" || el.tags?.locked === "yes"),
        hasPlayground: el.tags?.leisure === "playground",
      };
    }).filter(Boolean);

    // Merge with OSM first — wins ties on natural features. Dedup at ~80m
    // proximity to catch the case where Google and OSM both list the same
    // viewpoint with different names.
    const merged = [...osmResults, ...googleResults];
    const out = [];
    for (const r of merged) {
      if (r.dMi > MAX_MI) continue;
      const dupe = out.find(x => haversine(x.lat, x.lng, r.lat, r.lng) < 0.05);
      if (dupe) continue;
      out.push(r);
    }
    return out.sort((a, b) => a.dMi - b.dMi).slice(0, 8);
  };

  // Open the outing builder anchored on a playground. Seeds the trip with
  // the playground itself + cafe + view suggestions. The user can then pick
  // any category from the chips to refresh suggestions.
  // The optional extraStop lets the "Pair this with" section pre-populate
  // an outing with both the venue and a suggested partner stop.
  const [outingCategory, setOutingCategory] = useState("cafe");
  const openOuting = async (pg, extraStop = null) => {
    if (!pg) return;
    setOutingAnchor(pg);
    setOutingStartTime(new Date());
    setOutingSearchRadius(3200);
    const initialStops = [{ kind: "play", name: displayName(pg.name, pg.address), lat: pg.lat, lng: pg.lng }];
    if (extraStop) initialStops.push(extraStop);
    setOutingStops(initialStops);
    setOutingSuggestions({ items: [] });
    setOutingCategory("cafe");
    setOutingSuggestLoading(true);
    setState("outing");
    // Suggest near the LAST stop so adding more stops after the extra one
    // continues to flow geographically.
    const anchor = extraStop || pg;
    const items = await fetchOutingSuggestions("cafe", anchor.lat, anchor.lng, 3200);
    setOutingSuggestions({ items });
    setOutingSuggestLoading(false);
  };

  const switchOutingCategory = async (kind, radiusM = 3200) => {
    if (!outingAnchor) return;
    setOutingCategory(kind);
    setOutingSearchRadius(radiusM);
    setOutingSuggestLoading(true);
    // Suggest near the LAST stop, not the anchor — keeps the trip flowing
    // geographically (snack near where you'll just have been, not where you
    // started 2 stops ago).
    const last = outingStops[outingStops.length - 1] || outingAnchor;
    const items = await fetchOutingSuggestions(kind, last.lat, last.lng, radiusM);
    setOutingSuggestions({ items });
    setOutingSuggestLoading(false);
  };

  const filteredPlaygrounds = useMemo(() => {
    const radiusMiles = searchRadius / 1609;
    // "Little Ones": traditional playground equipment — swings, slides, sandbox, etc.
    // Most OSM nodes default to ["swings","slide"] when untagged, so this matches
    // virtually all real playgrounds and excludes pure sports venues.
    const TODDLER_FEATURES = ["baby_swing", "sandbox", "swings", "slide", "spinner", "water"];
    const filtered = playgrounds.filter(pg => {
      if (hidden[pg.osmId]) return false;
      if ((parseFloat(pg.distance) || 0) > radiusMiles) return false;
      if (selectedFeatures.length > 0 && !selectedFeatures.every(f => (pg.features || []).includes(f))) return false;
      if (showFavoritesOnly && !favorites[pg.osmId]) return false;
      if (showOpenOnly && pg.openNow === false) return false;
      // "Best Now" hides school playgrounds during school hours (they're inaccessible)
      // and other closed venues. Default sort shows them with the ⚠️ badge instead.
      if (sortBy === "conditions") {
        if (pg.openNow === false) return false;
        if (!schoolOverrides[pg.osmId] && pg.isSchool && isSchoolHours()) return false;
      }
      if (indoorOnly && !pg.isIndoor) return false;
      if (outdoorOnly && pg.isIndoor) return false;
      if (hideSchools && pg.isSchool) return false;
      // Age-range filter (playground mode only — activity modes are already specific)
      if (!activityMode && ageRange === "toddler") {
        if (!TODDLER_FEATURES.some(f => (pg.features || []).includes(f))) return false;
      }

      if (windShelterFilter && weather?.wind >= 15) {
        if (!isWindSheltered(pg, weather.windDirection)) return false;
      }
      if (avoidTolls && location && tollBridgeForTrip(location.lat, location.lng, pg.lat, pg.lng)) return false;
      return true;
    });
    if (sortBy === "conditions") {
      return [...filtered].sort((a, b) =>
        getConditionsScore(b, sunInfo, weather) - getConditionsScore(a, sunInfo, weather)
      );
    }
    if (sortBy === "leastsun") {
      return [...filtered].sort((a, b) =>
        getConditionsScore(a, sunInfo, weather) - getConditionsScore(b, sunInfo, weather)
      );
    }
    return filtered; // already sorted by distance from fetch
  }, [playgrounds, hidden, selectedFeatures, showFavoritesOnly, showOpenOnly, favorites, indoorOnly, outdoorOnly, hideSchools, ageRange, activityMode, sortBy, sunInfo, weather, searchRadius, windShelterFilter, avoidTolls, location, schoolOverrides]);
  const hasAnyIndoor = useMemo(() => playgrounds.some(pg => !hidden[pg.osmId] && pg.isIndoor), [playgrounds, hidden]);
  // Only surface the toll filter when some venue is actually across a toll bridge.
  const hasAnyTollVenue = useMemo(() =>
    !!location && playgrounds.some(pg => !hidden[pg.osmId] && tollBridgeForTrip(location.lat, location.lng, pg.lat, pg.lng)),
    [playgrounds, hidden, location]);
  const hasAnyOpenNow = useMemo(() => playgrounds.some(pg => !hidden[pg.osmId] && pg.openNow != null), [playgrounds, hidden]);
  // Switches activity mode and clears filters that don't carry over (feature chips,
  // Open Now, and age range are mode-specific; favorites/sort persist).
  // Radius is swapped to the new mode's last-used value (per-activity persistence
  // via `pg_search_radius:<id>`), or the mode's defaultRadius if never set, or
  // 3200 (2mi) for the no-mode playground default.
  const switchMode = useCallback((next) => {
    setActivityMode(next);
    setSelectedFeatures([]);
    setShowOpenOnly(false);
    setAgeRange(null);
    const nextMode = next ? ACTIVITY_MODES.find(m => m.id === next) : null;
    let nextRadius = nextMode?.defaultRadius ?? 3200;
    try {
      const stored = localStorage.getItem(`pg_search_radius:${next || "_default"}`);
      if (stored) {
        const n = parseInt(stored, 10);
        if (RADIUS_OPTIONS.some(o => o.meters === n)) nextRadius = n;
      }
    } catch {}
    setSearchRadius(nextRadius);
    localStorage.setItem("pg_exp_activity", next || "");
    if (location) applyLocation(location.lat, location.lng, locationLabel, fetchWeather, nextRadius, next);
  }, [location, locationLabel, applyLocation, fetchWeather]); // eslint-disable-line

  const hasActiveFilters = selectedFeatures.length > 0 || showFavoritesOnly || showOpenOnly || indoorOnly || outdoorOnly || hideSchools || ageRange !== null || sortBy !== "distance" || useMetric || Object.keys(hidden).length > 0 || windShelterFilter || avoidTolls;
  const resetAllSettings = () => {
    setSelectedFeatures([]); setShowFavoritesOnly(false); setShowOpenOnly(false);
    setIndoorOnly(false); setOutdoorOnly(false); setHideSchools(false); setAgeRange(null);
    setSortBy("distance"); setKidsMode(false); setUseMetric(false);
    setWindShelterFilter(false); setAvoidTolls(false);
    setHidden({}); try { localStorage.removeItem("pg_hidden"); localStorage.removeItem("pg_kids_mode"); localStorage.removeItem("pg_metric"); } catch {}
  };

  // ── Styles ───────────────────────────────────────────────────────────────────
  const isPsearch = activityMode === "psearch";
  const theme = isPsearch ? PEE : SUNNY;
  // No viewport lock: the page always scrolls so the Side Quests panel + map
  // can both fit on a phone viewport. Prior lock (height 100vh +
  // overflow:hidden when effectiveMapView is on) cut off content below the fold.
  const S = {
    app: {
      fontFamily: SUNNY.fontBody,
      // Per origin/staging: drop maxWidth:390 so the app fills full screen width.
      // Per origin/staging: use height:100dvh + overflow:hidden in map view so
      // the map can fill the viewport without internal scroll. Use 100dvh
      // (dynamic viewport height) so iOS Safari's bottom toolbar and the
      // home-indicator safe area don't push the bottom sheet off-screen.
      // The viewport lock only applies when actually rendering the map
      // (step === "results"); leaking it into step === "detail" killed scroll.
      margin: "0 auto",
      ...(effectiveMapView && step === "results" ? { height: "100dvh", overflow: "hidden" } : { minHeight: "100dvh", overflowX: "hidden" }),
      background: theme.cream,
      color: SUNNY.ink, position: "relative",
    },
    // In map view, screen is a definite-height flex column so the map
    // container's flex:1 actually distributes the remaining space. With
    // minHeight only, the leaflet div was collapsing to its 400px floor
    // on iOS, leaving a large cream gap below the map. Outside map view
    // we keep minHeight so detail/list pages can grow + scroll naturally.
    screen: {
      ...(effectiveMapView && step === "results" ? { height: "100dvh" } : { minHeight: "100dvh" }),
      display: "flex", flexDirection: "column",
    },

    // Splash
    splash: {
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", padding: "40px 24px", textAlign: "center", gap: 24,
    },
    splashEmoji: { fontSize: 80, lineHeight: 1, filter: "drop-shadow(0 8px 24px rgba(251,191,36,0.4))" },
    splashTitle: {
      fontSize: 34, fontWeight: 900, letterSpacing: -1,
      background: "linear-gradient(135deg, #fbbf24, #fb923c, #f43f5e)",
      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
      lineHeight: 1.1, margin: 0,
    },
    splashSub: { fontSize: 16, color: "#94a3b8", lineHeight: 1.6, margin: 0, maxWidth: 280 },
    pillRow: { display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 8 },
    pill: {
      padding: "6px 14px", borderRadius: 99, fontSize: 13,
      background: "rgba(251,191,36,0.12)", color: "#fbbf24",
      border: "1px solid rgba(251,191,36,0.2)",
    },
    btn: {
      padding: "18px 48px", borderRadius: 99, border: "none", cursor: "pointer",
      background: "linear-gradient(135deg, #fbbf24, #f97316)",
      color: "#0f1923", fontSize: 17, fontWeight: 800,
      boxShadow: "0 8px 32px rgba(251,191,36,0.35)",
      letterSpacing: 0.3, marginTop: 8,
    },

    // Locating
    locating: {
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", gap: 20, textAlign: "center", padding: 24,
    },
    pulse: {
      width: 80, height: 80, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(251,191,36,0.3), transparent 70%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 36, animation: "pulse 1.4s ease-in-out infinite",
    },


    // Manual location
    manual: {
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", padding: "40px 22px", gap: 18, textAlign: "center",
      maxWidth: 440, margin: "0 auto",
    },
    manualTitle: {
      fontFamily: SUNNY.fontDisplay, fontSize: 32, fontWeight: 700,
      letterSpacing: -0.5, lineHeight: 1.05, margin: 0, color: SUNNY.ink,
    },
    manualSub: {
      fontSize: 14, color: SUNNY.inkSoft, margin: 0, fontWeight: 700, lineHeight: 1.5,
      maxWidth: 320,
    },
    manualInput: {
      width: "100%", padding: "14px 18px", borderRadius: 16, fontSize: 16,
      background: "#fff", color: SUNNY.ink,
      border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
      boxShadow: SUNNY.shadowButton,
      outline: "none", fontFamily: SUNNY.fontBody, fontWeight: 700,
      boxSizing: "border-box",
    },
    manualBtn: {
      width: "100%", padding: "14px", borderRadius: SUNNY.radiusButton,
      background: SUNNY.ink, color: SUNNY.cream,
      border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
      boxShadow: SUNNY.shadowButtonAccent,
      cursor: "pointer", fontSize: 14, fontWeight: 900, letterSpacing: 0.5,
      textTransform: "uppercase", fontFamily: SUNNY.fontBody,
      boxSizing: "border-box",
    },
    manualError: {
      fontSize: 12, fontWeight: 800,
      color: "#fff", background: SUNNY.tomato,
      padding: "8px 12px", borderRadius: 12,
      border: `2px solid ${SUNNY.ink}`, boxShadow: SUNNY.shadowSticker,
      width: "100%", textAlign: "left", boxSizing: "border-box",
      fontFamily: SUNNY.fontBody,
    },
    orLine: { flex: 1, height: 2, background: SUNNY.ink, opacity: 0.18, borderRadius: 1 },
    retryGeo: {
      background: "#fff", color: SUNNY.ink,
      border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`, borderRadius: SUNNY.radiusButton,
      boxShadow: SUNNY.shadowButton,
      padding: "12px 20px", fontSize: 13, fontWeight: 900, letterSpacing: 0.5,
      textTransform: "uppercase", cursor: "pointer",
      fontFamily: SUNNY.fontBody, width: "100%", boxSizing: "border-box",
    },

    // Results — tighter padding in map view so the map has room to breathe.
    // List view still gets full top padding for visual rhythm.
    header: {
      padding: effectiveMapView ? "max(20px, env(safe-area-inset-top, 20px)) 16px 8px" : "52px 16px 14px",
      background: "transparent",
    },
    headerTitle: { fontSize: 22, fontWeight: 900, margin: 0, letterSpacing: -0.5, color: SUNNY.ink },
    // Sticker-style icon button used in the header right rail.
    iconBtn: {
      width: 38, height: 38,
      background: "#fff", color: SUNNY.ink,
      border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
      borderRadius: 12,
      boxShadow: SUNNY.shadowSticker,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 16, cursor: "pointer", flexShrink: 0,
    },
    iconBtnPrimary: {
      background: SUNNY.ink, color: SUNNY.cream,
    },
    weatherChip: { fontSize: 11, fontWeight: 800, color: SUNNY.inkSoft, display: "flex", alignItems: "center", gap: 4 },

    // Feature filter
    filterRow: {
      display: "flex", gap: 8, padding: "0 20px 16px",
      overflowX: "auto", scrollbarWidth: "none",
      flexShrink: 0,
    },
    filterChip: (active) => ({
      padding: "7px 14px", borderRadius: 99, fontSize: kidsMode ? 14 : 13, fontWeight: 700,
      border: `${kidsMode ? "2px" : "1.5px"} solid ${active ? SUNNY.sun : kidsMode ? "rgba(0,0,0,0.1)" : "rgba(43,30,21,0.18)"}`,
      background: active ? (kidsMode ? "#fef08a" : "rgba(249,179,60,0.15)") : kidsMode ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.55)",
      color: active ? (kidsMode ? "#92400e" : SUNNY.sunDeep) : kidsMode ? "#475569" : SUNNY.inkSoft,
      cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
    }),

    // Card list — extra horizontal padding so the rotated card corners and
    // hard-offset shadows have room to breathe; bigger gap so each card's
    // 4px offset shadow doesn't kiss the card below.
    cardList: { padding: "8px 18px 40px", display: "flex", flexDirection: "column", gap: 18 },
    card: {
      borderRadius: 20, padding: "16px",
      background: "linear-gradient(135deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))",
      border: "1px solid rgba(255,255,255,0.1)",
      cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
    },
    cardTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
    cardName: { fontSize: 16, fontWeight: 800, margin: "0 0 4px", letterSpacing: -0.3 },
    cardMeta: { fontSize: 12, color: "#64748b", display: "flex", gap: 8, flexWrap: "wrap" },
    metaChip: { display: "flex", alignItems: "center", gap: 3 },
    featureRow: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 },
    featureTag: {
      padding: "4px 10px", borderRadius: 99, fontSize: 12,
      background: "rgba(255,255,255,0.07)", color: "#94a3b8",
    },
    distBadge: {
      padding: "4px 10px", borderRadius: 99, fontSize: 13, fontWeight: 700,
      background: "rgba(74,222,128,0.12)", color: "#4ade80", flexShrink: 0,
    },

    // Detail
    detailBack: {
      padding: "52px 20px 16px",
      display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
    },
    detailName: { fontSize: "clamp(19px, 5.5vw, 24px)", fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.15, margin: "0 20px 16px", overflowWrap: "break-word", hyphens: "auto" },
    analysisCard: {
      margin: "0 16px 16px",
      borderRadius: 20, padding: "20px",
      background: kidsMode
        ? "linear-gradient(135deg, #fef9c3, #dcfce7)"
        : "linear-gradient(135deg, rgba(251,191,36,0.1), rgba(249,115,22,0.08))",
      border: kidsMode ? "2px solid #fde047" : "1px solid rgba(251,191,36,0.2)",
    },
    analysisLoading: {
      display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
      padding: "32px 0", color: kidsMode ? "#64748b" : "#94a3b8", fontSize: 14,
    },
    verdictRow: { display: "flex", alignItems: "center", gap: 16, marginBottom: 16 },
    verdictText: { flex: 1 },
    verdictHeadline: { fontSize: kidsMode ? 20 : 18, fontWeight: 900, margin: "0 0 4px", letterSpacing: -0.3, color: kidsMode ? "#1e293b" : undefined },
    verdictSub: { fontSize: 13, color: kidsMode ? "#475569" : "#94a3b8", margin: 0 },
    infoRow: { display: "flex", flexDirection: "column", gap: 10 },
    infoItem: {
      padding: "12px 14px", borderRadius: 14,
      background: kidsMode ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.05)",
      border: kidsMode ? "1px solid rgba(0,0,0,0.07)" : "1px solid rgba(255,255,255,0.08)",
    },
    infoLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: kidsMode ? "#64748b" : "#64748b", marginBottom: 4 },
    infoVal: { fontSize: 14, color: kidsMode ? "#1e293b" : "#e2e8f0" },
    goNowBanner: kidsMode ? {
      margin: "0 16px 16px", padding: "20px",
      borderRadius: 20,
      background: "linear-gradient(135deg, #fef08a, #bbf7d0)",
      border: "3px solid #4ade80",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
      textAlign: "center",
    } : {
      margin: "0 16px 16px", padding: "14px 20px", borderRadius: 14,
      background: "linear-gradient(135deg, rgba(74,222,128,0.15), rgba(34,197,94,0.1))",
      border: "1px solid rgba(74,222,128,0.25)",
      display: "flex", alignItems: "center", gap: 12,
      fontSize: 15, fontWeight: 700, color: "#4ade80",
    },
    detailFeatures: { padding: "0 16px 24px" },
    detailFeatureTitle: { fontSize: 13, color: kidsMode ? "#475569" : "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 },
    detailFeatureGrid: { display: "flex", flexWrap: "wrap", gap: 8 },
    detailFeatureTag: {
      padding: kidsMode ? "10px 16px" : "8px 14px", borderRadius: kidsMode ? 16 : 12, fontSize: kidsMode ? 15 : 14,
      background: kidsMode ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.07)",
      border: kidsMode ? "2px solid rgba(251,191,36,0.4)" : "1px solid rgba(255,255,255,0.1)",
      color: kidsMode ? "#1e293b" : undefined, fontWeight: kidsMode ? 700 : undefined,
    },
    sunCard: {
      margin: "0 16px 16px", borderRadius: 20, padding: "16px",
      background: kidsMode
        ? "linear-gradient(135deg, #fef9c3, #fef3c7)"
        : "linear-gradient(135deg, rgba(251,191,36,0.08), rgba(251,191,36,0.04))",
      border: kidsMode ? "2px solid #fde047" : "1px solid rgba(251,191,36,0.15)",
      display: "flex", gap: 16, alignItems: "center",
    },
    sunInfo: { flex: 1 },
    sunTitle: { fontSize: 13, color: "#f59e0b", fontWeight: 700, marginBottom: 4 },
    sunVal: { fontSize: 22, fontWeight: 800, color: kidsMode ? "#1e293b" : undefined },
    sunSub: { fontSize: 12, color: kidsMode ? "#64748b" : "#94a3b8", marginTop: 2 },
    mapContainer: {
      margin: "0 16px 16px", borderRadius: 20, overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.1)", position: "relative", height: 200,
    },
    mapOverlay: {
      position: "absolute", inset: 0, pointerEvents: "none",
      background: kidsMode
        ? "linear-gradient(to bottom, transparent 60%, rgba(255,255,255,0.25) 100%)"
        : "linear-gradient(to bottom, transparent 60%, rgba(15,25,35,0.6) 100%)",
      borderRadius: 20,
    },
    mapBtnRow: {
      margin: "0 16px 16px", display: "flex", gap: 10,
    },
    mapBtn: (primary) => ({
      flex: 1, padding: "14px 10px", borderRadius: 16, border: "none", cursor: "pointer",
      background: primary ? "linear-gradient(135deg, #3b82f6, #2563eb)" : kidsMode ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.07)",
      border: primary ? "none" : `1px solid ${kidsMode ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.12)"}`,
      color: primary ? "white" : kidsMode ? "#1e293b" : "#94a3b8",
      fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center",
      justifyContent: "center", gap: 6,
      fontFamily: "'Nunito',system-ui,sans-serif",
    }),
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (step === "splash") return (
    // Outer wrapper — full screen width (cream goes edge-to-edge). Matches
    // the rest of the app, which dropped maxWidth:390 a while back. The
    // inner column below caps content width for readability on wide screens.
    <div style={{
      minHeight: "100vh", width: "100%", overflowX: "hidden",
      background: SUNNY.cream, color: SUNNY.ink, position: "relative",
      fontFamily: SUNNY.fontBody,
    }}>
      <style>{`
        @keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.15);opacity:0.7} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes wiggle { 0%,100%{transform:rotate(-1.2deg)} 50%{transform:rotate(0.5deg)} }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        body { margin: 0; }
      `}</style>
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "100vh", padding: "40px 20px", textAlign: "center", gap: 22,
        // Cap the inner column's width so the title doesn't look comical on
        // tablet/desktop, but let it expand far beyond 390 on phone widths
        // that exceed it (Pro Max, foldable inner, etc.).
        maxWidth: 480, margin: "0 auto", width: "100%",
      }}>
        {/* Hero sun sticker — the visual signature, sets the tone for the rest */}
        <div style={{
          background: SUNNY.sun,
          border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
          boxShadow: `5px 5px 0 ${SUNNY.ink}`,
          borderRadius: "50%", width: 132, height: 132,
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "wiggle 4s ease-in-out infinite",
        }}>
          <SunGlyph size={88} />
        </div>

        {/* Title — Fraunces serif, ink color, optional BETA tag */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <h1 style={{
            fontFamily: SUNNY.fontDisplay, fontWeight: 700,
            fontSize: 40, letterSpacing: -1, lineHeight: 1.05, margin: 0,
            color: SUNNY.ink,
          }}>
            Find Your Next<br />Adventure
          </h1>
          <span style={{
            background: SUNNY.ink, color: SUNNY.sun,
            fontFamily: SUNNY.fontBody, fontWeight: 900,
            fontSize: 10, letterSpacing: "0.12em", padding: "3px 9px",
            borderRadius: 6, transform: "rotate(-2deg)",
          }}>BETA</span>
        </div>

        <p style={{
          fontSize: 15, color: SUNNY.inkSoft, lineHeight: 1.6,
          margin: 0, maxWidth: 320, fontWeight: 700,
        }}>
          Sun-aware, weather-smart, right now near you.
        </p>

        {/* Activity patches — sticker pills with rotation, matching the design
            language. Two-column grid so each patch stretches to fill its half
            of the row; on extra-wide widths the inner column cap (480) keeps
            them sensibly sized. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, width: "100%" }}>
          {[
            { color: SUNNY.tomato, text: "🛝 Playgrounds", mode: null,          rotate: -1.5 },
            { color: SUNNY.grass,  text: "🛹 Skateparks",  mode: "skatepark",   rotate: 1    },
            { color: SUNNY.sky,    text: "🚵 Pump Tracks", mode: "pump_track",  rotate: -0.8 },
            { color: SUNNY.sun,    text: "🏖️ Beaches",     mode: "beach",       rotate: 1.5  },
          ].map(({ color, text, mode, rotate }) => (
            <button key={text} onClick={() => {
              splashModeRef.current = mode;
              switchMode(mode);
              start();
            }} style={{ all: "unset", cursor: "pointer", display: "block" }}>
              <Patch color={color} text={text} rotate={rotate} style={{ width: "100%", justifyContent: "center" }} />
            </button>
          ))}
        </div>

        {/* Primary CTA — full-width sticker button, ink-filled with sun-colored
            hard shadow per the design rule. Width:100% gives it real visual
            weight on Pro Max widths instead of floating in a sea of cream. */}
        <button onClick={start} style={{
          background: SUNNY.ink, color: SUNNY.cream,
          border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
          borderRadius: SUNNY.radiusButton,
          boxShadow: SUNNY.shadowButtonAccent,
          padding: "16px 24px", fontFamily: SUNNY.fontBody,
          fontSize: 15, fontWeight: 900, letterSpacing: 0.5,
          textTransform: "uppercase", cursor: "pointer",
          marginTop: 4, width: "100%",
        }}>
          Find Adventures Near Me →
        </button>

        <p style={{ fontSize: 12, color: SUNNY.inkSoft, opacity: 0.7, margin: 0, fontWeight: 700 }}>
          Location used only in this session
        </p>
        {/* Quiet link to the about/compare page. Plain <a> for full-page nav
            so the static page (and its SEO content) actually loads. */}
        <a href="/compare" style={{
          fontSize: 12, color: SUNNY.inkSoft, opacity: 0.55,
          textDecoration: "underline", fontWeight: 700, marginTop: -8,
        }}>
          About this app →
        </a>
      </div>
    </div>
  );

  if (step === "locating") return (
    <div style={S.app}>
      <style>{`@keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.2);opacity:0.6}} *{box-sizing:border-box}`}</style>
      <div style={S.locating}>
        <div style={S.pulse}>📍</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Finding you…</div>
        <div style={{ fontSize: 14, color: "#64748b" }}>Checking weather & sun position</div>
      </div>
    </div>
  );


  if (step === "loading") return (
    <div style={S.app}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes bounce{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-12px) rotate(4deg)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        *{box-sizing:border-box}
      `}</style>
      <div style={S.locating}>
        <div style={{
          width: 96, height: 96, borderRadius: 24,
          background: SUNNY.sun, border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
          boxShadow: SUNNY.shadowSticker,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 52,
          animation: "bounce 1.4s ease-in-out infinite",
        }}>
          {activityMode ? (ACTIVITY_MODES.find(m => m.id === activityMode)?.icon ?? "🧭") : "🧭"}
        </div>
        <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 22, fontWeight: 900, color: SUNNY.ink, letterSpacing: -0.3 }}>
          {isPsearch ? "Scanning desperately…" : `Finding ${activityMode ? (() => { const m = ACTIVITY_MODES.find(x => x.id === activityMode); return m?.findingLabel ?? (m?.label.toLowerCase().endsWith("s") ? m.label.toLowerCase() : m?.label.toLowerCase() + "s"); })() : "playgrounds"}…`}
        </div>
        <div key={loadingPhase} style={{ fontSize: 15, color: SUNNY.inkSoft, animation: "fadeIn 0.4s ease both", minHeight: 24 }}>
          {LOADING_PHASES[loadingPhase].icon} {LOADING_PHASES[loadingPhase].text}
        </div>
        <div style={{ width: 44, height: 44, border: `5px solid ${SUNNY.ink}20`, borderTopColor: SUNNY.sun, borderRadius: "50%", animation: "spin 0.9s linear infinite", marginTop: 4 }} />
        {loadingPhase >= 1 && (
          <div key="note" style={{ fontSize: 12, color: SUNNY.inkSoft, marginTop: 4, textAlign: "center", animation: "fadeIn 0.4s ease both", maxWidth: 240, lineHeight: 1.6 }}>
            Results appear as we find them.
          </div>
        )}
        {/* Progress pills */}
        <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
          {LOADING_PHASES.map((_, i) => (
            <div key={i} style={{
              width: i <= loadingPhase ? 22 : 8, height: 8, borderRadius: 4,
              background: i <= loadingPhase ? SUNNY.ink : `${SUNNY.ink}20`,
              transition: "all 0.4s ease",
            }} />
          ))}
        </div>
      </div>
    </div>
  );

  if (step === "manual") return (
    <div style={S.app}>
      <style>{`
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent} body{margin:0}
        .manual-input:focus{
          outline: none;
          box-shadow: ${SUNNY.shadowButton}, 0 0 0 4px ${SUNNY.sun} !important;
          transition: box-shadow 0.15s;
        }
        .manual-input::placeholder{color:${SUNNY.inkSoft};opacity:0.55}
      `}</style>
      <div style={S.manual}>
        {/* Sticker-style pin hero — sunny aesthetic */}
        <div style={{
          background: SUNNY.tomato, color: "#fff",
          border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
          borderRadius: 18,
          boxShadow: `4px 4px 0 ${SUNNY.ink}`,
          width: 84, height: 84,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 44, transform: "rotate(-3deg)",
        }}>📍</div>
        <h2 style={S.manualTitle}>Where are you?</h2>
        <p style={S.manualSub}>Drop a city, neighborhood, address, or zip — anywhere in the world.</p>

        <input
          ref={inputRef}
          className="manual-input"
          style={S.manualInput}
          placeholder="City, neighborhood, address, or zip"
          value={manualInput}
          onChange={e => { setManualInput(e.target.value); setGeocodeError(""); setManualResults([]); }}
          onFocus={() => { setManualInput(""); setGeocodeError(""); setManualResults([]); }}
          onKeyDown={e => e.key === "Enter" && submitManual()}
        />
        {ipCityHint && manualInput.trim() !== ipCityHint && (
          <button
            onClick={() => { setManualInput(ipCityHint); setGeocodeError(""); setManualResults([]); setTimeout(submitManual, 0); }}
            style={{
              alignSelf: "stretch",
              background: SUNNY.sun, color: SUNNY.ink,
              border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
              borderRadius: SUNNY.radiusButton,
              boxShadow: SUNNY.shadowButton,
              padding: "12px 16px",
              fontSize: 13, fontWeight: 900, cursor: "pointer",
              textAlign: "left", fontFamily: SUNNY.fontBody,
              letterSpacing: 0.3,
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <span style={{ fontSize: 18 }}>📍</span>
            <span>Use <b style={{ fontFamily: SUNNY.fontDisplay, fontWeight: 700 }}>{ipCityHint}</b></span>
          </button>
        )}
        {recentLocations.length > 0 && (
          <div style={{ alignSelf: "stretch", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1, color: SUNNY.inkSoft, textAlign: "left", textTransform: "uppercase" }}>Recent</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {recentLocations.map((r, i) => (
                <button
                  key={`${r.label}-${i}`}
                  onClick={() => applyRecentLocation(r)}
                  style={{
                    background: "#fff", color: SUNNY.ink,
                    border: `2px solid ${SUNNY.ink}`,
                    boxShadow: SUNNY.shadowSticker,
                    borderRadius: SUNNY.radiusPill,
                    padding: "6px 12px",
                    fontSize: 12, fontWeight: 800, fontFamily: SUNNY.fontBody,
                    cursor: "pointer",
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {geocodeError && <div style={S.manualError}>⚠️ {geocodeError}</div>}
        {manualResults.length > 0 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1, color: SUNNY.inkSoft, textAlign: "left", textTransform: "uppercase" }}>Select the right place</div>
            {manualResults.map((r, i) => (
              <button key={i} onClick={() => applyManualResult(r)}
                style={{
                  background: "#fff", color: SUNNY.ink,
                  border: `2px solid ${SUNNY.ink}`, borderRadius: 12,
                  boxShadow: SUNNY.shadowSticker,
                  padding: "10px 14px",
                  fontSize: 13, fontFamily: SUNNY.fontBody, fontWeight: 700,
                  textAlign: "left", cursor: "pointer", width: "100%",
                  lineHeight: 1.3,
                }}>
                {r.display_name.split(",").slice(0, 3).join(",")}
              </button>
            ))}
          </div>
        )}
        <button style={{ ...S.manualBtn, opacity: geocoding ? 0.55 : 1, cursor: geocoding ? "not-allowed" : "pointer" }} onClick={submitManual} disabled={geocoding}>
          {geocoding ? "Searching…" : `Find ${activityMode ? (ACTIVITY_MODES.find(m => m.id === activityMode)?.label ?? "Adventures") : "Playgrounds"} →`}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", color: SUNNY.inkSoft, fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>
          <div style={S.orLine} /><span>or</span><div style={S.orLine} />
        </div>
        <button style={{ ...S.retryGeo, opacity: gpsLoading ? 0.55 : 1, cursor: gpsLoading ? "not-allowed" : "pointer" }} disabled={gpsLoading} onClick={() => {
          setGpsError("");
          setGpsLoading(true);
          navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
              setGpsLoading(false);
              setLocationSource("gps");
              applyLocation(coords.latitude, coords.longitude, "Your location", fetchWeather);
            },
            async (err) => {
              setGpsLoading(false);
              if (err.code === 1) {
                // PERMISSION_DENIED — iOS caches denials; reload flushes the cache
                sessionStorage.setItem("pg_gps_retry", "1");
                window.location.reload();
                return;
              } else if (err.code === 3) {
                // TIMEOUT — no popup appeared or GPS can't get a fix
                setGpsError("No GPS signal. If a location popup appeared, tap Allow. Otherwise try stepping outside or enter your city/zip below.");
              } else {
                // POSITION_UNAVAILABLE
                const ok = await tryIPLocation();
                if (!ok) setGpsError("GPS unavailable. Enter your city or zip code below.");
              }
            },
            { timeout: 8000, enableHighAccuracy: false },
          );
        }}>
          {gpsLoading ? "⏳ Waiting for GPS…" : "📡 Try GPS again"}
        </button>
        {gpsError && (
          <div style={{ fontSize: 12, fontWeight: 700, color: SUNNY.inkSoft, textAlign: "center", lineHeight: 1.5, padding: "0 8px", marginTop: -4 }}>
            {gpsError}
          </div>
        )}
        {manualUseCount >= 2 && (
          <div style={{
            background: SUNNY.sky, color: "#fff",
            border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
            borderRadius: 14,
            boxShadow: SUNNY.shadowButton,
            padding: "12px 14px", width: "100%",
            fontSize: 12, fontWeight: 700, lineHeight: 1.5,
            fontFamily: SUNNY.fontBody, textAlign: "left",
            transform: "rotate(-0.4deg)",
          }}>
            <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 14, fontWeight: 700, marginBottom: 4, letterSpacing: -0.2 }}>
              Tip: enable GPS to skip this step
            </div>
            {/iPad|iPhone|iPod/.test(navigator.userAgent)
              ? "iPhone/iPad: Settings → Privacy & Security → Location Services → Safari → While Using"
              : /Android/.test(navigator.userAgent)
              ? "Android: tap the lock icon in the address bar → Permissions → Location → Allow"
              : "Desktop: tap the lock icon in the address bar → Site settings → Location → Allow"}
          </div>
        )}
      </div>
    </div>
  );

  if (step === "results") return (
    <div style={isDesktop ? { display: "flex", height: "100dvh", overflow: "hidden", background: SUNNY.cream } : {}}>
    <div style={isDesktop ? { ...S.app, width: 460, flexShrink: 0, height: "100dvh", overflowY: "auto", overflowX: "hidden", borderRight: `2px solid ${SUNNY.ink}`, margin: 0 } : S.app}
      onTouchStart={e => {
        if (e.touches.length > 1) { pullRef.current.pulling = false; return; }
        pullRef.current.startY = e.touches[0].clientY;
        pullRef.current.pulling = window.scrollY === 0;
      }}
      onTouchMove={e => {
        if (e.touches.length > 1) { pullRef.current.pulling = false; setPullDistance(0); return; }
        if (!pullRef.current.pulling) return;
        const dist = Math.max(0, Math.min(80, e.touches[0].clientY - pullRef.current.startY));
        setPullDistance(dist);
      }}
      onTouchEnd={() => {
        if (pullDistance >= 70) { tryGeolocation(); }
        pullRef.current.pulling = false;
        setPullDistance(0);
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800;900&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes kidsPop{0%{opacity:0;transform:scale(0.85)}60%{transform:scale(1.04)}100%{opacity:1;transform:scale(1)}}
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent} body{margin:0}
        .pg-card:hover{transform:translateY(-2px);box-shadow:0 12px 40px rgba(0,0,0,0.3)}
        ${kidsMode ? `.pg-card{animation:kidsPop 0.35s cubic-bezier(0.34,1.56,0.64,1) both!important;background:white!important;border:2px solid #fde047!important;border-left:5px solid #f59e0b!important;box-shadow:0 4px 16px rgba(0,0,0,0.08)!important;color:#1e293b!important}
        .pg-card:hover{transform:scale(1.02)!important;box-shadow:0 8px 28px rgba(245,158,11,0.25)!important}` : ""}
      `}</style>
      <div style={S.screen}>
        {!isOnline && (
          <div style={{ background: "#7c3aed", color: "white", textAlign: "center", padding: "6px 12px", fontSize: 13, fontWeight: 600 }}>
            📵 Offline — showing cached results
          </div>
        )}
        {locationSource === "ip" && (
          <div style={{ background: "rgba(251,191,36,0.12)", borderBottom: "1px solid rgba(251,191,36,0.25)", padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#b45309", lineHeight: 1.4 }}>
              📍 Approximate location — distances and walk times may be off
            </span>
            <button onClick={tryGeolocation} style={{ background: "rgba(251,191,36,0.2)", border: "1px solid rgba(251,191,36,0.4)", borderRadius: 8, color: "#fbbf24", fontSize: 11, fontWeight: 700, padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
              Use GPS
            </button>
          </div>
        )}
        {pullDistance > 0 && (
          <div style={{ textAlign: "center", padding: `${pullDistance * 0.4}px 0 4px`, fontSize: 13, color: "#64748b", transition: "none" }}>
            {pullDistance >= 70 ? "↑ Release to refresh" : "↓ Pull to refresh"}
          </div>
        )}
        <div style={S.header}>
          {/* Top row: location pill (left, flex:1) + small action buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => { setManualInput(locationLabel && locationLabel !== "Your location" ? locationLabel : ""); setManualResults([]); setGeocodeError(""); setState("manual"); }}
              style={{
                flex: 1, minWidth: 0, textAlign: "left",
                background: "#fff", color: SUNNY.ink,
                border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`, borderRadius: 16,
                boxShadow: SUNNY.shadowButton,
                padding: "8px 12px",
                display: "flex", alignItems: "center", gap: 10,
                cursor: "pointer", fontFamily: SUNNY.fontBody,
              }}
              title="Change location"
            >
              {/* tomato pin glyph */}
              <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
                <circle cx="9" cy="7" r="3.5" fill="none" stroke={SUNNY.ink} strokeWidth="2"/>
                <path d="M9 10.5 C5 12 3 15 3 17 M15 17 C15 15 13 12 9 10.5" fill={SUNNY.tomato} stroke={SUNNY.ink} strokeWidth="2" strokeLinejoin="round"/>
              </svg>
              <div style={{ flex: 1, minWidth: 0, lineHeight: 1.1 }}>
                <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1.2, opacity: 0.55, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
                  <span onClick={e => { e.stopPropagation(); handleSecretTap(); }}>HERE IN</span>
                  {locationSource && (
                    <span style={{
                      fontSize: 8, padding: "1px 5px", borderRadius: 4, letterSpacing: 0.4,
                      background: locationSource === "gps" ? SUNNY.grass : locationSource === "ip" ? SUNNY.sun : SUNNY.inkSoft,
                      color: "#fff",
                    }}>{locationSource === "gps" ? "GPS" : locationSource === "ip" ? "NET" : "MANUAL"}</span>
                  )}
                </div>
                <div style={{
                  fontFamily: SUNNY.fontDisplay, fontSize: 17, fontWeight: 700,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  color: SUNNY.ink,
                }}>
                  {locationLabel || "Find me"}
                </div>
              </div>
              {/* Debug-panel trigger (5 quick taps). Lives on the badge, not
                  the city text — a missed tap there hits the parent button
                  and opens the location dialog, aborting the count. */}
              <span onClick={e => { e.stopPropagation(); handleDebugTap(); }} style={{
                background: SUNNY.ink, color: SUNNY.sun, fontFamily: SUNNY.fontBody,
                fontWeight: 900, fontSize: 9, letterSpacing: 0.8, padding: "2px 6px",
                borderRadius: 5, transform: "rotate(-3deg)", flexShrink: 0,
                touchAction: "manipulation",
              }}>BETA</span>
            </button>
            {/* Refresh */}
            <button
              onClick={() => {
                Object.keys(localStorage).filter(k => k.startsWith("pg_cache_") || k.startsWith("pt_cache_") || k.startsWith("act_")).forEach(k => localStorage.removeItem(k));
                const fallback = () => applyLocation(location.lat, location.lng, locationLabel, fetchWeather, searchRadius);
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    async ({ coords }) => {
                      setLocationSource("gps");
                      applyLocation(coords.latitude, coords.longitude, "Your location", fetchWeather, searchRadius);
                      try {
                        const r = await fetch(`/api/geocode/reverse?lat=${coords.latitude}&lng=${coords.longitude}`);
                        const d = await r.json();
                        const a = d.address || {};
                        const place = a.neighbourhood || a.suburb || a.quarter || a.village || a.town || a.city;
                        if (place) setLocationLabel(place);
                      } catch {}
                    },
                    fallback,
                    { timeout: 7000 }
                  );
                } else { fallback(); }
              }}
              style={S.iconBtn}
              title="Refresh results"
            >🔄</button>
            {/* Share app */}
            <button
              onClick={async () => {
                const url = window.location.origin + window.location.pathname;
                const text = "Find playgrounds near you 🛝";
                if (navigator.share) {
                  try { await navigator.share({ title: "Playground Finder", text, url }); } catch {}
                } else {
                  try { await navigator.clipboard.writeText(url); alert("Link copied!"); } catch {}
                }
              }}
              style={S.iconBtn}
              title="Share Playground Finder"
            >↗</button>
          </div>

          {/* Weather sticker — visible in both list and map view.
              In map view it renders in compact mode (compact={effectiveMapView}) so it
              stays narrow and doesn't eat into map height.
              At night, swap the marigold sun-glyph background for a sky-blue
              moon: showing "☀️ SUNNY" at 9pm because the API said cloud%=0
              wasn't a useful signal — the user is asking about right now. */}
          {weather && !weather.unavailable && (() => {
            const nowDate = new Date();
            const isNight = sunInfo?.sunset && sunInfo?.sunrise &&
              (nowDate > sunInfo.sunset || nowDate < sunInfo.sunrise);
            const cloudy = weather.cloud != null && weather.cloud > 60;
            const partly = weather.cloud != null && weather.cloud > 30;
            // Glyph: moon at night, weather icon when cloudy/foggy, sun otherwise.
            const glyph = isNight
              ? <span style={{ fontSize: 36, lineHeight: 1 }}>🌙</span>
              : (weather.fog || cloudy
                  ? <span style={{ fontSize: 36, lineHeight: 1 }}>{weather.icon}</span>
                  : <SunGlyph size={48} />);
            // Condition: "Clear night" / "Cloudy night" etc. at night so we
            // never claim "Sunny" after sunset. Daytime keeps the prior copy.
            let conditionText;
            if (isNight) {
              conditionText = weather.fog ? "Foggy night"
                : (weather.cloud != null && weather.cloud > 85) ? "Overcast night"
                : cloudy ? "Cloudy night"
                : "Clear night";
            } else {
              conditionText = weather.fog ? "Foggy"
                : (weather.cloud != null && weather.cloud > 85) ? "Overcast"
                : cloudy ? "Cloudy"
                : partly ? "Partly sunny"
                : "Sunny";
            }
            return (
              <div style={{ marginTop: effectiveMapView ? 8 : 14 }}>
                <WeatherSticker
                  onTap={handleWeatherTap}
                  compact={effectiveMapView}
                  glyph={glyph}
                  temp={
                    <span onClick={e => { e.stopPropagation(); toggleMetric(); }} style={{ cursor: "pointer" }} title="Tap to switch units">
                      {fmtTemp(weather.temp, useMetric)}
                    </span>
                  }
                  conditionText={conditionText}
                  captionRight={
                    `💨 ${fmtWind(weather.wind, useMetric, weather.windDirection)}` +
                    (sunInfo ? ` · ${sunInfo.altitude}° ${getCompass(sunInfo.azimuth)}` : "")
                  }
                  style={{ width: "100%", display: "flex", transform: "rotate(-0.5deg)", ...(isNight ? { background: SUNNY.sky, color: "#fff" } : {}) }}
                />
              </div>
            );
          })()}
          {weather && weather.unavailable && !effectiveMapView && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: SUNNY.tomato, color: "#fff",
              border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`, borderRadius: 14,
              boxShadow: SUNNY.shadowButton, fontSize: 12, fontWeight: 800 }}>
              Weather unavailable
            </div>
          )}

          {/* Sun arc bar — signature element of the redesign. Shows where
              we are in the daylight day plus sunrise/noon/sunset ticks.
              Compact (no tick labels, slimmer bar) in map view. */}
          {sunInfo?.sunrise && sunInfo?.sunset && (
            <div style={{ marginTop: effectiveMapView ? 8 : 16 }}>
              <div style={{
                background: SUNNY.paper,
                border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                borderRadius: 16, boxShadow: SUNNY.shadowButton,
                padding: effectiveMapView ? "8px 12px" : "12px 14px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, fontWeight: 900, letterSpacing: 1.2, marginBottom: effectiveMapView ? 5 : 8, fontFamily: SUNNY.fontBody, color: SUNNY.ink }}>
                  <span style={{ opacity: 0.7 }}>TODAY'S SUN</span>
                  <span style={{ color: SUNNY.tomato }}>SETS {sunInfo.sunset.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "")}</span>
                </div>
                <SunArcBar sunrise={sunInfo.sunrise} sunset={sunInfo.sunset} compact={effectiveMapView} />
              </div>
            </div>
          )}
        </div>

        {/* Activity picker — sticker chips, list-view only. In map view
            the rail on the right of the map handles this. */}
        {!effectiveMapView && (
          <div style={{ display: "flex", gap: 8, padding: "8px 16px 12px", overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", flexShrink: 0 }}>
            {isPsearch && (
              <button onClick={() => switchMode(null)} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: SUNNY.radiusPill, cursor: "pointer",
                flexShrink: 0, whiteSpace: "nowrap",
                background: "#F5E225", color: "#3D2800",
                border: "2px solid #3D2800", boxShadow: "2px 2px 0 #3D2800",
                fontSize: 12, fontWeight: 800, fontFamily: SUNNY.fontBody,
                textTransform: "uppercase", letterSpacing: 0.4,
              }}>
                <span style={{ fontSize: 14 }}>🚻</span>
                <span>💦 PSEARCH</span>
              </button>
            )}
            {chipModes.map(mode => {
              const isActive = activityMode === mode.id;
              return (
                <button key={mode.id ?? "playgrounds"} onClick={() => switchMode(mode.id ?? null)} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: SUNNY.radiusPill, cursor: "pointer",
                  flexShrink: 0, transition: "all 0.15s", whiteSpace: "nowrap",
                  background: isActive ? SUNNY.ink : "#fff",
                  color: isActive ? SUNNY.cream : SUNNY.ink,
                  border: `2px solid ${SUNNY.ink}`,
                  boxShadow: isActive ? "none" : SUNNY.shadowSticker,
                  fontSize: 12, fontWeight: 800, fontFamily: SUNNY.fontBody,
                  textTransform: "uppercase", letterSpacing: 0.4,
                }}>
                  <span style={{ fontSize: 14 }}>{mode.icon}</span>
                  <span>{mode.label}</span>
                </button>
              );
            })}
            {/* Customize — open the activity picker to star/unstar top chips */}
            <button onClick={() => setShowActivityPicker(true)} title="Choose your activities" style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: SUNNY.radiusPill, cursor: "pointer",
              flexShrink: 0, whiteSpace: "nowrap",
              background: "#fff", color: SUNNY.ink,
              border: `2px dashed ${SUNNY.ink}`,
              fontSize: 12, fontWeight: 800, fontFamily: SUNNY.fontBody,
              textTransform: "uppercase", letterSpacing: 0.4,
            }}>
              <span style={{ fontSize: 14 }}>＋</span>
              <span>Customize</span>
            </button>
          </div>
        )}

        {/* Control row — sort chips + filter drawer trigger */}
        {(() => {
          const drawerCount = selectedFeatures.length +
            (hideSchools ? 1 : 0) + (indoorOnly ? 1 : 0) + (outdoorOnly ? 1 : 0) +
            (ageRange ? 1 : 0) + (showOpenOnly ? 1 : 0) + (windShelterFilter ? 1 : 0) +
            (avoidTolls ? 1 : 0) + (sortBy === "leastsun" ? 1 : 0);
          return (
            <div style={{ display: "flex", gap: 8, padding: "0 20px 12px", overflowX: "auto", scrollbarWidth: "none", flexShrink: 0 }}>
              {hasActiveFilters && (
                <button style={{ padding: "7px 14px", borderRadius: 99, fontSize: 13, fontWeight: 700, flexShrink: 0, cursor: "pointer", whiteSpace: "nowrap", background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.3)", color: "#f87171" }} onClick={resetAllSettings}>
                  × Reset
                </button>
              )}
              {!isDesktop && (
                effectiveMapView
                  ? <button style={{ ...S.filterChip(true), flexShrink: 0 }} onClick={() => setMapView(false)}>📋 List</button>
                  : <button style={{ ...S.filterChip(false), flexShrink: 0 }} onClick={() => setMapView(true)}>🗺️ Map</button>
              )}
              <button style={{ ...S.filterChip(showFavoritesOnly), flexShrink: 0 }} onClick={() => setShowFavoritesOnly(p => !p)}>
                ❤️
              </button>
              <button style={{ ...S.filterChip(sortBy === "conditions"), flexShrink: 0 }} onClick={() => setSortBy(s => s === "conditions" ? "distance" : "conditions")}>
                🌤 Best Now
              </button>
              <button style={{ ...S.filterChip(false), flexShrink: 0 }} onClick={() => {
                const next = RADIUS_OPTIONS.find(o => o.meters > searchRadius) || RADIUS_OPTIONS[0];
                setSearchRadius(next.meters);
                if (location) applyLocation(location.lat, location.lng, locationLabel, fetchWeather, next.meters, activityMode);
              }}>{radLabel(searchRadius, useMetric)} ↻</button>
              <button
                onClick={() => setFilterDrawerOpen(true)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "7px 14px", borderRadius: SUNNY.radiusPill,
                  fontFamily: SUNNY.fontBody, fontSize: 13, fontWeight: 800,
                  background: drawerCount > 0 ? SUNNY.ink : "#fff",
                  color: drawerCount > 0 ? SUNNY.cream : SUNNY.ink,
                  border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                  boxShadow: SUNNY.shadowSticker,
                  cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                }}>
                ⚙ Filters{drawerCount > 0 ? ` · ${drawerCount}` : ""}
              </button>
            </div>
          );
        })()}

        {/* Filter drawer — bottom sheet overlay */}
        {filterDrawerOpen && (
          <div
            onClick={() => setFilterDrawerOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(43,30,21,0.35)", zIndex: 1100, display: "flex", alignItems: "flex-end" }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: "100%", maxWidth: 480, margin: "0 auto",
                background: SUNNY.paper,
                border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                borderRadius: "24px 24px 0 0",
                padding: "16px 20px max(20px, env(safe-area-inset-bottom))",
                boxShadow: "0 -4px 24px rgba(43,30,21,0.15)",
                fontFamily: SUNNY.fontBody,
              }}
            >
              <div style={{ width: 48, height: 5, background: SUNNY.ink, borderRadius: 3, margin: "0 auto 20px", opacity: 0.2 }} />

              {/* Filters */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.4, textTransform: "uppercase", color: SUNNY.inkSoft, opacity: 0.6, marginBottom: 10 }}>Filter by</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <button style={S.filterChip(sortBy === "leastsun")} onClick={() => { setSortBy(s => s === "leastsun" ? "distance" : "leastsun"); setFilterDrawerOpen(false); }}>☁️ Least Sun</button>
                  {hasAnyOpenNow && <button style={S.filterChip(showOpenOnly)} onClick={() => setShowOpenOnly(p => !p)}>🟢 Open Now</button>}
                  {weather?.wind >= 15 && <button style={S.filterChip(windShelterFilter)} onClick={() => setWindShelterFilter(v => !v)}>💨 Sheltered</button>}
                  {hasAnyTollVenue && <button style={S.filterChip(avoidTolls)} onClick={() => setAvoidTolls(v => !v)}>🌉 No Tolls</button>}
                  {hasAnyIndoor && <button style={S.filterChip(indoorOnly)} onClick={() => { setIndoorOnly(p => !p); setOutdoorOnly(false); }}>🌧️ Rainy</button>}
                  {hasAnyIndoor && <button style={S.filterChip(outdoorOnly)} onClick={() => { setOutdoorOnly(p => !p); setIndoorOnly(false); }}>☀️ Outdoors</button>}
                  {playgrounds.some(pg => pg.isSchool) && <button style={S.filterChip(hideSchools)} onClick={() => setHideSchools(p => !p)}>🚫 No School</button>}
                  {!activityMode && <button style={S.filterChip(ageRange === "toddler")} onClick={() => setAgeRange(r => r === "toddler" ? null : "toddler")}>🍼 Toddlers</button>}
                </div>
              </div>

              {/* Must have */}
              {FEATURES.filter(f => selectedFeatures.includes(f.id) || playgrounds.some(pg => pg.features.includes(f.id))).length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.4, textTransform: "uppercase", color: SUNNY.inkSoft, opacity: 0.6, marginBottom: 10 }}>Must have</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {FEATURES.filter(f => selectedFeatures.includes(f.id) || playgrounds.some(pg => pg.features.includes(f.id))).map(f => (
                      <button key={f.id} style={S.filterChip(selectedFeatures.includes(f.id))}
                        onClick={() => setSelectedFeatures(prev => prev.includes(f.id) ? prev.filter(x => x !== f.id) : [...prev, f.id])}>
                        {f.icon} {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={() => setFilterDrawerOpen(false)} style={{
                width: "100%", padding: "14px", borderRadius: SUNNY.radiusButton,
                background: SUNNY.ink, color: SUNNY.cream,
                border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                boxShadow: SUNNY.shadowButton,
                fontFamily: SUNNY.fontBody, fontSize: 15, fontWeight: 900,
                cursor: "pointer", letterSpacing: 0.4,
              }}>
                Done
              </button>
            </div>
          </div>
        )}

        {/* Experimental panel — hidden side quests + after hours secret.
            Revealed by the weather-sticker easter egg (3 taps → setShowExperimental).
            In map view the page is height:100dvh / overflow:hidden, so the inline
            flex block can't render — it would be clipped or squash the map. There
            we float it as a centered overlay card with a tap-to-dismiss backdrop so
            the easter egg works in both views. */}
        {showExperimental && (
          <>
            {effectiveMapView && (
              <div
                onClick={() => setShowExperimental(false)}
                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1400 }}
              />
            )}
          <div style={effectiveMapView
            ? { position: "fixed", top: "50%", left: 16, right: 16, transform: "translateY(-50%)", maxHeight: "80vh", overflowY: "auto", zIndex: 1500, background: "#f5f3ff", border: "1px solid rgba(124,58,237,0.3)", borderRadius: 14, padding: "12px 16px", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }
            : { margin: "0 16px 8px", background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.3)", borderRadius: 14, padding: "12px 16px" }}>

            {/* Side Quests — hidden activity modes */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#a78bfa", letterSpacing: 1, marginBottom: 2 }}>🗺️ SIDE QUESTS</div>
              <div style={{ fontSize: 11, color: "#7c3aed", marginBottom: 8, lineHeight: 1.4 }}>Experimental — your mileage may vary.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
                {ACTIVITY_MODES.filter(m => m.hidden && !m.secret).map(mode => {
                  const isActive = activityMode === mode.id;
                  return (
                    <button key={mode.id} onClick={() => switchMode(isActive ? null : mode.id)} style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "6px 12px", borderRadius: 99, cursor: "pointer",
                      flexShrink: 0, transition: "all 0.15s", whiteSpace: "nowrap",
                      background: isActive ? "rgba(167,139,250,0.25)" : "rgba(167,139,250,0.07)",
                      border: `1px solid ${isActive ? "rgba(167,139,250,0.6)" : "rgba(167,139,250,0.2)"}`,
                      color: isActive ? "#c4b5fd" : "#7c3aed",
                      fontSize: 12, fontWeight: 700,
                    }}>
                      <span style={{ fontSize: 14 }}>{mode.icon}</span>
                      <span>{mode.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* After Hours — shown directly in the hidden menu */}
            <div style={{ marginTop: 10, borderTop: "1px solid rgba(251,191,36,0.15)", paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#fbbf24", letterSpacing: 1, marginBottom: 2 }}>🍻 BREWS & PATIOS</div>
              <div style={{ fontSize: 11, color: "#92400e", marginBottom: 8, lineHeight: 1.4 }}>Family-friendly spots where the adults can unwind too.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ACTIVITY_MODES.filter(m => m.secret && m.id !== "psearch").map(mode => {
                  const isActive = activityMode === mode.id;
                  return (
                    <button key={mode.id} onClick={() => switchMode(isActive ? null : mode.id)} style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "6px 12px", borderRadius: 99, cursor: "pointer",
                      flexShrink: 0, transition: "all 0.15s", whiteSpace: "nowrap",
                      background: isActive ? "rgba(251,191,36,0.25)" : "rgba(251,191,36,0.07)",
                      border: `1px solid ${isActive ? "rgba(251,191,36,0.6)" : "rgba(251,191,36,0.2)"}`,
                      color: isActive ? "#fbbf24" : "#b45309",
                      fontSize: 12, fontWeight: 700,
                    }}>
                      <span style={{ fontSize: 14 }}>{mode.icon}</span>
                      <span>{mode.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 🚽 NATURE CALLS — the most secret secret */}
            <div style={{ marginTop: 10, borderTop: "1px solid rgba(245,226,37,0.2)", paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#C9B100", letterSpacing: 1, marginBottom: 2 }}>🚽 NATURE CALLS</div>
              <div style={{ fontSize: 11, color: "#8B7000", marginBottom: 8, lineHeight: 1.4 }}>For when you really, truly need to go.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ACTIVITY_MODES.filter(m => m.id === "psearch").map(mode => {
                  const isActive = activityMode === mode.id;
                  return (
                    <button key={mode.id} onClick={() => { switchMode(isActive ? null : mode.id); setShowExperimental(false); }} style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "6px 12px", borderRadius: 99, cursor: "pointer",
                      flexShrink: 0, whiteSpace: "nowrap",
                      background: isActive ? "rgba(245,226,37,0.3)" : "rgba(245,226,37,0.07)",
                      border: `1px solid ${isActive ? "rgba(245,226,37,0.7)" : "rgba(245,226,37,0.2)"}`,
                      color: isActive ? "#C9B100" : "#8B7000",
                      fontSize: 12, fontWeight: 700,
                    }}>
                      <span style={{ fontSize: 14 }}>{mode.icon}</span>
                      <span>{mode.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={() => {
                setShowExperimental(false);
                if (activityMode) switchMode(null);
              }} style={{ background: "none", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#64748b", fontSize: 12, padding: "3px 10px", cursor: "pointer" }}>
                ✕ Close
              </button>
            </div>
          </div>
          </>
        )}

        {/* Activity picker — "Choose your activities" sheet opened by the
            Customize chip (list view) / ＋ tile (map rail). Star toggles which
            modes appear in the top row; tapping a row body switches to it and
            closes. z-index 1800: above map controls (700) and the "Search this
            area" pill (1000), below the debug panel (2000). Secret modes
            (Brewery Patios, P-Search) stay easter-egg-only and aren't listed. */}
        {showActivityPicker && (
          <>
            <div
              onClick={() => setShowActivityPicker(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1750 }}
            />
            <div style={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1800,
              maxHeight: "82vh", display: "flex", flexDirection: "column",
              background: SUNNY.cream,
              borderTopLeftRadius: 22, borderTopRightRadius: 22,
              borderTop: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
              boxShadow: "0 -12px 40px rgba(0,0,0,0.3)",
              paddingBottom: "max(16px, env(safe-area-inset-bottom))",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px 10px", flexShrink: 0 }}>
                <div>
                  <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 20, fontWeight: 700, color: SUNNY.ink }}>Your activities</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: SUNNY.inkSoft, marginTop: 2 }}>⭐ Star the ones you want in your top row.</div>
                </div>
                <button onClick={() => setShowActivityPicker(false)} style={{ background: "none", border: "none", color: SUNNY.ink, fontSize: 24, cursor: "pointer", lineHeight: 1, padding: 4 }}>✕</button>
              </div>
              <div style={{ overflowY: "auto", padding: "4px 14px 8px" }}>
                {[PLAYGROUNDS_CHIP, ...ACTIVITY_MODES.filter(m => !m.secret)].map(mode => {
                  const pinned = mode.id === null; // Playgrounds: always on, can't unstar
                  const starred = pinned || favActivities.includes(mode.id);
                  const isActive = activityMode === mode.id;
                  return (
                    <div key={mode.id ?? "playgrounds"} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 12px", marginBottom: 6, borderRadius: 14,
                      background: isActive ? SUNNY.ink : "#fff",
                      border: `2px solid ${SUNNY.ink}`,
                      boxShadow: isActive ? "none" : SUNNY.shadowSticker,
                    }}>
                      <button
                        onClick={() => { switchMode(mode.id ?? null); setShowActivityPicker(false); }}
                        style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", color: isActive ? SUNNY.cream : SUNNY.ink }}
                      >
                        <span style={{ fontSize: 24 }}>{mode.icon}</span>
                        <span style={{ fontSize: 15, fontWeight: 800, fontFamily: SUNNY.fontBody }}>{mode.label}</span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavActivity(mode.id); }}
                        disabled={pinned}
                        title={pinned ? "Always pinned" : starred ? "Remove from top row" : "Add to top row"}
                        style={{
                          background: "none", border: "none", padding: 6,
                          cursor: pinned ? "default" : "pointer",
                          fontSize: 22, lineHeight: 1,
                          opacity: starred ? 1 : 0.3,
                          filter: pinned ? "grayscale(0.4)" : "none",
                        }}
                      >{starred ? "⭐" : "☆"}</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Debug / analytics panel. z-index 2000 keeps it above every map
            overlay — the zoom control, "Search this area" pill, and preview
            sheet are all z-index 1000; at 999 they bled through over it. */}
        {showDebug && (
          <div style={{ position: "fixed", inset: 0, background: "#0f172a", zIndex: 2000, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", paddingTop: "max(16px, env(safe-area-inset-top))", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>🔬 Debug & Analytics</div>
                <button onClick={() => setShowDebug(false)} style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 22, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "20px", paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}>

              {/* Admin token (enables photo unflag, future mod tools) */}
              <AdminTokenInput />

              {/* Venue name overrides — fix mislabeled OSM results */}
              <VenueRenamePanel
                nameOverrides={nameOverrides}
                onSaved={() => {
                  fetch("/api/name-overrides")
                    .then(r => r.ok ? r.json() : {})
                    .then(data => {
                      nameOverridesRef.current = data;
                      setNameOverrides(data);
                      setPlaygrounds(prev => prev.map(p => {
                        const override = data[String(p.osmId)];
                        return override ? { ...p, name: override } : p;
                      }));
                    })
                    .catch(() => {});
                }}
              />

              {/* Server cache stats */}
              {cacheStats && (
                <div style={{ marginBottom: 16, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 12, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#34d399", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>💾 Server Cache</div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {[
                      ["Hit rate", cacheStats.hitRate],
                      ["Hits", cacheStats.hits],
                      ["Misses", cacheStats.misses],
                      ["Coalesced", cacheStats.coalesced],
                      ["Entries", cacheStats.entries],
                      ["Est. saved", cacheStats.estimatedSavedUsd],
                    ].map(([label, val]) => (
                      <div key={label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "white" }}>{val}</div>
                        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Usage stats */}
              {usageStats && (
                <div style={{ marginBottom: 16, background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.25)", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#c084fc", letterSpacing: "0.08em", textTransform: "uppercase" }}>📊 Usage Stats</div>
                    <button onClick={() => fetch("/api/admin/stats", { headers: adminHeaders() }).then(r => r.ok ? r.json() : null).then(setUsageStats).catch(() => {})}
                      style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, padding: "2px 8px", color: "#cbd5e1", fontSize: 11, cursor: "pointer" }}>↻</button>
                  </div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
                    {[
                      ["Today", `${usageStats.today.searches} / ${usageStats.today.uniqueUsers}u`],
                      ["Total", usageStats.totals.searches],
                      ["Errors", usageStats.totals.errors],
                      ["Days tracked", usageStats.last30.days.length],
                      ["Sum daily users (30d)", usageStats.last30.sumDailyUniqueUsers],
                    ].map(([label, val]) => (
                      <div key={label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "white" }}>{val}</div>
                        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {Object.keys(usageStats.byMode).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>By mode</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {Object.entries(usageStats.byMode).sort((a, b) => b[1] - a[1]).map(([mode, n]) => (
                          <span key={mode} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#cbd5e1" }}>{mode}: {n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {Object.keys(usageStats.byRadius).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>By radius</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {Object.entries(usageStats.byRadius).sort((a, b) => b[1] - a[1]).map(([r, n]) => (
                          <span key={r} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#cbd5e1" }}>{r}: {n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {Object.keys(usageStats.byRegion || {}).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>By region</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {Object.entries(usageStats.byRegion).sort((a, b) => b[1] - a[1]).map(([r, n]) => (
                          <span key={r} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#cbd5e1" }}>{r}: {n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {Object.keys(usageStats.byHour || {}).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>By hour (UTC)</div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {Array.from({ length: 24 }, (_, h) => ({ h, n: usageStats.byHour[h] || 0 }))
                          .filter(({ n }) => n > 0)
                          .map(({ h, n }) => (
                            <span key={h} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, padding: "3px 7px", fontSize: 11, color: "#cbd5e1" }}>{String(h).padStart(2,"0")}h: {n}</span>
                          ))}
                      </div>
                    </div>
                  )}
                  {Object.keys(usageStats.byErrorType).length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, color: "#fda4af", marginBottom: 4 }}>Errors</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {Object.entries(usageStats.byErrorType).sort((a, b) => b[1] - a[1]).map(([type, n]) => (
                          <span key={type} style={{ background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.3)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#fda4af" }}>{type}: {n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cache management */}
              <CacheCleanButton onCleared={() => fetch("/api/cache-stats").then(r => r.json()).then(setCacheStats).catch(() => {})} />

              {/* Secret location search */}
              <div style={{ marginBottom: 16, background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#60a5fa", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>📍 Search Location</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={locationSearch}
                    onChange={e => { setLocationSearch(e.target.value); setLocationSearchResults([]); }}
                    onKeyDown={e => e.key === "Enter" && runLocationSearch(locationSearch)}
                    placeholder="City, address, or lat,lng"
                    style={{ flex: 1, minWidth: 0, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 14 }}
                  />
                  <button onClick={() => runLocationSearch(locationSearch)} disabled={locationSearching}
                    style={{ background: "#3b82f6", color: "#fff", fontWeight: 800, border: "none", borderRadius: 10, padding: "10px 16px", cursor: "pointer", fontSize: 14 }}>
                    {locationSearching ? "…" : "Go"}
                  </button>
                </div>
                {locationSearchResults.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                    {locationSearchResults.map((r, i) => (
                      <button key={i} onClick={() => {
                        const lat = parseFloat(r.lat), lng = parseFloat(r.lon);
                        const label = r.display_name.split(",").slice(0, 2).join(",").trim();
                        setLocationSearch("");
                        setLocationSearchResults([]);
                        setShowDebug(false);
                        applyLocation(lat, lng, label, fetchWeather, searchRadius);
                      }} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 12px", color: "#e2e8f0", fontSize: 13, textAlign: "left", cursor: "pointer" }}>
                        {r.display_name.split(",").slice(0, 3).join(",")}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <input
                  type="number" value={debugRadius} onChange={e => setDebugRadius(e.target.value)}
                  placeholder="Miles" min="1" max="50"
                  style={{ flex: 1, minWidth: 0, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 15 }}
                />
                <button onClick={runDebugSearch} disabled={debugLoading}
                  style={{ background: "#fbbf24", color: "#0f1923", fontWeight: 800, border: "none", borderRadius: 10, padding: "10px 18px", cursor: "pointer", fontSize: 15 }}>
                  {debugLoading ? "…" : "Search"}
                </button>
              </div>
              {debugResults && !debugResults.error && (
                <div>
                  <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 12, display: "flex", justifyContent: "space-between" }}>
                    <span>{debugResults.total} playgrounds within {debugResults.miles} mi</span>
                    <span>⏱ {debugResults.elapsed}s</span>
                  </div>

                  {/* Feature breakdown */}
                  <div style={{ marginBottom: 16 }}>
                    {FEATURES.map(f => {
                      const count = debugResults.counts[f.id] || 0;
                      const pct = debugResults.total > 0 ? Math.round(count / debugResults.total * 100) : 0;
                      return (
                        <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: 16, width: 22 }}>{f.icon}</span>
                          <span style={{ flex: 1, fontSize: 13, color: count > 0 ? "white" : "#475569" }}>{f.label}</span>
                          <div style={{ width: 80, height: 5, background: "rgba(255,255,255,0.1)", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: count > 0 ? "#4ade80" : "#334155", borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, color: count > 0 ? "#4ade80" : "#475569", width: 52, textAlign: "right" }}>{count} ({pct}%)</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Per-playground list */}
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>All Playgrounds</div>
                    {debugResults.pgList.map((pg, i) => (
                      <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "white", flex: 1 }}>{pg.isSchool ? "🏫 " : ""}{pg.name}</span>
                          <span style={{ fontSize: 11, color: "#64748b", flexShrink: 0 }}>{fmtDist(pg.distance, useMetric)} · {pg.source}</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                          {pg.features.length > 0
                            ? pg.features.map(fid => {
                                const feat = FEATURES.find(x => x.id === fid);
                                return <span key={fid} style={{ fontSize: 11, background: "rgba(74,222,128,0.1)", color: "#4ade80", borderRadius: 6, padding: "1px 6px" }}>{feat?.icon} {feat?.label || fid}</span>;
                              })
                            : <span style={{ fontSize: 11, color: "#475569" }}>no features detected</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {debugResults?.error && (
                <div>
                  <div style={{ color: "#f87171", fontSize: 13, marginBottom: 4 }}>❌ {debugResults.error}</div>
                  {debugResults.elapsed && <div style={{ color: "#475569", fontSize: 11 }}>Failed after {debugResults.elapsed}s</div>}
                </div>
              )}
              {/* Inspector toggle */}
              <div style={{ marginTop: 16, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 12, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#60a5fa", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>🔍 Result inspector</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#cbd5e1", cursor: "pointer" }}>
                  <input type="checkbox" checked={debugInspector} onChange={e => setDebugInspector(e.target.checked)} />
                  Show raw source data on each detail view
                </label>
              </div>
              <BackupNowButton />
              {/* Curated venues */}
              <div style={{ marginTop: 20, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f87171", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>📍 Curated venues</div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 8 }}>{curatedVenues.length} venue{curatedVenues.length !== 1 ? "s" : ""}</div>
                {curatedVenues.map(v => (
                  <div key={v.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "rgba(255,255,255,0.04)", borderRadius: 8, marginBottom: 4, fontSize: 13 }}>
                    <div>
                      <span style={{ color: "#e2e8f0" }}>{v.name}</span>
                      <span style={{ color: "#475569", marginLeft: 8, fontSize: 11 }}>{v.lat?.toFixed(4)}, {v.lng?.toFixed(4)}</span>
                    </div>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <button onClick={() => {
                        setEditingVenueId(v.id);
                        setAddVenueForm({
                          name: v.name || "",
                          lat: v.lat != null ? String(v.lat) : "",
                          lng: v.lng != null ? String(v.lng) : "",
                          features: Array.isArray(v.features) ? [...v.features] : [],
                          address: v.address || "",
                          yearBuilt: v.yearBuilt != null ? String(v.yearBuilt) : "",
                          neighborhood: v.neighborhood || "",
                        });
                        setShowDebug(false);
                        setShowAddVenue(true);
                      }} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}>✎</button>
                      <button onClick={() => {
                        if (!confirm(`Remove "${v.name}"?`)) return;
                        fetch(`/api/curated-venues/${v.id}`, { method: "DELETE" })
                          .then(() => setCuratedVenues(prev => prev.filter(x => x.id !== v.id)));
                      }} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>
                    </div>
                  </div>
                ))}
                <button onClick={() => { setShowDebug(false); setEditingVenueId(null); setAddVenueForm({ name: "", lat: location?.lat?.toFixed(5) || "", lng: location?.lng?.toFixed(5) || "", features: [], address: "", yearBuilt: "", neighborhood: "" }); setShowAddVenue(true); }}
                  style={{ width: "100%", marginTop: 8, padding: "11px", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 10, color: "#f87171", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                  + Add missing playground
                </button>
              </div>

              <button onClick={() => setShowDebug(false)} style={{ width: "100%", marginTop: 20, padding: "14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, color: "#94a3b8", fontSize: 15, cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Add missing playground modal (admin) */}
        {showAddVenue && (
          <div style={{ position: "fixed", inset: 0, background: "#0f172a", zIndex: 1000, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", paddingTop: "max(16px, env(safe-area-inset-top))", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>{editingVenueId ? "Edit Curated Venue" : "Add Missing Playground"}</div>
              <button onClick={() => { setShowAddVenue(false); setEditingVenueId(null); }} style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 22, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px", paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}>
              {[
                ["Name", "name", "Fairway Park", "text"],
                ["Address (optional)", "address", "540 Ridgeway Dr, Pacifica, CA", "text"],
                ["Neighborhood (optional)", "neighborhood", "Sharp Park", "text"],
                ["Year built (optional)", "yearBuilt", "1998", "number"],
              ].map(([label, key, ph, type]) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>{label}</div>
                  <input value={addVenueForm[key]} onChange={e => setAddVenueForm(p => ({ ...p, [key]: e.target.value }))}
                    placeholder={ph} type={type}
                    style={{ width: "100%", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 15, boxSizing: "border-box" }} />
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {[["Latitude", "lat", "37.6204"], ["Longitude", "lng", "-122.4817"]].map(([label, key, ph]) => (
                  <div key={key} style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>{label}</div>
                    <input value={addVenueForm[key]} onChange={e => setAddVenueForm(p => ({ ...p, [key]: e.target.value }))}
                      placeholder={ph} type="number" step="any"
                      style={{ width: "100%", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 15, boxSizing: "border-box" }} />
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>Features</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[["swings","🪁"],["slide","🛝"],["baby_swing","👶"],["sandbox","🏖️"],["climbing","🧗"],["field","🌿"],["basketball","🏀"],["baseball","⚾"],["soccer","⚽"],["restrooms","🚻"],["drinking_water","💧"],["shade","🌳"],["water","💦"],["pump_track","🚴"]].map(([id, icon]) => {
                    const on = addVenueForm.features.includes(id);
                    return (
                      <button key={id} onClick={() => setAddVenueForm(p => ({ ...p, features: on ? p.features.filter(f => f !== id) : [...p.features, id] }))}
                        style={{ background: on ? "rgba(251,191,36,0.2)" : "rgba(255,255,255,0.05)", border: `1px solid ${on ? "rgba(251,191,36,0.5)" : "rgba(255,255,255,0.1)"}`, borderRadius: 99, color: on ? "#fbbf24" : "#94a3b8", fontSize: 12, padding: "5px 10px", cursor: "pointer" }}>
                        {icon} {id.replace(/_/g, " ")}
                      </button>
                    );
                  })}
                </div>
              </div>
              {addVenueForm.lat && addVenueForm.lng && (
                <div style={{ marginBottom: 16, padding: "10px 12px", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
                  Also add to OpenStreetMap so everyone benefits:{" "}
                  <a href={`https://www.openstreetmap.org/edit#map=18/${addVenueForm.lat}/${addVenueForm.lng}`} target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>Open iD editor →</a>
                  <div style={{ marginTop: 4, color: "#64748b" }}>Add a node tagged <code style={{ background: "rgba(255,255,255,0.07)", padding: "0 4px", borderRadius: 3 }}>leisure=playground</code></div>
                </div>
              )}
              <button disabled={addVenueLoading || !addVenueForm.name || !addVenueForm.lat || !addVenueForm.lng}
                onClick={async () => {
                  setAddVenueLoading(true);
                  try {
                    const url = editingVenueId ? `/api/curated-venues/${editingVenueId}` : "/api/curated-venues";
                    const method = editingVenueId ? "PUT" : "POST";
                    const res = await fetch(url, {
                      method,
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ ...addVenueForm, lat: parseFloat(addVenueForm.lat), lng: parseFloat(addVenueForm.lng) }),
                    });
                    if (!res.ok) throw new Error(await res.text());
                    const venue = await res.json();
                    setCuratedVenues(prev => editingVenueId
                      ? prev.map(v => v.id === editingVenueId ? venue : v)
                      : [...prev, venue]);
                    setShowAddVenue(false);
                    setEditingVenueId(null);
                    setAddVenueForm({ name: "", lat: "", lng: "", features: [], address: "", yearBuilt: "", neighborhood: "" });
                    if (location) applyLocation(location.lat, location.lng, locationLabel, fetchWeather, searchRadius);
                  } catch (e) { alert(`Failed: ${e.message}`); }
                  finally { setAddVenueLoading(false); }
                }}
                style={{ width: "100%", padding: "14px", background: addVenueLoading ? "#334155" : "#ef4444", border: "none", borderRadius: 14, color: "white", fontWeight: 800, fontSize: 16, cursor: addVenueLoading ? "not-allowed" : "pointer", opacity: (!addVenueForm.name || !addVenueForm.lat || !addVenueForm.lng) ? 0.5 : 1 }}>
                {addVenueLoading ? "Saving…" : (editingVenueId ? "Save changes" : "Add Playground")}
              </button>
            </div>
          </div>
        )}

        {effectiveMapView && location && (
          <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
            {/* Side-quest rail — vertical sticker-button stack on the right
                edge of the map. Replaces the always-on top-of-screen chip row
                with a discoverable persistent surface. */}
            <div style={{
              position: "absolute", top: 82, right: 12, zIndex: 700,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {chipModes.map(mode => {
                const active = activityMode === mode.id;
                return (
                  <button
                    key={mode.id ?? "playgrounds"}
                    onClick={() => switchMode(mode.id ?? null)}
                    title={mode.label}
                    style={{
                      width: 44, height: 44,
                      background: active ? SUNNY.tomato : "#fff",
                      color: active ? "#fff" : SUNNY.ink,
                      border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                      borderRadius: 14,
                      boxShadow: SUNNY.shadowSticker,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20, cursor: "pointer", padding: 0,
                      transition: "transform 0.1s",
                    }}
                  >{mode.icon}</button>
                );
              })}
              {/* Customize — same picker as the list-view chip row */}
              <button
                onClick={() => setShowActivityPicker(true)}
                title="Choose your activities"
                style={{
                  width: 44, height: 44,
                  background: "#fff", color: SUNNY.ink,
                  border: `${SUNNY.borderWidth}px dashed ${SUNNY.ink}`,
                  borderRadius: 14,
                  boxShadow: SUNNY.shadowSticker,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22, cursor: "pointer", padding: 0,
                }}
              >＋</button>
            </div>

            {/* Left control strip — List button + radius selectors stacked vertically */}
            <div style={{
              position: "absolute", top: 12, left: 12, zIndex: 700,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <button onClick={() => setMapView(false)} style={{
                background: "#fff", color: SUNNY.ink,
                border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                borderRadius: SUNNY.radiusPill,
                boxShadow: SUNNY.shadowSticker,
                padding: "6px 12px", fontFamily: SUNNY.fontBody,
                fontWeight: 800, fontSize: 12,
                textTransform: "uppercase", letterSpacing: 0.4,
                cursor: "pointer", whiteSpace: "nowrap",
              }}>📋 List</button>
              {RADIUS_OPTIONS.map(opt => {
                const active = searchRadius === opt.meters;
                return (
                  <button key={opt.meters} onClick={() => {
                    setSearchRadius(opt.meters);
                    if (location) applyLocation(location.lat, location.lng, locationLabel, fetchWeather, opt.meters, activityMode);
                  }} style={{
                    background: active ? SUNNY.ink : "#fff",
                    color: active ? SUNNY.cream : SUNNY.ink,
                    border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                    borderRadius: SUNNY.radiusPill,
                    boxShadow: active ? "none" : SUNNY.shadowSticker,
                    padding: "6px 12px", fontFamily: SUNNY.fontBody,
                    fontWeight: 800, fontSize: 12,
                    textTransform: "uppercase", letterSpacing: 0.4,
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}>{radLabel(opt.meters, useMetric)}</button>
                );
              })}
            </div>

            {/* "Search this area" pill — sticker styled to match the rest of
                the app (was dark navy + amber, pre-redesign leftover). */}
            {mapCenter && (
              <button
                onClick={searchMapArea}
                style={{
                  position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)",
                  zIndex: 1000,
                  background: SUNNY.ink, color: SUNNY.cream,
                  border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                  borderRadius: SUNNY.radiusPill,
                  boxShadow: SUNNY.shadowButtonAccent,
                  padding: "8px 16px",
                  fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 900,
                  letterSpacing: 0.5, textTransform: "uppercase",
                  display: "flex", alignItems: "center", gap: 6,
                  cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                🔍 Search this area
              </button>
            )}
            <Suspense fallback={<div style={{height:400,display:"flex",alignItems:"center",justifyContent:"center",color:SUNNY.inkSoft,fontFamily:SUNNY.fontBody,fontWeight:800}}>Loading map…</div>}>
              <MapView
                playgrounds={filteredPlaygrounds}
                userLat={location.lat}
                userLng={location.lng}
                radiusMeters={searchRadius}
                onSelect={pg => { openDetail(pg); }}
                onMapMove={(lat, lng, viewRadius) => setMapCenter({ lat, lng, viewRadius })}
                viewportSearchToken={viewportSearchToken}
                activityEmoji={ACTIVITY_MODES.find(m => m.id === activityMode)?.icon ?? "🛝"}
                bottomSheetHeight={300}
              />
            </Suspense>

            {/* Bottom sheet — always renders. Two states: best-pick when
                results exist, no-results CTA when empty. The unconditional
                render gives the map a consistent visual bottom border (the
                sheet's ink top border + soft shadow), keeps OSM attribution
                always visible, and replaces the previous dark-navy in-map
                "no results" overlay with a sticker-styled card matching the
                rest of the redesign. */}
            {(() => {
              const hasResults = filteredPlaygrounds.length > 0;
              const top = hasResults ? filteredPlaygrounds[0] : null;
              const mode = ACTIVITY_MODES.find(m => m.id === activityMode);
              const noun = mode?.findingLabel || (activityMode ? `${mode?.label?.toLowerCase()} spots` : "playgrounds");
              const radiusLabel = RADIUS_OPTIONS.find(o => o.meters === searchRadius)?.label || "this radius";
              const nextOpt = RADIUS_OPTIONS.find(opt => opt.meters > searchRadius);
              const fmtTime = (d) => d?.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "") || "";

              // Best-pick-only computations (skipped when no results)
              const topName = hasResults ? displayName(top.name, top.address) : null;
              const topTravel = hasResults ? formatTravelTime(roadDists[top.osmId] ?? top.distance, mode?.bikeMode, walkTimes[top.osmId], activityMode) : null;
              const featCount = hasResults ? (top.features?.length || 0) : 0;
              let sunPatch = null;
              if (hasResults) {
                const now = new Date();
                if (weather?.fog) sunPatch = { color: SUNNY.sky, text: "Foggy", sub: null };
                else if (weather?.cloud != null && weather.cloud > 85) sunPatch = { color: SUNNY.sky, text: "Overcast", sub: null };
                else if (sunInfo?.sunset && now > sunInfo.sunset) {
                  sunPatch = { color: SUNNY.ink, text: "After sunset", sub: sunInfo.sunrise ? `sunrise ${fmtTime(sunInfo.sunrise)}` : null };
                }
                else if (sunInfo?.sunrise && now < sunInfo.sunrise) {
                  sunPatch = { color: SUNNY.sky, text: "Before sunrise", sub: `sunrise ${fmtTime(sunInfo.sunrise)}` };
                }
                else if (weather?.cloud != null && weather.cloud > 60) sunPatch = { color: SUNNY.sky, text: "Cloudy", sub: null };
                else if (sunInfo?.sunset) sunPatch = { color: SUNNY.sun, text: "Full sun", sub: `til ${fmtTime(sunInfo.sunset)}` };
              }

              return (
                <div style={{
                  position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 1000,
                  background: SUNNY.cream,
                  borderTop: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                  borderRadius: "24px 24px 0 0",
                  // Bottom padding respects the iOS home-indicator safe area
                  // so the action buttons stay above the indicator.
                  padding: "10px 16px max(12px, env(safe-area-inset-bottom))",
                  boxShadow: "0 -8px 24px rgba(43,30,21,0.12)",
                  fontFamily: SUNNY.fontBody, color: SUNNY.ink,
                  cursor: (sheetCollapsed || hasResults) ? "pointer" : "default",
                  transform: sheetCollapsed ? "translateY(calc(100% - 36px))" : "translateY(0)",
                  transition: "transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  willChange: "transform",
                }}
                onTouchStart={e => { sheetDragRef.current = { startY: e.touches[0].clientY, didDrag: false }; }}
                onTouchMove={e => { if (Math.abs(e.touches[0].clientY - sheetDragRef.current.startY) > 8) sheetDragRef.current.didDrag = true; }}
                onTouchEnd={e => {
                  const dy = e.changedTouches[0].clientY - sheetDragRef.current.startY;
                  if (!sheetDragRef.current.didDrag) return;
                  if (dy > 40) setSheetCollapsed(true);
                  else if (dy < -40) setSheetCollapsed(false);
                }}
                onClick={e => {
                  if (sheetDragRef.current.didDrag) { sheetDragRef.current.didDrag = false; return; }
                  if (sheetCollapsed) { setSheetCollapsed(false); return; }
                  if (hasResults) openDetail(top);
                }}>
                  {/* Drag handle */}
                  <div style={{
                    width: 48, height: 5, background: SUNNY.ink, borderRadius: 3,
                    margin: "0 auto 12px",
                    opacity: sheetCollapsed ? 0.5 : 0.3,
                    transition: "opacity 0.25s",
                  }} />

                  {hasResults ? (
                    <>
                      {/* Tag — best for now (only when conditions sort active) */}
                      {sortBy === "conditions" && (
                        <div style={{
                          display: "inline-block",
                          background: SUNNY.ink, color: SUNNY.sun,
                          fontFamily: SUNNY.fontBody, fontWeight: 900, fontSize: 10,
                          letterSpacing: 1.2, padding: "4px 10px", borderRadius: 8,
                          transform: "rotate(-2deg)", marginBottom: 8, textTransform: "uppercase",
                        }}>★ Best pick right now</div>
                      )}
                      {/* Title */}
                      <div style={{
                        fontFamily: SUNNY.fontDisplay, fontSize: 24, fontWeight: 700,
                        letterSpacing: -0.4, lineHeight: 1.1, marginBottom: 8,
                        paddingRight: 4,
                      }}>
                        {topName}
                      </div>
                      {/* Patches */}
                      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                        <Patch color={SUNNY.tomato} text={`${fmtDist(top.distance, useMetric)} · ${topTravel}`} rotate={-1} />
                        {sunPatch && <Patch color={sunPatch.color} text={sunPatch.text} sub={sunPatch.sub} rotate={1} />}
                        {featCount > 0 && <Patch color={SUNNY.grass} text={`${featCount} feature${featCount === 1 ? "" : "s"}`} rotate={-1.5} />}
                      </div>
                      {/* Action row */}
                      <div style={{ display: "flex", gap: 10 }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => openDetail(top)}
                          style={{
                            flex: 1,
                            background: SUNNY.ink, color: SUNNY.cream,
                            border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                            borderRadius: SUNNY.radiusButton,
                            boxShadow: SUNNY.shadowButtonAccent,
                            padding: "12px", fontFamily: SUNNY.fontBody,
                            fontSize: 14, fontWeight: 900, letterSpacing: 0.5,
                            textTransform: "uppercase", cursor: "pointer",
                          }}
                        >Let's go →</button>
                        <button
                          onClick={() => openGoogleMaps(top.name, top.lat, top.lng, location?.lat, location?.lng)}
                          title="Open directions in Google Maps"
                          style={{
                            background: "#fff", color: SUNNY.ink,
                            border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                            borderRadius: SUNNY.radiusButton,
                            boxShadow: SUNNY.shadowButton,
                            padding: "12px 14px", fontFamily: SUNNY.fontBody,
                            fontSize: 18, cursor: "pointer", lineHeight: 1,
                          }}
                        >🗺</button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Tag — no matches */}
                      <div style={{
                        display: "inline-block",
                        background: SUNNY.ink, color: SUNNY.tomato,
                        fontFamily: SUNNY.fontBody, fontWeight: 900, fontSize: 10,
                        letterSpacing: 1.2, padding: "4px 10px", borderRadius: 8,
                        transform: "rotate(-2deg)", marginBottom: 8, textTransform: "uppercase",
                      }}>🔍 No matches yet</div>
                      {/* Title */}
                      <div style={{
                        fontFamily: SUNNY.fontDisplay, fontSize: 22, fontWeight: 700,
                        letterSpacing: -0.4, lineHeight: 1.15, marginBottom: 6,
                        paddingRight: 4,
                      }}>
                        No {noun} within {radiusLabel}
                      </div>
                      {/* Subtitle */}
                      <div style={{ fontSize: 13, color: SUNNY.inkSoft, marginBottom: 12, lineHeight: 1.5, fontWeight: 700 }}>
                        {playgrounds.length > 0
                          ? "Filters may be hiding nearby results."
                          : "Pan the map and tap \"Search this area,\" or try a wider radius."}
                      </div>
                      {/* Action row */}
                      <div style={{ display: "flex", gap: 10 }}>
                        {nextOpt ? (
                          <button
                            onClick={() => {
                              setSearchRadius(nextOpt.meters);
                              if (location) applyLocation(location.lat, location.lng, locationLabel, fetchWeather, nextOpt.meters);
                            }}
                            style={{
                              flex: 1,
                              background: SUNNY.ink, color: SUNNY.cream,
                              border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                              borderRadius: SUNNY.radiusButton,
                              boxShadow: SUNNY.shadowButtonAccent,
                              padding: "12px", fontFamily: SUNNY.fontBody,
                              fontSize: 14, fontWeight: 900, letterSpacing: 0.5,
                              textTransform: "uppercase", cursor: "pointer",
                            }}
                          >Try {radLabel(nextOpt.meters, useMetric)} →</button>
                        ) : (
                          <div style={{ flex: 1 }} />
                        )}
                      </div>
                    </>
                  )}

                  {/* Map attribution — license-required, embedded here so the
                      Leaflet default control (which sits at z-index 1000) doesn't
                      clash with our overlay UI. */}
                  <div onClick={e => e.stopPropagation()} style={{
                    marginTop: 10, fontSize: 9, color: SUNNY.inkSoft, opacity: 0.55,
                    textAlign: "center", lineHeight: 1.3, fontWeight: 700,
                  }}>
                    Map © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>OpenStreetMap</a> contributors · Tiles © <a href="https://www.esri.com/" target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>Esri</a>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {showWhatsNew && !effectiveMapView && (
          <div style={{
            margin: "0 16px 10px", background: SUNNY.paper,
            border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
            borderRadius: SUNNY.radiusCard, boxShadow: SUNNY.shadowCard,
            padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.4 }}>✨</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: SUNNY.fontDisplay, fontSize: 15, fontWeight: 900,
                color: SUNNY.ink, marginBottom: 6,
              }}>What's new</div>
              <ul style={{
                margin: 0, padding: 0, listStyle: "none", fontFamily: SUNNY.fontBody,
                fontSize: 13, lineHeight: 1.5, color: SUNNY.inkSoft, fontWeight: 700,
              }}>
                {WHATS_NEW_ITEMS.map((item, i) => (
                  <li key={i} style={{ marginBottom: i === WHATS_NEW_ITEMS.length - 1 ? 0 : 3 }}>{item}</li>
                ))}
              </ul>
            </div>
            <button
              aria-label="Dismiss what's new"
              onClick={() => {
                setShowWhatsNew(false);
                try { localStorage.setItem("pg_whatsnew_seen", WHATS_NEW_VERSION); } catch {}
              }}
              style={{
                background: "none", border: "none", color: SUNNY.inkSoft, fontSize: 20,
                cursor: "pointer", padding: "0 0 0 4px", lineHeight: 1, flexShrink: 0,
                fontWeight: 900,
              }}>×</button>
          </div>
        )}

        {showHomescreenTip && !effectiveMapView && (
          <div style={{ margin: "0 16px 10px", background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 12, padding: "10px 12px 10px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.4 }}>💡</span>
            <div style={{ flex: 1, fontSize: 13, color: "#94a3b8", lineHeight: 1.5 }}>
              <span style={{ color: "#fbbf24", fontWeight: 700 }}>Pro tip: </span>
              Add this to your home screen for instant access — it looks and feels like a native app. On iPhone tap the Share button → "Add to Home Screen". On Android tap ⋮ → "Add to Home Screen".
            </div>
            <button onClick={() => { setShowHomescreenTip(false); try { localStorage.setItem("pg_tip_homescreen", "1"); } catch {} }}
              style={{ background: "none", border: "none", color: "#64748b", fontSize: 18, cursor: "pointer", padding: "0 0 0 4px", lineHeight: 1, flexShrink: 0 }}>×</button>
          </div>
        )}

        <div style={{ ...S.cardList, display: effectiveMapView ? "none" : undefined }}>
          {filteredPlaygrounds.length === 0 && playgrounds.length > 0 && (() => {
            const defaultRadius = ACTIVITY_MODES.find(m => m.id === activityMode)?.defaultRadius || 8000;
            const resultsExistBeyondRadius = playgrounds.some(pg => !hidden[pg.osmId]);
            const activeNonRadiusFilters = selectedFeatures.length > 0 || showFavoritesOnly || showOpenOnly || indoorOnly || outdoorOnly || hideSchools || !!ageRange;
            const radiusIsBlocker = resultsExistBeyondRadius && !activeNonRadiusFilters;
            const mode = ACTIVITY_MODES.find(m => m.id === activityMode);
            const noun = mode?.findingLabel || (activityMode ? `${mode?.label?.toLowerCase()} spots` : "playgrounds");
            const resetFilters = () => {
              setShowFavoritesOnly(false); setIndoorOnly(false); setOutdoorOnly(false);
              setShowOpenOnly(false); setHideSchools(false); setAgeRange(null); setSelectedFeatures([]);
            };
            const searchFurther = () => {
              const bigger = RADIUS_OPTIONS.find(o => o.meters > searchRadius);
              if (bigger) {
                setSearchRadius(bigger.meters);
                if (location) applyLocation(location.lat, location.lng, locationLabel, fetchWeather, bigger.meters);
              }
            };
            return (
              <div style={{ textAlign: "center", color: "#64748b", padding: "40px 20px", fontSize: 15, lineHeight: 1.7 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                <div style={{ fontWeight: 700, color: "#94a3b8", marginBottom: 6 }}>
                  {radiusIsBlocker
                    ? `No ${noun} within ${RADIUS_OPTIONS.find(o => o.meters === searchRadius)?.label || "this radius"}`
                    : `No ${noun} match your current filters`}
                </div>
                {radiusIsBlocker ? (
                  <button style={{ ...S.btn, padding: "12px 28px", fontSize: 14, marginTop: 8 }} onClick={searchFurther}>
                    Search further
                  </button>
                ) : (
                  <button style={{ ...S.btn, padding: "12px 28px", fontSize: 14, marginTop: 8 }} onClick={resetFilters}>
                    Reset filters
                  </button>
                )}
              </div>
            );
          })()}
          {filteredPlaygrounds.length === 0 && playgrounds.length === 0 && (
            <div style={{ textAlign: "center", color: "#64748b", padding: "40px 20px", fontSize: 15, lineHeight: 1.7 }}>
              {loadError === "network" ? (
                <>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📡</div>
                  <div style={{ fontWeight: 700, color: "#f87171", marginBottom: 6 }}>Couldn't reach playground data</div>
                  <div style={{ fontSize: 13, marginBottom: 16 }}>Check your connection and try again.</div>
                  <button style={{ ...S.btn, padding: "12px 28px", fontSize: 14, marginTop: 0 }}
                    onClick={() => applyLocation(location.lat, location.lng, locationLabel, fetchWeather, searchRadius)}>
                    🔄 Retry
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🗺️</div>
                  <div style={{ fontWeight: 700, color: "#94a3b8", marginBottom: 6 }}>{isPsearch ? "Nothing nearby. You're on your own. 💀" : "No playgrounds found nearby"}</div>
                  <div style={{ fontSize: 13, marginBottom: 16 }}>{isPsearch ? "Try a wider radius. Or a coffee shop." : "OpenStreetMap may not have playgrounds mapped in this area yet."}</div>
                  {(() => {
                    const nextOpt = RADIUS_OPTIONS.find(opt => opt.meters > searchRadius);
                    return nextOpt ? (
                      <button style={{ ...S.btn, padding: "12px 28px", fontSize: 14, marginTop: 0 }} onClick={expandSearch}>
                        Search wider ({radLabel(nextOpt.meters, useMetric)} radius)
                      </button>
                    ) : null;
                  })()}
                </>
              )}
            </div>
          )}
          {/* Sparse-results notice for experimental activity modes */}
          {activityMode && playgrounds.length > 0 && playgrounds.length < 4 && (() => {
            const mode = ACTIVITY_MODES.find(m => m.id === activityMode);
            if (!mode?.hidden && !mode?.secret) return null; // only for experimental modes
            const noun = mode.findingLabel || `${mode.label.toLowerCase()} spots`;
            return (
              <div style={{ margin: "0 16px 12px", padding: "10px 14px", borderRadius: 12, background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)", display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>🔍</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fbbf24", marginBottom: 2 }}>
                    Only {playgrounds.length} {noun} found nearby
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>
                    Coverage depends on OpenStreetMap data in your area. Results may be incomplete.
                  </div>
                </div>
              </div>
            );
          })()}
          {filteredPlaygrounds.slice(0, displayLimit).map((pg, i) => (
            <PlaygroundCard
              key={pg.id}
              pg={pg}
              isFavorite={!!favorites[pg.osmId]}
              isSchool={!!(schoolOverrides[pg.osmId] ?? pg.isSchool)}
              nearestSchool={pg.nearestSchool}
              schoolOverridden={schoolOverrides[pg.osmId]}
              selectedFeatures={selectedFeatures}
              activityMode={activityMode}
              walkTime={walkTimes[pg.osmId]}
              roadDist={roadDists[pg.osmId] ?? null}
              conditionsScore={sortBy === "conditions" ? getConditionsScore(pg, sunInfo, weather) : null}
              windSheltered={weather?.wind >= 15 ? isWindSheltered(pg, weather?.windDirection) : null}
              showWindBadge={windShelterFilter && weather?.wind >= 15}
              userLat={location?.lat}
              userLng={location?.lng}
              useMetric={useMetric}
              kidsMode={kidsMode}
              animIndex={i}
              onOpen={openDetail}
              onToggleFavorite={toggleFavorite}
              onHide={hidePlayground}
              onDistanceTap={handlePsearchTap}
            />
          ))}
          {filteredPlaygrounds.length > displayLimit && (
            <button
              onClick={() => setDisplayLimit(l => l + 20)}
              style={{
                width: "100%", margin: "8px 0", padding: "14px",
                background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.3)",
                borderRadius: 14, color: "#fbbf24", fontWeight: 800, fontSize: 15, cursor: "pointer",
              }}
            >
              Show {Math.min(20, filteredPlaygrounds.length - displayLimit)} more
              <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.7, marginLeft: 8 }}>
                ({filteredPlaygrounds.length - displayLimit} remaining)
              </span>
            </button>
          )}
        </div>
        {!effectiveMapView && (() => {
          const nextOpt = RADIUS_OPTIONS.find(opt => opt.meters > searchRadius);
          const farthestMi = filteredPlaygrounds.length > 0
            ? Math.max(...filteredPlaygrounds.map(pg => parseFloat(pg.distance) || 0))
            : 0;
          const nextMi = nextOpt ? nextOpt.meters / 1609 : Infinity;
          if (!nextOpt || farthestMi >= nextMi) return null;
          return (
            <div style={{ textAlign: "center", padding: "8px 20px 24px" }}>
              <button style={{ ...S.btn, padding: "12px 28px", fontSize: 14, marginTop: 0 }} onClick={expandSearch}>
                🔍 Search further ({radLabel(nextOpt.meters, useMetric)} radius)
              </button>
            </div>
          );
        })()}
        {!effectiveMapView && (
          <div style={{ textAlign: "center", padding: "0 20px 32px", fontSize: 11, color: "#334155" }}>
            Playground data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" style={{ color: "#475569" }}>OpenStreetMap</a> contributors
          </div>
        )}
      </div>
    </div>
    {isDesktop && location && (
      <div style={{ flex: 1, position: "relative", height: "100dvh", overflow: "hidden" }}>
        {mapCenter && (
          <button
            onClick={searchMapArea}
            style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: SUNNY.ink, color: SUNNY.cream, border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`, borderRadius: SUNNY.radiusPill, boxShadow: SUNNY.shadowButtonAccent, padding: "8px 16px", fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap" }}
          >🔍 Search this area</button>
        )}
        <Suspense fallback={<div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: SUNNY.inkSoft, fontFamily: SUNNY.fontBody, fontWeight: 800 }}>Loading map…</div>}>
          <MapView
            playgrounds={filteredPlaygrounds}
            userLat={location.lat}
            userLng={location.lng}
            radiusMeters={searchRadius}
            onSelect={pg => { openDetail(pg); }}
            onMapMove={(lat, lng, viewRadius) => setMapCenter({ lat, lng, viewRadius })}
            viewportSearchToken={viewportSearchToken}
            activityEmoji={ACTIVITY_MODES.find(m => m.id === activityMode)?.icon ?? "🛝"}
            bottomSheetHeight={0}
          />
        </Suspense>
      </div>
    )}
    </div>
  );

  if (step === "outing" && outingAnchor) {
    const anchor = outingAnchor;
    const totalMiles = outingStops.length > 1
      ? outingStops.reduce((sum, s, i) => i === 0 ? 0 : sum + haversine(outingStops[i-1].lat, outingStops[i-1].lng, s.lat, s.lng), 0)
      : 0;
    const totalMin = Math.round(totalMiles * 20 + outingStops.length * 30); // rough: 20min/mi walking + 30min per stop
    const sunPct = sunInfo?.sunrise && sunInfo?.sunset
      ? Math.max(0, Math.min(100, Math.round(((sunInfo.sunset - outingStartTime) / (sunInfo.sunset - sunInfo.sunrise)) * 100)))
      : null;
    const KIND = {
      play:       { label: "PLAY",       emoji: "🛝", color: SUNNY.tomato },
      playground: { label: "PLAYGROUND", emoji: "🛝", color: SUNNY.tomato },
      skatepark:  { label: "SKATEPARK",  emoji: "🛹", color: SUNNY.sunDeep },
      pump_track: { label: "PUMP TRACK", emoji: "🚵", color: SUNNY.grass },
      beach:      { label: "BEACH",      emoji: "🏖️", color: SUNNY.sky },
      cafe:       { label: "CAFE",       emoji: "☕", color: SUNNY.grass },
      food:       { label: "FOOD",       emoji: "🍽",  color: SUNNY.tomato },
      icecream:   { label: "ICE CREAM",  emoji: "🍦", color: SUNNY.sky },
      restroom:   { label: "RESTROOM",   emoji: "🚻", color: SUNNY.inkSoft },
      park:       { label: "PARK",       emoji: "🌳", color: SUNNY.grass },
      picnic:     { label: "PICNIC",     emoji: "🧺", color: SUNNY.grass },
      view:       { label: "VIEW",       emoji: "🌅", color: SUNNY.sky },
      add:        { label: "ADD",        emoji: "+",  color: SUNNY.cream },
    };
    const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "") : "";
    const startStops = (() => {
      // Build ordered timestamps starting from outingStartTime, spacing 45min between stops.
      const start = outingStartTime;
      return outingStops.map((s, i) => {
        const t = new Date(start.getTime() + i * 45 * 60_000);
        return { ...s, time: fmtTime(t) };
      });
    })();
    const startInGoogleMaps = () => {
      // Google Maps multi-stop directions
      if (outingStops.length === 0) return;
      const origin = `${outingStops[0].lat},${outingStops[0].lng}`;
      const destination = `${outingStops[outingStops.length - 1].lat},${outingStops[outingStops.length - 1].lng}`;
      const waypoints = outingStops.slice(1, -1).map(s => `${s.lat},${s.lng}`).join("|");
      const params = new URLSearchParams({
        api: "1",
        origin, destination,
        ...(waypoints && { waypoints }),
        travelmode: "walking",
      });
      const mapsUrl = `https://www.google.com/maps/dir/?${params.toString()}`;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) { window.location.href = mapsUrl; } else { window.open(mapsUrl, "_blank"); }
    };
    return (
      <div style={S.app}>
        <style>{`*{box-sizing:border-box;-webkit-tap-highlight-color:transparent} body{margin:0}`}</style>
        <div style={{ minHeight: "100vh", padding: "20px 18px 120px", fontFamily: SUNNY.fontBody, color: SUNNY.ink }}>
          {/* Back */}
          <div onClick={() => setState("detail")} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", color: SUNNY.inkSoft, fontSize: 13, fontWeight: 800, marginBottom: 16, marginTop: 16 }}>
            <span style={{ fontSize: 18 }}>←</span> Back
          </div>
          {/* Title */}
          <h1 style={{ fontFamily: SUNNY.fontDisplay, fontSize: 30, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.05, margin: "0 0 14px" }}>
            Plan your outing
          </h1>
          {/* Stat patches */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 22 }}>
            <Patch color={SUNNY.tomato} text={totalMin >= 60 ? `${Math.floor(totalMin/60)}h ${totalMin%60}m` : `${totalMin}m`} sub="estimated" rotate={-1} />
            <Patch color={SUNNY.grass} text={`${totalMiles.toFixed(1)} mi`} sub="walking total" rotate={1} />
            {sunPct != null && <Patch color={SUNNY.sun} text={`Sun: ${sunPct}%`} sub="of day left" rotate={-1.5} />}
          </div>

          {/* DEPARTS AT — time picker chip row */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.4, opacity: 0.7, color: SUNNY.ink, marginBottom: 8 }}>
              DEPARTS AT
            </div>
            <div style={{ display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingBottom: 2 }}>
              {(() => {
                const isNow = Math.abs(outingStartTime.getTime() - Date.now()) < 120000;
                const now = new Date();
                const tenpm = new Date(now); tenpm.setHours(22, 0, 0, 0);
                const ceiling = sunInfo?.sunset && sunInfo.sunset < tenpm ? sunInfo.sunset : tenpm;
                const slots = [];
                let cursor = new Date(now);
                cursor.setMinutes(cursor.getMinutes() < 30 ? 30 : 60, 0, 0);
                while (cursor <= ceiling) { slots.push(new Date(cursor)); cursor = new Date(cursor.getTime() + 30 * 60 * 1000); }
                return [
                  <button key="now" onClick={() => setOutingStartTime(new Date())} style={{
                    flexShrink: 0, padding: "6px 14px", borderRadius: SUNNY.radiusPill,
                    border: `2px solid ${SUNNY.ink}`, cursor: "pointer",
                    boxShadow: isNow ? "none" : SUNNY.shadowSticker,
                    background: isNow ? SUNNY.ink : "#fff",
                    color: isNow ? SUNNY.cream : SUNNY.ink,
                    fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap",
                  }}>Now</button>,
                  ...slots.map(slot => {
                    const isSel = Math.abs(outingStartTime.getTime() - slot.getTime()) < 60000;
                    const label = slot.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "");
                    return (
                      <button key={slot.getTime()} onClick={() => setOutingStartTime(slot)} style={{
                        flexShrink: 0, padding: "6px 14px", borderRadius: SUNNY.radiusPill,
                        border: `2px solid ${SUNNY.ink}`, cursor: "pointer",
                        boxShadow: isSel ? "none" : SUNNY.shadowSticker,
                        background: isSel ? SUNNY.sun : "#fff",
                        color: SUNNY.ink,
                        fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                        textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap",
                      }}>{label}</button>
                    );
                  }),
                ];
              })()}
            </div>
          </div>

          {/* Timeline */}
          <div style={{ position: "relative", paddingLeft: 56 }}>
            {/* Vertical rail */}
            <div style={{ position: "absolute", left: 48, top: 8, bottom: 8, width: 2, background: SUNNY.ink, opacity: 0.3 }} />

            {startStops.map((stop, i) => {
              const k = KIND[stop.kind] || KIND.play;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 16, position: "relative" }}>
                  {/* Time label on the left (timeline rail) */}
                  <div style={{ position: "absolute", left: -56, top: "50%", transform: "translateY(-50%)", textAlign: "right", width: 44, fontFamily: SUNNY.fontDisplay, fontSize: 13, fontWeight: 700, color: SUNNY.ink }}>
                    {stop.time}
                  </div>
                  {/* Sticker card on the right */}
                  <div style={{
                    flex: 1, background: "#fff",
                    border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                    borderRadius: 16,
                    boxShadow: SUNNY.shadowButton,
                    padding: "10px 12px",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: k.color, color: k.color === SUNNY.cream ? SUNNY.ink : "#fff",
                      border: `2px solid ${SUNNY.ink}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 18, flexShrink: 0,
                    }}>{k.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.8, opacity: 0.55, color: SUNNY.ink }}>{k.label}</div>
                      <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 16, fontWeight: 700, lineHeight: 1.15, color: SUNNY.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {stop.name}
                      </div>
                      {stop.kind === "restroom" && (() => {
                        const rKey = stop.hasPlayground ? `${stop.osmId}-restroom` : String(stop.osmId);
                        const rr = rKey ? restroomRatings[rKey] : null;
                        const locked = stop.accessKey || (rr?.tags?.locked > 0);
                        if (!locked && !rr?.count) return null;
                        return (
                          <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
                            {locked && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 5, background: SUNNY.tomato, color: "#fff" }}>🔒 Key required</span>}
                            {rr?.count > 0 && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 5, background: SUNNY.cream, border: `1px solid ${SUNNY.ink}` }}>{tierFromAvg(rr.average).emoji} {tierFromAvg(rr.average).label}</span>}
                          </div>
                        );
                      })()}
                    </div>
                    {i > 0 && (
                      <button
                        onClick={() => setOutingStops(prev => prev.filter((_, idx) => idx !== i))}
                        title="Remove this stop"
                        style={{ background: "none", border: "none", color: SUNNY.inkSoft, fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1, opacity: 0.5 }}
                      >×</button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Add-stop dashed placeholder — category chips on top, then a
                distance-sorted list of suggestions for the active category.
                Tapping a chip re-fetches suggestions near the LAST stop so
                the trip flows geographically. */}
            <div style={{
              border: `2.5px dashed ${SUNNY.ink}`,
              borderRadius: 16, padding: "12px",
              background: "rgba(255,255,255,0.5)",
              fontFamily: SUNNY.fontBody, color: SUNNY.ink,
            }}>
              <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 8, opacity: 0.7 }}>Add a stop near {(outingStops[outingStops.length - 1]?.name || "the playground").slice(0, 24)}{(outingStops[outingStops.length - 1]?.name || "").length > 24 ? "…" : ""}</div>

              {/* Category chips */}
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
                {OUTING_CATEGORIES.map(c => {
                  const active = outingCategory === c.kind;
                  return (
                    <button
                      key={c.kind}
                      onClick={() => switchOutingCategory(c.kind)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "5px 10px", borderRadius: SUNNY.radiusPill,
                        background: active ? SUNNY.ink : "#fff",
                        color: active ? SUNNY.cream : SUNNY.ink,
                        border: `2px solid ${SUNNY.ink}`,
                        boxShadow: active ? "none" : SUNNY.shadowSticker,
                        fontSize: 11, fontWeight: 800, cursor: "pointer",
                        textTransform: "uppercase", letterSpacing: 0.4,
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{c.icon}</span>
                      <span>{c.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Suggestions list */}
              {outingSuggestLoading && (
                <div style={{ fontSize: 12, color: SUNNY.inkSoft, padding: "8px 0" }}>Searching…</div>
              )}
              {!outingSuggestLoading && (outingSuggestions.items || []).filter(s => !outingStops.some(existing => existing.name === s.name)).length === 0 && (
                <div style={{ fontSize: 12, color: SUNNY.inkSoft, padding: "8px 0", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>No {OUTING_CATEGORIES.find(c => c.kind === outingCategory)?.label.toLowerCase()} within {Math.round(outingSearchRadius / 1609.34 * 10) / 10} mi.</span>
                  {outingSearchRadius < 16000 && (
                    <button
                      onClick={async () => {
                        const next = outingSearchRadius >= 8000 ? 16000 : 8000;
                        const last = outingStops[outingStops.length - 1] || outingAnchor;
                        setOutingSearchRadius(next);
                        setOutingSuggestLoading(true);
                        const items = await fetchOutingSuggestions(outingCategory, last.lat, last.lng, next);
                        setOutingSuggestions({ items });
                        setOutingSuggestLoading(false);
                      }}
                      style={{
                        background: SUNNY.ink, color: SUNNY.cream,
                        border: "none", borderRadius: 10,
                        padding: "4px 10px", fontWeight: 900, fontSize: 11,
                        letterSpacing: 0.5, textTransform: "uppercase", cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Search wider ({Math.round((outingSearchRadius >= 8000 ? 16000 : 8000) / 1609.34)} mi)
                    </button>
                  )}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(outingSuggestions.items || [])
                  .filter(s => !outingStops.some(existing => existing.name === s.name))
                  .map((s, i) => {
                    const k = KIND[s.kind] || KIND.add;
                    const addStop = () => setOutingStops(prev => [...prev, { kind: s.kind, name: s.name, lat: s.lat, lng: s.lng, osmId: s.osmId, accessKey: s.accessKey, hasPlayground: s.hasPlayground }]);
                    if (s.kind !== "restroom") {
                      return (
                        <button
                          key={i}
                          onClick={addStop}
                          style={{
                            display: "flex", alignItems: "center", gap: 10, width: "100%",
                            background: "#fff", color: SUNNY.ink,
                            border: `2px solid ${SUNNY.ink}`, borderRadius: 12,
                            padding: "8px 10px", cursor: "pointer", textAlign: "left",
                            fontFamily: SUNNY.fontBody,
                          }}
                        >
                          <span style={{
                            width: 28, height: 28, borderRadius: 8,
                            background: k.color, color: "#fff", fontSize: 14,
                            border: `2px solid ${SUNNY.ink}`,
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          }}>{k.emoji}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                            {s.dMi != null && (
                              <div style={{ fontSize: 10, fontWeight: 700, color: SUNNY.tomato, marginTop: 1 }}>
                                {s.dMi < 0.1 ? "<0.1 mi" : `${s.dMi.toFixed(1)} mi away`}
                              </div>
                            )}
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 900, color: SUNNY.inkSoft, letterSpacing: 0.5 }}>+ ADD</span>
                        </button>
                      );
                    }
                    // ── Restroom card — interactive rating inline, can't nest buttons ──
                    const rKey = s.hasPlayground ? `${s.osmId}-restroom` : String(s.osmId);
                    const rating = restroomRatings[rKey];
                    const savedR = (() => { try { return JSON.parse(localStorage.getItem("restroom_ratings") || "{}")[rKey] || null; } catch { return null; } })();
                    const isLocked = s.accessKey || (rating?.tags?.locked > 0);
                    const submitR = (val, locked) => {
                      if (!s.osmId) return;
                      const tags = locked ? ["locked"] : [];
                      fetch("/api/condition", { method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ osmId: rKey, rating: val, tags }) })
                        .then(r => r.json()).then(d => { if (d?.count) setRestroomRatings(prev => ({ ...prev, [rKey]: d })); });
                      try {
                        const ls = JSON.parse(localStorage.getItem("restroom_ratings") || "{}");
                        ls[rKey] = { rating: val, locked };
                        localStorage.setItem("restroom_ratings", JSON.stringify(ls));
                      } catch {}
                    };
                    return (
                      <div key={i} style={{
                        background: "#fff", color: SUNNY.ink,
                        border: `2px solid ${SUNNY.ink}`, borderRadius: 12,
                        padding: "8px 10px", fontFamily: SUNNY.fontBody,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{
                            width: 28, height: 28, borderRadius: 8,
                            background: k.color, color: "#fff", fontSize: 14,
                            border: `2px solid ${SUNNY.ink}`,
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          }}>{k.emoji}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {s.hasPlayground ? `🛝 ${s.name}` : s.name}
                            </div>
                            <div style={{ display: "flex", gap: 5, alignItems: "center", marginTop: 2, flexWrap: "wrap" }}>
                              {s.dMi != null && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: SUNNY.tomato }}>
                                  {s.dMi < 0.1 ? "<0.1 mi" : `${s.dMi.toFixed(1)} mi away`}
                                </span>
                              )}
                              {isLocked && <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 6, background: SUNNY.tomato, color: "#fff" }}>🔒 Key required</span>}
                              {rating?.count > 0 && <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 6, background: SUNNY.cream, border: `1px solid ${SUNNY.ink}` }}>{tierFromAvg(rating.average).emoji} {tierFromAvg(rating.average).label}</span>}
                            </div>
                          </div>
                          <button onClick={addStop} style={{ fontSize: 10, fontWeight: 900, color: SUNNY.inkSoft, letterSpacing: 0.5, background: "none", border: "none", cursor: "pointer", padding: "4px 6px", fontFamily: SUNNY.fontBody }}>+ ADD</button>
                        </div>
                        {s.osmId && (
                          <div style={{ display: "flex", gap: 4, marginTop: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 9, fontWeight: 900, color: SUNNY.inkSoft, letterSpacing: 0.5, marginRight: 2, textTransform: "uppercase" }}>Rate:</span>
                            {RESTROOM_TIERS.map(t => (
                              <button key={t.value} title={t.label}
                                onClick={() => submitR(t.value, savedR?.locked || false)}
                                style={{ fontSize: 15, padding: "2px 5px", borderRadius: 7, cursor: "pointer",
                                  border: `1.5px solid ${SUNNY.ink}`,
                                  background: savedR?.rating === t.value ? SUNNY.sun : SUNNY.cream,
                                  boxShadow: savedR?.rating === t.value ? SUNNY.shadowSticker : "none" }}>
                                {t.emoji}
                              </button>
                            ))}
                            <button title="Locked / key required"
                              onClick={() => submitR(savedR?.rating || 3, !(savedR?.locked || false))}
                              style={{ fontSize: 13, padding: "2px 7px", borderRadius: 7, cursor: "pointer", marginLeft: 2,
                                border: `1.5px solid ${SUNNY.ink}`,
                                background: (savedR?.locked) ? SUNNY.tomato : SUNNY.cream,
                                color: (savedR?.locked) ? "#fff" : SUNNY.ink,
                                boxShadow: (savedR?.locked) ? SUNNY.shadowSticker : "none" }}>
                              🔒
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <button
            onClick={startInGoogleMaps}
            disabled={outingStops.length < 1}
            style={{
              position: "fixed", left: "50%", bottom: 22, transform: "translateX(-50%)",
              width: "min(360px, calc(100% - 36px))",
              background: outingStops.length >= 1 ? SUNNY.ink : SUNNY.inkSoft,
              color: SUNNY.cream,
              border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
              borderRadius: SUNNY.radiusButton,
              boxShadow: SUNNY.shadowButtonAccent,
              padding: "16px", fontFamily: SUNNY.fontBody,
              fontSize: 14, fontWeight: 900, letterSpacing: 0.5,
              textTransform: "uppercase", cursor: outingStops.length >= 1 ? "pointer" : "not-allowed",
              zIndex: 20,
            }}
          >Start the outing →</button>
        </div>
      </div>
    );
  }

  if (step === "detail" && selected) return (
    <div style={isDesktop ? { display: "flex", height: "100dvh", overflow: "hidden", background: SUNNY.cream } : {}}>
    <div style={isDesktop ? { ...S.app, width: 460, flexShrink: 0, height: "100dvh", overflowY: "auto", overflowX: "hidden", borderRight: `2px solid ${SUNNY.ink}`, margin: 0 } : S.app}
      onTouchStart={e => {
        // Only track single-finger swipes starting from the left 40px edge
        // (standard iOS back gesture zone). Ignoring swipes that start
        // elsewhere prevents accidental back-navigation while panning the
        // embedded map or scrolling horizontal content.
        if (e.touches.length > 1) { detailSwipe.current = null; return; }
        const t = e.touches[0];
        if (t.clientX > 40) { detailSwipe.current = null; return; }
        detailSwipe.current = { x: t.clientX, y: t.clientY };
      }}
      onTouchMove={e => {
        if (e.touches.length > 1) detailSwipe.current = null;
      }}
      onTouchEnd={e => {
        const s = detailSwipe.current;
        if (!s) return;
        const dx = e.changedTouches[0].clientX - s.x;
        const dy = e.changedTouches[0].clientY - s.y;
        if (dx > 80 && Math.abs(dx) > Math.abs(dy) * 1.5) setState("results");
        detailSwipe.current = null;
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800;900&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}} @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent} body{margin:0}
      `}</style>
      <div style={S.screen}>
        {/* ── Sunny redesign Phase 9a ── HERO + VERDICT + SUN ARC ─────────── */}
        {(() => {
          const heroPhotoRef = selected.photoRefs?.[0] || selected.photoRef || null;
          const heroPhotoUrl = heroPhotoRef ? `/api/photo?ref=${encodeURIComponent(heroPhotoRef)}&w=720` : null;
          const photoCount = (selected.photoRefs?.length) || (selected.photoRef ? 1 : 0);
          const titleText = displayName(GENERIC_NAME_RE.test(selected.name) && detailGeoInfo?.suggestedName ? detailGeoInfo.suggestedName : selected.name, selected.address || detailGeoInfo?.address);
          // Eyebrow tag — pull a couple of features for the kicker line
          const featLabels = (selected.features || []).slice(0, 3)
            .map(f => FEATURES.find(x => x.id === f)?.label || f).join(" · ").toUpperCase();
          // Activity/source kicker fallback if no features
          const modeLabel = ACTIVITY_MODES.find(m => m.id === activityMode)?.label?.toUpperCase()
            || (selected.isIndoor ? "INDOOR" : "PUBLIC PLAYGROUND");
          const kicker = featLabels || modeLabel;
          // Hero gradient picks colors based on activity for visual variety
          const heroGradient = selected.isIndoor
            ? `linear-gradient(135deg, ${SUNNY.ink} 0%, ${SUNNY.inkSoft} 100%)`
            : activityMode === "skatepark"
              ? `linear-gradient(135deg, ${SUNNY.tomato} 0%, ${SUNNY.sunDeep} 100%)`
              : activityMode === "beach"
                ? `linear-gradient(135deg, ${SUNNY.sky} 0%, ${SUNNY.sun} 100%)`
                : `linear-gradient(135deg, ${SUNNY.sky} 0%, ${SUNNY.grass} 100%)`;
          const heroEmoji = ACTIVITY_MODES.find(m => m.id === activityMode)?.icon || "🛝";

          return (
            <>
              {/* HERO */}
              <div style={{
                height: 240, position: "relative",
                background: heroPhotoUrl ? `url(${heroPhotoUrl}) center/cover no-repeat` : heroGradient,
                borderBottom: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                marginTop: -1, // butt up against safe-area
              }}>
                {/* Emoji watermark when no photo */}
                {!heroPhotoUrl && (
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 96, opacity: 0.5,
                  }}>{heroEmoji}</div>
                )}
                {/* Bottom-fade gradient to keep title legible over photos */}
                {heroPhotoUrl && (
                  <div style={{
                    position: "absolute", left: 0, right: 0, bottom: 0, height: 130,
                    background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)",
                    pointerEvents: "none",
                  }} />
                )}
                {/* Back sticker — needs to clear iOS Dynamic Island / status
                    bar. The 50px floor handles non-PWA Safari (where env may
                    resolve to 0); with env support we sit 12px below the
                    inset so the button doesn't kiss the status text. */}
                <button onClick={() => setState("results")} aria-label="Back" style={{
                  position: "absolute", top: "max(50px, calc(env(safe-area-inset-top, 0px) + 12px))", left: 16,
                  width: 44, height: 44, borderRadius: 14,
                  background: "#fff", color: SUNNY.ink,
                  border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                  boxShadow: SUNNY.shadowButton,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22, fontWeight: 900, cursor: "pointer",
                }}>←</button>
                {/* Heart sticker */}
                <button onClick={e => toggleFavorite(selected, e)} aria-label="Favorite" style={{
                  position: "absolute", top: "max(50px, calc(env(safe-area-inset-top, 0px) + 12px))", right: 16,
                  width: 44, height: 44, borderRadius: 14,
                  background: favorites[selected.osmId] ? SUNNY.tomato : "#fff",
                  color: favorites[selected.osmId] ? "#fff" : SUNNY.ink,
                  border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                  boxShadow: SUNNY.shadowButton,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, cursor: "pointer",
                }}>{favorites[selected.osmId] ? "♥" : "♡"}</button>
                {/* Photo count chip */}
                {photoCount > 0 && (
                  <div style={{
                    position: "absolute", bottom: 14, right: 14,
                    background: "#fff", color: SUNNY.ink,
                    border: `2px solid ${SUNNY.ink}`, borderRadius: 999,
                    padding: "4px 10px", fontWeight: 800, fontSize: 11,
                    boxShadow: SUNNY.shadowSticker, fontFamily: SUNNY.fontBody,
                  }}>📷 {photoCount}</div>
                )}
                {/* Title overlay */}
                <div style={{
                  position: "absolute", bottom: 16, left: 16, right: 90, fontFamily: SUNNY.fontBody,
                }}>
                  <div style={{
                    display: "inline-block",
                    background: SUNNY.ink, color: SUNNY.sun,
                    fontSize: 9, fontWeight: 900, letterSpacing: 1.4,
                    padding: "4px 8px", borderRadius: 6,
                    transform: "rotate(-2deg)", marginBottom: 6,
                    textTransform: "uppercase",
                  }}>{kicker}</div>
                  <div style={{
                    fontFamily: SUNNY.fontDisplay, fontSize: 28, fontWeight: 700,
                    color: "#fff", letterSpacing: -0.5, lineHeight: 1.05,
                    textShadow: `2px 2px 0 ${SUNNY.ink}`,
                  }}>{titleText}</div>
                </div>
              </div>

              {/* VERDICT + SUN ARC + small action row */}
              <div style={{ padding: "16px 16px 0", fontFamily: SUNNY.fontBody, color: SUNNY.ink }}>
                {/* Small action row — Outing + Share. Positioned right under
                    the hero so the most-likely actions are reachable without
                    scrolling. */}
                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  <button onClick={() => openOuting(selected)} style={{
                    flex: 1,
                    background: SUNNY.ink, color: SUNNY.cream,
                    border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                    borderRadius: SUNNY.radiusButton,
                    boxShadow: SUNNY.shadowButtonAccent,
                    fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 900,
                    letterSpacing: 0.5, textTransform: "uppercase",
                    padding: "10px 14px", cursor: "pointer",
                  }}>🎒 Plan an outing</button>
                  <button onClick={async () => {
                    const params = new URLSearchParams({
                      spot: selected.osmId,
                      name: displayName(selected.name, selected.address),
                      mode: activityMode || "",
                      lat: selected.lat,
                      lng: selected.lng,
                    });
                    const url = `${window.location.origin}${window.location.pathname}?${params}`;
                    const text = `Check out ${displayName(selected.name, selected.address)} on Playground Finder!`;
                    if (navigator.share) {
                      try { await navigator.share({ title: displayName(selected.name, selected.address), text, url }); } catch {}
                    } else {
                      try { await navigator.clipboard.writeText(`${text} ${url}`); alert("Link copied!"); } catch {}
                    }
                  }} aria-label="Share" style={{
                    background: "#fff", color: SUNNY.ink,
                    border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                    borderRadius: SUNNY.radiusButton,
                    boxShadow: SUNNY.shadowButton,
                    padding: "10px 14px", fontSize: 18, cursor: "pointer", lineHeight: 1,
                  }}>🔗</button>
                </div>

                {/* THE VERDICT — sun sticker, auto-running Claude analysis */}
                <div style={{
                  background: SUNNY.sun, color: SUNNY.ink,
                  border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                  borderRadius: 18, boxShadow: SUNNY.shadowButton,
                  padding: "14px 16px", marginBottom: 14,
                  transform: "rotate(-0.6deg)",
                }}>
                  <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1.4, opacity: 0.7, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>✨ THE VERDICT</span>
                    {analyzing && <span style={{ marginLeft: "auto", opacity: 0.5 }}>analyzing…</span>}
                    {analysis && (
                      <span style={{ marginLeft: "auto", opacity: 0.5 }}>
                        {Math.abs(visitTime.getTime() - Date.now()) > 120000
                          ? `for ${visitTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "")}`
                          : "updated just now"}
                      </span>
                    )}
                  </div>
                  {analyzing && !analysis && (
                    <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 18, fontWeight: 700, lineHeight: 1.15, marginTop: 6, opacity: 0.85 }}>
                      Reading the weather…
                    </div>
                  )}
                  {analysisError && !analysis && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
                        Couldn't get a verdict.
                      </div>
                      <button onClick={runAnalysis} style={{
                        marginTop: 8, background: SUNNY.ink, color: SUNNY.cream,
                        border: `2px solid ${SUNNY.ink}`, borderRadius: 10,
                        padding: "6px 12px", fontWeight: 900, fontSize: 11,
                        letterSpacing: 0.5, textTransform: "uppercase", cursor: "pointer",
                      }}>Retry</button>
                    </div>
                  )}
                  {analysis && (
                    <>
                      <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 22, fontWeight: 700, letterSpacing: -0.4, lineHeight: 1.15, marginTop: 6 }}>
                        {analysis.headline}
                      </div>
                      {Array.isArray(analysis.factors) && analysis.factors.length > 0 && (
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 700 }}>
                          {analysis.factors.slice(0, 3).map((f, i) => (
                            <div key={i}>{f.icon || "•"} {f.label && <b>{f.label}</b>}{f.label && f.detail ? " · " : ""}{f.detail || ""}</div>
                          ))}
                        </div>
                      )}
                      {(analysis.bestFor || (analysis.watchOut && analysis.watchOutRelevant !== false)) && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1.5px dashed ${SUNNY.ink}40`, display: "flex", flexDirection: "column", gap: 5 }}>
                          {analysis.bestFor && (
                            <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>👍 Best for: {analysis.bestFor}</div>
                          )}
                          {analysis.watchOut && analysis.watchOutRelevant !== false && (
                            <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>⚠️ {analysis.watchOut}</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* SUN ARC card — same primitive as the home/list header */}
                {sunInfo?.sunrise && sunInfo?.sunset && !selected.isIndoor && (
                  <div style={{
                    background: "#fff",
                    border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                    borderRadius: 16, boxShadow: SUNNY.shadowButton,
                    padding: "12px 14px", marginBottom: 14,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, fontWeight: 900, letterSpacing: 1.2, marginBottom: 8 }}>
                      <span style={{ opacity: 0.7 }}>TODAY'S SUN</span>
                      <span style={{ color: SUNNY.tomato }}>SETS {sunInfo.sunset.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "")}</span>
                    </div>
                    <SunArcBar sunrise={sunInfo.sunrise} sunset={sunInfo.sunset} now={visitTime} />
                    {/* Best remaining stretch of the day, from the hourly
                        forecast + sun angle. Hidden when there's no hourly
                        data (wttr.in fallback) or nothing nice left today. */}
                    {(() => {
                      const win = bestWindowToday(weather, selected.lat, selected.lng);
                      if (!win) return null;
                      const fmtH = (d) => d.toLocaleTimeString([], { hour: "numeric" }).toLowerCase().replace(/\s/g, "");
                      return (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1.5px dashed ${SUNNY.ink}40`, fontSize: 12, fontWeight: 800, color: SUNNY.ink }}>
                          ⛱️ Best window today: {fmtH(win.start)}–{fmtH(win.end)} · {fmtTemp(win.temp, useMetric)}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ── PLAN AHEAD — time-shift picker ─────────────────────────
                    Lets users score this playground for a future visit time.
                    Only shown for outdoor venues when future slots exist. */}
                {!selected.isIndoor && sunInfo?.sunset && timeSlotOptions.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 900, letterSpacing: 1.4,
                      opacity: 0.7, color: SUNNY.ink,
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between", marginBottom: 8,
                    }}>
                      <span>PLAN AHEAD — VISIT TIME</span>
                      {Math.abs(visitTime.getTime() - Date.now()) > 120000 && (
                        <button onClick={() => setVisitTime(new Date())} style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: SUNNY.tomato, fontFamily: SUNNY.fontBody,
                          fontSize: 11, fontWeight: 900, letterSpacing: 0.3,
                          padding: 0, textDecoration: "underline",
                        }}>Reset to now</button>
                      )}
                    </div>
                    <div style={{
                      display: "flex", gap: 6, overflowX: "auto",
                      scrollbarWidth: "none", WebkitOverflowScrolling: "touch",
                      paddingBottom: 2,
                    }}>
                      {(() => {
                        const isNowSel = Math.abs(visitTime.getTime() - Date.now()) < 120000;
                        return (
                          <button key="now" onClick={() => setVisitTime(new Date())} style={{
                            flexShrink: 0, padding: "6px 14px",
                            borderRadius: SUNNY.radiusPill,
                            border: `2px solid ${SUNNY.ink}`, cursor: "pointer",
                            boxShadow: isNowSel ? "none" : SUNNY.shadowSticker,
                            background: isNowSel ? SUNNY.ink : "#fff",
                            color: isNowSel ? SUNNY.cream : SUNNY.ink,
                            fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                            textTransform: "uppercase", letterSpacing: 0.4,
                            whiteSpace: "nowrap",
                          }}>Now</button>
                        );
                      })()}
                      {timeSlotOptions.map(slot => {
                        const isSel = Math.abs(visitTime.getTime() - slot.getTime()) < 60000;
                        const label = slot.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "");
                        return (
                          <button key={slot.getTime()} onClick={() => setVisitTime(slot)} style={{
                            flexShrink: 0, padding: "6px 14px",
                            borderRadius: SUNNY.radiusPill,
                            border: `2px solid ${SUNNY.ink}`, cursor: "pointer",
                            boxShadow: isSel ? "none" : SUNNY.shadowSticker,
                            background: isSel ? SUNNY.sun : "#fff",
                            color: SUNNY.ink,
                            fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                            textTransform: "uppercase", letterSpacing: 0.4,
                            whiteSpace: "nowrap",
                          }}>{label}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── 9b ── STAT PATCH ROW: distance/walk · sun-fit · facing ─
                    SUN·FIT is hidden at night (after sunset, before sunrise)
                    because the score reflects current sun conditions and is
                    meaningless when there's no sun. We swap in an "AFTER
                    SUNSET" / "BEFORE SUNRISE" patch so the row doesn't look
                    empty. */}
                {(() => {
                  const mode = ACTIVITY_MODES.find(m => m.id === activityMode);
                  // Match the card/hero: key off road distance once it's loaded.
                  const dispDist = roadDists[selected.osmId] ?? selected.distance;
                  const travel = formatTravelTime(dispDist, mode?.bikeMode, walkTimes[selected.osmId], activityMode);
                  const score = getConditionsScore(selected, visitSunInfo, weather);
                  const facing = selected.facing;
                  const facingLabel = facing ? facing.toUpperCase() : null;
                  const facingSub = facing
                    ? (facing === "N" || facing === "S" ? "compass-aligned" :
                       facing.includes("E") ? "morning best" :
                       facing.includes("W") ? "afternoon best" : "")
                    : null;
                  const fmtTime = (d) => d?.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "") || "";
                  const isAfterSunset = visitSunInfo?.sunset && visitTime > visitSunInfo.sunset;
                  const isBeforeSunrise = visitSunInfo?.sunrise && visitTime < visitSunInfo.sunrise;
                  const isNight = isAfterSunset || isBeforeSunrise;
                  const isShifted = Math.abs(visitTime.getTime() - Date.now()) > 120000;
                  return (
                    <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                      <Patch
                        color={SUNNY.tomato}
                        text={travel}
                        sub={`${fmtDist(dispDist, useMetric)} · directions ›`}
                        rotate={-1}
                        title="Open directions"
                        onClick={() => {
                          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                          if (isIOS) openAppleMaps(selected.name, selected.lat, selected.lng, location?.lat, location?.lng);
                          else openGoogleMaps(selected.name, selected.lat, selected.lng, location?.lat, location?.lng);
                        }}
                      />
                      {isNight ? (
                        <Patch
                          color={SUNNY.ink}
                          text={isAfterSunset ? "AFTER SUNSET" : "BEFORE SUNRISE"}
                          sub={isAfterSunset
                            ? (visitSunInfo?.sunrise ? `sunrise ${fmtTime(visitSunInfo.sunrise)}` : null)
                            : (visitSunInfo?.sunrise ? `rises ${fmtTime(visitSunInfo.sunrise)}` : null)}
                          rotate={1}
                        />
                      ) : (
                        score != null && <Patch color={SUNNY.sun} text={`${score} SUN·FIT`} sub={score >= 70 ? (isShifted ? "great then" : "great now") : score >= 40 ? "fair" : "low"} rotate={1} />
                      )}
                      {facingLabel && <Patch color={SUNNY.grass} text={facingLabel} sub={facingSub || "facing"} rotate={-1.2} />}
                    </div>
                  );
                })()}

                {/* ── 9b ── WHAT'S HERE features grid + "Something missing?" ─
                    Always renders if there are any visible features OR any
                    missing-feature suggestions to offer (so users can
                    crowdsource even on feature-less spots). The "Something
                    missing?" affordance was lost when the old features
                    section got hidden in 9b — bringing it forward into the
                    sticker design here. */}
                {(() => {
                  const modeFeatureId = activityMode ? ACTIVITY_MODES.find(m => m.id === activityMode)?.featureId : null;
                  const allFeatures = [...new Set([...(selected.features || []), ...communityFeatures])];
                  const visible = allFeatures.filter(f => f !== modeFeatureId);
                  const missingFeatures = FEATURES
                    .filter(f => !allFeatures.includes(f.id) && f.id !== modeFeatureId)
                    .sort((a, b) => {
                      const aActive = selectedFeatures.includes(a.id);
                      const bActive = selectedFeatures.includes(b.id);
                      return aActive === bActive ? 0 : aActive ? -1 : 1;
                    });
                  const missingFilterFeatures = missingFeatures.filter(f => selectedFeatures.includes(f.id));
                  if (visible.length === 0 && missingFeatures.length === 0 && !selected.yearBuilt) return null;
                  const recentlyBuilt = isRecentlyBuilt(selected.yearBuilt);
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.4, opacity: 0.7, marginBottom: 8, color: SUNNY.ink }}>WHAT'S HERE</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        {/* Year-built sticker — a recent build/upgrade (e.g. a new
                            Magical Bridge playground) gets a "New" highlight; older
                            venues show the plain build year. Mirrors the list card. */}
                        {selected.yearBuilt && (
                          <span style={{
                            background: recentlyBuilt ? SUNNY.grass : "#fff",
                            color: recentlyBuilt ? "#fff" : SUNNY.ink,
                            border: `2px solid ${SUNNY.ink}`, borderRadius: 12,
                            boxShadow: SUNNY.shadowSticker,
                            padding: "6px 10px",
                            display: "inline-flex", alignItems: "center", gap: 6,
                            fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                          }}>
                            <span style={{ fontSize: 14 }}>{recentlyBuilt ? "✨" : "📅"}</span>
                            <span>{recentlyBuilt ? `New (${selected.yearBuilt})` : `Built ${selected.yearBuilt}`}</span>
                          </span>
                        )}
                        {visible.map(f => {
                          const feat = FEATURES.find(x => x.id === f);
                          const icon = feat?.icon || SPOT_INFO[f]?.emoji || "•";
                          const label = feat?.label || SPOT_INFO[f]?.rateLabel || f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                          const isCommunity = communityFeatures.includes(f) && !(selected.features || []).includes(f);
                          return (
                            <span key={f} style={{
                              background: "#fff", color: SUNNY.ink,
                              border: `2px solid ${SUNNY.ink}`, borderRadius: 12,
                              boxShadow: SUNNY.shadowSticker,
                              padding: "6px 10px",
                              display: "inline-flex", alignItems: "center", gap: 6,
                              fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                              opacity: isCommunity ? 0.9 : 1,
                            }}>
                              <span style={{ fontSize: 14 }}>{icon}</span>
                              <span>{label}</span>
                              {isCommunity && <span style={{ fontSize: 9, opacity: 0.6 }}>👥</span>}
                              {/* Community-reported chips can be voted back off
                                  (ROADMAP "dispute features" Phase 1). OSM-sourced
                                  chips have no ✕ — disputing those is Phase 2. */}
                              {isCommunity && (
                                <button
                                  aria-label={`Report ${label} as not here`}
                                  onClick={() => {
                                    if (!window.confirm(`Not seeing "${label}" at this spot? This removes a community report.`)) return;
                                    fetch("/api/features", {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json" },
                                      body: JSON.stringify({ osmId: selected.osmId, feature: f, action: "remove" }),
                                    })
                                      .then(r => r.ok ? r.json() : null)
                                      .then(data => {
                                        if (!data) return;
                                        setCommunityFeatures(data.features);
                                        if (!data.features.includes(f)) {
                                          setPlaygrounds(prev => prev.map(p =>
                                            String(p.osmId) === String(selected.osmId)
                                              ? { ...p, features: (p.features || []).filter(x => x !== f) }
                                              : p
                                          ));
                                        }
                                      })
                                      .catch(() => {});
                                  }}
                                  style={{
                                    background: "none", border: "none", padding: "0 0 0 2px",
                                    fontSize: 11, fontWeight: 900, color: SUNNY.ink, opacity: 0.45,
                                    cursor: "pointer", lineHeight: 1,
                                  }}
                                >✕</button>
                              )}
                            </span>
                          );
                        })}
                        {/* "Something missing?" toggle — specific label when the user's
                            active filter feature is absent; dashed border signals "add". */}
                        {missingFeatures.length > 0 && (
                          <button
                            onClick={() => setShowAddFeatures(p => !p)}
                            style={{
                              background: showAddFeatures ? SUNNY.ink : SUNNY.cream,
                              color: showAddFeatures ? SUNNY.cream : SUNNY.ink,
                              border: `2px dashed ${SUNNY.ink}`,
                              borderRadius: 12,
                              boxShadow: showAddFeatures ? "none" : SUNNY.shadowSticker,
                              padding: "6px 10px",
                              fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                              cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 6,
                            }}
                          >
                            {showAddFeatures ? "✕ Cancel"
                              : missingFilterFeatures.length > 0
                                ? `➕ Add ${missingFilterFeatures.map(f => f.label).join(", ")}`
                                : "➕ Something missing?"}
                          </button>
                        )}
                      </div>
                      {/* Expanded panel — tap a chip to crowdsource the feature.
                          Uses the existing POST /api/features endpoint. */}
                      {showAddFeatures && missingFeatures.length > 0 && (
                        <div style={{
                          marginTop: 10,
                          background: SUNNY.paper,
                          border: `2px solid ${SUNNY.ink}`,
                          borderRadius: 14,
                          boxShadow: SUNNY.shadowSticker,
                          padding: "10px 12px",
                        }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: SUNNY.inkSoft, marginBottom: 8, lineHeight: 1.4 }}>
                            Tap a feature you see here to add it.
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {missingFeatures.map(f => {
                              const isFilterMatch = selectedFeatures.includes(f.id);
                              return (
                              <button key={f.id} onClick={() => {
                                fetch("/api/features", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ osmId: selected.osmId, feature: f.id, action: "add" }),
                                })
                                  .then(r => r.ok ? r.json() : null)
                                  .then(data => {
                                    if (!data) return;
                                    setCommunityFeatures(data.features);
                                    setShowAddFeatures(false);
                                    setPlaygrounds(prev => prev.map(p =>
                                      String(p.osmId) === String(selected.osmId)
                                        ? { ...p, features: [...new Set([...p.features, ...data.features])] }
                                        : p
                                    ));
                                  })
                                  .catch(() => {});
                              }} style={{
                                background: isFilterMatch ? SUNNY.sun : "#fff",
                                color: SUNNY.ink,
                                border: `2px solid ${SUNNY.ink}`,
                                borderRadius: 999,
                                boxShadow: SUNNY.shadowSticker,
                                padding: "5px 10px",
                                fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                                cursor: "pointer",
                                display: "inline-flex", alignItems: "center", gap: 4,
                              }}>
                                {f.icon} {f.label}
                              </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── 9b ── MAP CARD with directions primary + overflow ────── */}
                <div style={{
                  background: "#fff",
                  border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                  borderRadius: 16, boxShadow: SUNNY.shadowButton,
                  padding: 0, marginBottom: 16, overflow: "hidden",
                }}>
                  <div style={{ height: 140, position: "relative" }}>
                    <Suspense fallback={<div style={{ height: 140, background: SUNNY.cream }} />}>
                      <VenueMap lat={selected.lat} lng={selected.lng} />
                    </Suspense>
                  </div>
                  <div style={{
                    display: "flex", gap: 6, padding: 8,
                    borderTop: `2px solid ${SUNNY.ink}`,
                    background: SUNNY.cream,
                  }}>
                    <button
                      onClick={() => {
                        openGoogleMaps(selected.name, selected.lat, selected.lng, location?.lat, location?.lng);
                      }}
                      style={{
                        flex: 1,
                        background: SUNNY.ink, color: SUNNY.cream,
                        border: `2px solid ${SUNNY.ink}`, borderRadius: 12,
                        boxShadow: SUNNY.shadowButtonAccent,
                        padding: "10px 14px", fontFamily: SUNNY.fontBody,
                        fontSize: 12, fontWeight: 900, letterSpacing: 0.5,
                        textTransform: "uppercase", cursor: "pointer",
                      }}
                    >📍 Directions →</button>
                    {/* Overflow — Apple Maps as secondary option. */}
                    <button
                      onClick={() => openAppleMaps(selected.name, selected.lat, selected.lng, location?.lat, location?.lng)}
                      title="Open in Apple Maps"
                      style={{
                        background: "#fff", color: SUNNY.ink,
                        border: `2px solid ${SUNNY.ink}`, borderRadius: 12,
                        boxShadow: SUNNY.shadowSticker,
                        padding: "10px 14px", fontSize: 16, cursor: "pointer", lineHeight: 1,
                      }}
                    >···</button>
                  </div>
                </div>

                {/* ── 9c ── ADDRESS + PARKING unified card ──────────────────
                    Single cream sticker, two rows separated by a dashed
                    divider. Bottom row tappable for parking directions. */}
                {(selected.address || detailGeoInfo?.address || selected.parking) && (
                  <div style={{
                    background: SUNNY.cream,
                    border: `2px solid ${SUNNY.ink}`,
                    borderRadius: 14,
                    boxShadow: SUNNY.shadowSticker,
                    marginBottom: 16,
                    overflow: "hidden",
                  }}>
                    {(selected.address || detailGeoInfo?.address) && (
                      <div style={{
                        padding: "12px 14px",
                        display: "flex", alignItems: "center", gap: 10,
                        fontFamily: SUNNY.fontBody, fontSize: 13, fontWeight: 700,
                        color: SUNNY.ink,
                      }}>
                        <span style={{ fontSize: 18 }}>📍</span>
                        <span style={{ flex: 1, lineHeight: 1.3 }}>
                          {selected.address || detailGeoInfo?.address}
                        </span>
                      </div>
                    )}
                    {selected.parking && (selected.address || detailGeoInfo?.address) && (
                      <div style={{
                        borderTop: `1.5px dashed ${SUNNY.ink}`,
                        opacity: 0.5,
                        margin: "0 14px",
                      }} />
                    )}
                    {selected.parking && (() => {
                      const { type, fee, distance, lat: pLat, lng: pLng } = selected.parking;
                      const icon = type === "garage" ? "🏢" : type === "street" ? "🚗" : "🅿️";
                      const typeLabel = type === "garage" ? "Parking garage" : type === "street" ? "Street parking" : "On-site parking";
                      const feeLabel = fee ? "paid" : "free";
                      const distLabel = distance < 0.05 ? "on-site" : `${fmtShortDist(distance, useMetric)} from gate`;
                      const navigate = () => {
                        if (!pLat || !pLng) return;
                        if (location?.lat && location?.lng) {
                          openAppleMapsViaParking(location.lat, location.lng, pLat, pLng, selected.lat, selected.lng);
                        } else {
                          openAppleMaps("Parking", pLat, pLng);
                        }
                      };
                      return (
                        <div onClick={navigate} style={{
                          padding: "12px 14px",
                          display: "flex", alignItems: "center", gap: 10,
                          fontFamily: SUNNY.fontBody, fontSize: 13, fontWeight: 700,
                          color: SUNNY.ink,
                          cursor: pLat ? "pointer" : "default",
                        }}>
                          <span style={{ fontSize: 18 }}>{icon}</span>
                          <span style={{ flex: 1, lineHeight: 1.3 }}>
                            {typeLabel} · {feeLabel} · {distLabel}
                          </span>
                          {pLat && <span style={{ fontSize: 16, opacity: 0.5 }}>›</span>}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ── 9c ── COMMUNITY BLOCK ─────────────────────────────────
                    Overlapping ink-bordered colored avatars + summary,
                    condition-based rating scale, "📷 Add photo" trigger. */}
                {(() => {
                  // Prefer condition data (new unified system); fall back to
                  // legacy star ratings for venues rated before the change.
                  const condCount = pgCondition?.count || 0;
                  const starCount = pgRating?.count || 0;
                  const ratingCount = condCount > 0 ? condCount : starCount;
                  const condAvg = condCount > 0 ? parseFloat(pgCondition.average) : null;
                  const starAvg = pgRating?.average ? parseFloat(pgRating.average) : null;
                  const ratingAvg = condAvg ?? starAvg;
                  const COND_LABELS = ["Unsafe", "Rough", "OK", "Good", "Like new"];
                  const topTagEntries = pgCondition?.tags
                    ? Object.entries(pgCondition.tags).sort((a, b) => b[1] - a[1]).slice(0, 1)
                    : [];
                  const topTag = topTagEntries[0]?.[0]?.replace(/_/g, " ") || null;
                  // Avatar colors — picked deterministically per visible avatar
                  const AVATAR_COLORS = [SUNNY.tomato, SUNNY.grass, SUNNY.sky, SUNNY.sun];
                  const avatarCount = Math.min(ratingCount, 3);
                  return (
                    <div style={{
                      background: "#fff",
                      border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                      borderRadius: 16, boxShadow: SUNNY.shadowButton,
                      padding: "14px", marginBottom: 16,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: ratingCount > 0 ? 12 : 8 }}>
                        {/* Overlapping avatars */}
                        {avatarCount > 0 && (
                          <div style={{ display: "flex" }}>
                            {Array.from({ length: avatarCount }).map((_, i) => (
                              <div key={i} style={{
                                width: 28, height: 28, borderRadius: "50%",
                                background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                                border: `2px solid ${SUNNY.ink}`,
                                marginLeft: i === 0 ? 0 : -8,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 12, color: "#fff", fontWeight: 900,
                                fontFamily: SUNNY.fontBody,
                              }}>·</div>
                            ))}
                          </div>
                        )}
                        <div style={{ flex: 1, fontFamily: SUNNY.fontBody, lineHeight: 1.2 }}>
                          {ratingCount > 0 ? (
                            <>
                              <div style={{ fontSize: 13, fontWeight: 800, color: SUNNY.ink }}>
                                {ratingCount} {ratingCount === 1 ? "person" : "people"} rated this
                              </div>
                              {ratingAvg != null && (() => {
                                const idx = Math.min(4, Math.max(0, Math.round(ratingAvg) - 1));
                                const label = COND_LABELS[idx];
                                const color = [SUNNY.tomato, SUNNY.sunDeep, SUNNY.sun, SUNNY.grass, SUNNY.grass][idx];
                                return (
                                  <div style={{ display: "flex", alignItems: "center", gap: 2, marginTop: 2, flexWrap: "wrap" }}>
                                    {Array.from({ length: 5 }, (_, i) => (
                                      <span key={i} style={{ fontSize: 14, opacity: i < Math.round(ratingAvg) ? 1 : 0.18 }}>
                                        {getSpotInfo(activityMode, selected?.name).emoji}
                                      </span>
                                    ))}
                                    <span style={{ fontSize: 11, fontWeight: 800, color, marginLeft: 3 }}>
                                      {label} · {ratingAvg.toFixed(1)}/5
                                    </span>
                                  </div>
                                );
                              })()}
                            </>
                          ) : (
                            <div style={{ fontSize: 13, fontWeight: 800, color: SUNNY.inkSoft }}>
                              Be the first to rate this {getSpotInfo(activityMode, selected?.name).noun}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Pull-quote bubble — most-reported condition tag */}
                      {topTag && (
                        <div style={{
                          margin: "4px 0 12px",
                          padding: "10px 14px",
                          background: SUNNY.cream,
                          border: `1.5px dashed ${SUNNY.ink}`,
                          borderRadius: 14,
                          fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 700,
                          fontStyle: "italic", color: SUNNY.inkSoft,
                          lineHeight: 1.4,
                        }}>
                          "Most parents say <span style={{ color: SUNNY.ink }}>{topTag}</span>"
                        </div>
                      )}

                      {/* Condition-based rating — Unsafe → Like New scale.
                          Also writes to /api/rate so card scores stay current.
                          Shown for all activity modes. */}
                      {(() => {
                        const RATING_LABELS = ["Unsafe", "Rough", "OK", "Good", "Like new"];
                        const RATING_COLORS = [SUNNY.tomato, SUNNY.sunDeep, SUNNY.sun, SUNNY.grass, SUNNY.grass];
                        const submitCondition = async (rating, tags) => {
                          if (submittingCondition) return;
                          setSubmittingCondition(true);
                          setUserCondition(rating);
                          setUserRating(rating);
                          setUserConditionTags(tags);
                          try {
                            const saved = JSON.parse(localStorage.getItem("pg_condition") || "{}");
                            saved[selected.osmId] = { rating, tags };
                            localStorage.setItem("pg_condition", JSON.stringify(saved));
                            const rSaved = JSON.parse(localStorage.getItem("pg_ratings") || "{}");
                            rSaved[selected.osmId] = rating;
                            localStorage.setItem("pg_ratings", JSON.stringify(rSaved));
                            const [condRes, rateRes] = await Promise.all([
                              fetch("/api/condition", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ osmId: selected.osmId, rating, tags }),
                              }),
                              fetch("/api/rate", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ osmId: selected.osmId, rating }),
                              }),
                            ]);
                            const [condData, rateData] = await Promise.all([condRes.json(), rateRes.json()]);
                            if (condData?.count) setPgCondition(condData);
                            if (rateData?.count) setPgRating(rateData);
                            // Lock for this session; remember if revision already used
                            try {
                              const srs = JSON.parse(sessionStorage.getItem("pg_session_ratings") || "{}");
                              const wasAlreadyRated = !!srs[String(selected.osmId)];
                              srs[String(selected.osmId)] = { rating, revised: wasAlreadyRated };
                              sessionStorage.setItem("pg_session_ratings", JSON.stringify(srs));
                            } catch {}
                            setSessionRatingLocked(true);
                          } catch {}
                          setSubmittingCondition(false);
                        };
                        const toggleTag = (t) => {
                          const next = userConditionTags.includes(t)
                            ? userConditionTags.filter(x => x !== t)
                            : [...userConditionTags, t];
                          setUserConditionTags(next);
                          if (userCondition) submitCondition(userCondition, next);
                        };
                        const isLocked = sessionRatingLocked && !!userCondition;
                        const sessionEntry = (() => { try { return JSON.parse(sessionStorage.getItem("pg_session_ratings") || "{}")[String(selected.osmId)]; } catch { return null; } })();
                        const hasRevised = sessionEntry?.revised === true;
                        return (
                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1.5px dashed ${SUNNY.ink}`, opacity: 1 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1, color: SUNNY.inkSoft, textTransform: "uppercase" }}>
                                {userCondition ? "Your rating" : "Rate this spot"}
                              </div>
                              {isLocked && !hasRevised && (
                                <button
                                  onClick={() => {
                                    try {
                                      const srs = JSON.parse(sessionStorage.getItem("pg_session_ratings") || "{}");
                                      if (srs[String(selected.osmId)]) srs[String(selected.osmId)].revised = true;
                                      sessionStorage.setItem("pg_session_ratings", JSON.stringify(srs));
                                    } catch {}
                                    setSessionRatingLocked(false);
                                  }}
                                  style={{ fontSize: 11, fontWeight: 700, color: SUNNY.inkSoft, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0, fontFamily: SUNNY.fontBody }}
                                >Change</button>
                              )}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                              {[1,2,3,4,5].map(i => (
                                <button key={i}
                                  onClick={() => !isLocked && submitCondition(i, userConditionTags)}
                                  disabled={submittingCondition || isLocked}
                                  style={{
                                    background: "none", border: "none", padding: "2px 1px",
                                    cursor: isLocked ? "default" : "pointer", fontSize: 26, lineHeight: 1,
                                    opacity: i <= (userCondition || 0) ? 1 : (isLocked ? 0.08 : 0.18),
                                    transform: i === userCondition ? "scale(1.2)" : "scale(1)",
                                    transition: "opacity 0.1s, transform 0.1s",
                                  }}>
                                  {getSpotInfo(activityMode, selected?.name).emoji}
                                </button>
                              ))}
                              {userCondition && (
                                <span style={{
                                  fontSize: 12, fontWeight: 800, marginLeft: 4,
                                  color: RATING_COLORS[userCondition - 1],
                                  fontFamily: SUNNY.fontBody,
                                }}>
                                  {RATING_LABELS[userCondition - 1]}
                                </span>
                              )}
                            </div>
                            {!isLocked && <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                              {getConditionTags(activityMode).map(t => {
                                const active = userConditionTags.includes(t.id);
                                return (
                                  <button key={t.id} onClick={() => toggleTag(t.id)}
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: 4,
                                      padding: "4px 9px", borderRadius: SUNNY.radiusPill, fontSize: 11,
                                      background: active ? SUNNY.ink : "#fff",
                                      color: active ? SUNNY.cream : SUNNY.ink,
                                      border: `2px solid ${SUNNY.ink}`,
                                      boxShadow: active ? "none" : SUNNY.shadowSticker,
                                      cursor: "pointer", fontWeight: 800,
                                      fontFamily: SUNNY.fontBody, textTransform: "uppercase", letterSpacing: 0.3,
                                    }}>
                                    <span style={{ fontSize: 12 }}>{t.icon}</span><span>{t.label}</span>
                                  </button>
                                );
                              })}
                            </div>}
                          </div>
                        );
                      })()}
                      {/* ── Restroom quality (shown when playground has a restroom) ── */}
                      {(selected.features || []).includes("restrooms") && (() => {
                        const rKey = `${selected.osmId}-restroom`;
                        const rr = restroomRatings[rKey];
                        const savedR = (() => { try { return JSON.parse(localStorage.getItem("restroom_ratings") || "{}")[rKey] || null; } catch { return null; } })();
                        const isLocked = rr?.tags?.locked > 0 || savedR?.locked;
                        const submitR = (val, locked) => {
                          const tags = locked ? ["locked"] : [];
                          fetch("/api/condition", { method: "POST", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ osmId: rKey, rating: val, tags }) })
                            .then(r => r.json()).then(d => { if (d?.count) setRestroomRatings(prev => ({ ...prev, [rKey]: d })); });
                          try {
                            const ls = JSON.parse(localStorage.getItem("restroom_ratings") || "{}");
                            ls[rKey] = { rating: val, locked };
                            localStorage.setItem("restroom_ratings", JSON.stringify(ls));
                          } catch {}
                        };
                        return (
                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1.5px dashed ${SUNNY.ink}` }}>
                            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1, color: SUNNY.inkSoft, marginBottom: 8, textTransform: "uppercase" }}>
                              🚻 Restroom quality
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                              {isLocked && <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 8, background: SUNNY.tomato, color: "#fff", border: `1px solid ${SUNNY.ink}` }}>🔒 Key required</span>}
                              {rr?.count > 0 && <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 8, background: SUNNY.cream, border: `1px solid ${SUNNY.ink}` }}>{tierFromAvg(rr.average).emoji} {tierFromAvg(rr.average).label} <span style={{ color: SUNNY.inkSoft }}>({rr.count})</span></span>}
                            </div>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              <span style={{ fontSize: 9, fontWeight: 900, color: SUNNY.inkSoft, letterSpacing: 0.5, marginRight: 2, textTransform: "uppercase" }}>Rate:</span>
                              {RESTROOM_TIERS.map(t => (
                                <button key={t.value} title={t.label}
                                  onClick={() => submitR(t.value, savedR?.locked || false)}
                                  style={{ fontSize: 16, padding: "3px 7px", borderRadius: 8, cursor: "pointer",
                                    border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                                    background: savedR?.rating === t.value ? SUNNY.sun : SUNNY.cream,
                                    boxShadow: savedR?.rating === t.value ? SUNNY.shadowSticker : "none" }}>
                                  {t.emoji}
                                </button>
                              ))}
                              <button title="Locked / key required"
                                onClick={() => submitR(savedR?.rating || 3, !(savedR?.locked || false))}
                                style={{ fontSize: 14, padding: "3px 8px", borderRadius: 8, cursor: "pointer", marginLeft: 4,
                                  border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                                  background: savedR?.locked ? SUNNY.tomato : SUNNY.cream,
                                  color: savedR?.locked ? "#fff" : SUNNY.ink,
                                  boxShadow: savedR?.locked ? SUNNY.shadowSticker : "none" }}>
                                🔒
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                {/* ── 9d ── PAIR THIS WITH ─────────────────────────────────
                    Suggests up to 3 nearby spots. Each "+" button opens
                    the outing builder pre-populated with this venue and
                    the chosen stop. The "+ BUILD OUTING →" link top-right
                    opens the empty builder so the user can start fresh. */}
                {pairSuggestions.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.4, opacity: 0.7, color: SUNNY.ink }}>PAIR THIS WITH</div>
                      <button
                        onClick={() => openOuting(selected)}
                        style={{
                          background: "none", border: "none", padding: "2px 0",
                          color: SUNNY.tomato, fontFamily: SUNNY.fontBody,
                          fontSize: 11, fontWeight: 900, letterSpacing: 0.5,
                          textTransform: "uppercase", cursor: "pointer",
                        }}
                      >+ Build outing →</button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {pairSuggestions.map((s, i) => {
                        const k = (() => {
                          const map = {
                            cafe: { label: "COFFEE", emoji: "☕", color: SUNNY.grass },
                            food: { label: "FOOD", emoji: "🍽", color: SUNNY.tomato },
                            view: { label: "VIEW", emoji: "🌅", color: SUNNY.sky },
                            icecream: { label: "ICE CREAM", emoji: "🍦", color: SUNNY.sky },
                            playground: { label: "PLAYGROUND", emoji: "🛝", color: SUNNY.tomato },
                            park: { label: "PARK", emoji: "🌳", color: SUNNY.grass },
                            beach: { label: "BEACH", emoji: "🏖️", color: SUNNY.sky },
                            skatepark: { label: "SKATEPARK", emoji: "🛹", color: SUNNY.sunDeep },
                            pump_track: { label: "PUMP TRACK", emoji: "🚵", color: SUNNY.grass },
                            restroom: { label: "RESTROOM", emoji: "🚻", color: SUNNY.inkSoft },
                          };
                          return map[s.kind] || { label: s.kind?.toUpperCase() || "STOP", emoji: "📍", color: SUNNY.inkSoft };
                        })();
                        return (
                          <div key={i}
                            onClick={() => openAppleMaps(s.name, s.lat, s.lng, selected.lat, selected.lng)}
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              background: "#fff", color: SUNNY.ink,
                              border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`, borderRadius: 14,
                              boxShadow: SUNNY.shadowButton,
                              padding: "10px 12px",
                              fontFamily: SUNNY.fontBody,
                              cursor: "pointer",
                            }}>
                            <span style={{
                              width: 36, height: 36, borderRadius: 10,
                              background: k.color, color: "#fff",
                              border: `2px solid ${SUNNY.ink}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 18, flexShrink: 0,
                            }}>{k.emoji}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.8, opacity: 0.6, color: SUNNY.ink }}>{k.label}</div>
                              <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: 15, fontWeight: 700, lineHeight: 1.15, color: SUNNY.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {s.name}
                              </div>
                              {s.dMi != null && (
                                <div style={{ fontSize: 11, fontWeight: 700, color: SUNNY.tomato, marginTop: 2 }}>
                                  {s.dMi < 0.1 ? "<0.1 mi" : `${s.dMi.toFixed(1)} mi away`}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={e => { e.stopPropagation(); openOuting(selected, { kind: s.kind, name: s.name, lat: s.lat, lng: s.lng }); }}
                              title="Add to a new outing"
                              aria-label={`Add ${s.name} to a new outing`}
                              style={{
                                width: 36, height: 36, borderRadius: "50%",
                                background: SUNNY.cream, color: SUNNY.ink,
                                border: `2px solid ${SUNNY.ink}`,
                                boxShadow: SUNNY.shadowSticker,
                                fontSize: 18, fontWeight: 900,
                                cursor: "pointer", flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                lineHeight: 1,
                              }}
                            >+</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── 9d ── SMALL ACTIONS ROW ──────────────────────────────
                    De-emphasized info text per the spec — NOT buttons.
                    Visible-only when there's actually something to link to. */}
                <div style={{
                  display: "flex", justifyContent: "center", alignItems: "center", gap: 14, flexWrap: "wrap",
                  padding: "10px 0 6px",
                  fontFamily: SUNNY.fontBody, fontSize: 12, fontWeight: 800,
                  color: SUNNY.inkSoft, letterSpacing: 0.3,
                }}>
                  {selected.website && (() => {
                    let href = selected.website;
                    try {
                      const u = new URL(selected.website);
                      u.searchParams.set("utm_source", "playgroundfinder");
                      u.searchParams.set("utm_medium", "app");
                      href = u.toString();
                    } catch {}
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          color: SUNNY.ink, textDecoration: "none",
                          border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
                          borderRadius: SUNNY.radiusPill,
                          boxShadow: SUNNY.shadowButton,
                          padding: "6px 14px",
                          fontSize: 12, fontWeight: 900, letterSpacing: 0.5,
                          background: SUNNY.cream,
                        }}>
                        🌐 Website
                      </a>
                    );
                  })()}
                  <span
                    onClick={async () => {
                      const params = new URLSearchParams({
                        spot: selected.osmId,
                        name: displayName(selected.name, selected.address),
                        mode: activityMode || "",
                        lat: selected.lat,
                        lng: selected.lng,
                      });
                      const url = `${window.location.origin}${window.location.pathname}?${params}`;
                      const text = `Check out ${displayName(selected.name, selected.address)} on Playground Finder!`;
                      if (navigator.share) {
                        try { await navigator.share({ title: displayName(selected.name, selected.address), text, url }); } catch {}
                      } else {
                        try { await navigator.clipboard.writeText(`${text} ${url}`); alert("Link copied!"); } catch {}
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >↗ Share</span>
                  <span
                    onClick={() => {
                      const issue = window.prompt(`Report a problem with "${displayName(selected.name, selected.address)}":\n\n(e.g. "wrong location", "no longer exists", "not a playground")`);
                      if (!issue) return;
                      // We don't have a dedicated reports endpoint yet — the
                      // existing /api/features endpoint accepts a freeform
                      // "report" feature so the data lands in feature-reports.json
                      // for the operator to triage manually.
                      fetch("/api/features", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ osmId: selected.osmId, feature: `report:${issue.slice(0, 200)}`, action: "add" }),
                      }).then(() => alert("Thanks — we'll take a look.")).catch(() => {});
                    }}
                    style={{ cursor: "pointer", opacity: 0.6 }}
                  >⚐ Report</span>
                </div>
              </div>
            </>
          );
        })()}
        {/* ── /Phase 9a ──────────────────────────────────────────────────── */}

        {/* Old back row + title — hero replaces both. Spread first so our
            display:none wins (S.detailBack has display:flex which the merge
            from origin/staging un-overrode). */}
        <div style={{ ...S.detailBack, display: "none" }}>
          <div onClick={() => setState("results")}>← Back</div>
        </div>
        <div style={{ ...S.detailName, display: "none" }}>
          {displayName(GENERIC_NAME_RE.test(selected.name) && detailGeoInfo?.suggestedName ? detailGeoInfo.suggestedName : selected.name, selected.address || detailGeoInfo?.address)}
        </div>

        {/* School override toggle — power-user action, hidden in the new
            sunny design. The school flag is set automatically from OSM tags
            and proximity heuristics; manual override can move into the
            inspector / report flow in a future phase. */}
        {false && !activityMode && (() => {
          const isSchool = schoolOverrides[selected.osmId] ?? selected.isSchool;
          const toggle = () => setSchoolOverrides(prev => {
            const next = { ...prev, [selected.osmId]: !isSchool };
            localStorage.setItem("pg_school_overrides", JSON.stringify(next));
            return next;
          });
          return (
            <button onClick={toggle} style={{ margin: "0 16px 12px", background: "none", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "6px 12px", color: isSchool ? "#fb923c" : "#64748b", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {isSchool
                ? `🏫 ${selected.nearestSchool ? `Near ${selected.nearestSchool}` : "School playground"} · tap to unmark`
                : "➕ Mark as school playground"}
            </button>
          );
        })()}

        {/* Photo section — id used by the community block's "📷 Add photo"
            button to scroll here. We skip the FIRST Google photo (already
            in the hero) so it's not duplicated; PhotoSection's gallery
            shows additional photos + user uploads + the upload button.
            If only one photo exists, PhotoSection just renders the upload
            UI (no gallery). */}
        <div id="pg-photo-section" />
        <PhotoSection
          playgroundId={String(selected.osmId)}
          googlePhotoRefs={(selected.photoRefs?.length ? selected.photoRefs : (selected.photoRef ? [selected.photoRef] : [])).slice(1)}
          playgroundName={displayName(selected.name, selected.address)}
          onUserPhotoAdded={url => {
            setPlaygrounds(prev => prev.map(p =>
              String(p.osmId) === String(selected.osmId)
                ? { ...p, userPhotoUrl: url }
                : p
            ));
            setSelected(prev => prev && String(prev.osmId) === String(selected.osmId)
              ? { ...prev, userPhotoUrl: url } : prev);
          }}
        />

        {/* Sun info card — replaced by Phase 9a Sun-Arc card; hidden for now.
            Will be removed in Phase 9b once Stat Patch row + Features grid land. */}
        {false && sunInfo && !selected.isIndoor && (
          <div style={S.sunCard}>
            <div style={{ fontSize: 40 }}>
              {(() => {
                if (weather?.fog) return "🌁";
                const cloud = weather?.cloud ?? 0;
                if (cloud > 85) return "☁️";
                if (cloud > 60) return "⛅";
                if (sunInfo.altitude >= 25) return "☀️";
                if (sunInfo.altitude >= 5) return "🌤️";
                if (sunInfo.altitude >= -6) return "🌅";
                if (sunInfo.altitude >= -18) return "🌆";
                return "🌙";
              })()}
            </div>
            <div style={S.sunInfo}>
              <div style={S.sunTitle}>{weather?.cloud > 60 ? "SKY CONDITIONS" : "SUN ANGLE RIGHT NOW"}</div>
              <div style={S.sunVal}>{sunInfo.altitude}° altitude{weather?.cloud > 0 ? ` · ${weather.cloud}% cloud` : ""}</div>
              <div style={S.sunSub}>
                {weather?.cloud > 80 ? "Full cloud cover — diffuse light" : weather?.cloud > 60 ? "Partly obscured by clouds" : sunInfo.label}
                {" · "}{getSpotInfo(activityMode, selected?.name).facing} faces {selected.facing}{sunInfo.altitude >= 0 ? ` · sun from ${getCompass(sunInfo.azimuth)}` : ""}
              </div>
            </div>
            <ScoreRing score={sunInfo.score} size={56} />
          </div>
        )}

        {/* AI Analysis — old presentation. Replaced by Phase 9a Verdict card
            above the photo section. Kept hidden so the analysis-cache state
            and infoRow detail (factors / score) still work for downstream
            sections that reference them; will be cleanly removed in 9b. */}
        <div style={{ ...S.analysisCard, display: "none" }}>
          {analyzing ? (
            <div style={S.analysisLoading}>
              <div style={{ fontSize: 28, animation: "spin 1s linear infinite" }}>🌀</div>
              <div>Claude is analyzing this {getSpotInfo(activityMode, selected?.name).noun}…</div>
              <div style={{ fontSize: 12 }}>Checking weather, sun, and features</div>
            </div>
          ) : !analysis ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "8px 4px" }}>
              {analysisError && (
                <div style={{ alignSelf: "stretch", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "8px 12px", color: "#f87171", fontSize: 12 }}>
                  ⚠️ {analysisError}
                </div>
              )}
              <button
                onClick={runAnalysis}
                disabled={!weather}
                style={{
                  background: weather ? "linear-gradient(135deg, #f59e0b, #fb923c)" : "#334155",
                  color: weather ? "#0f172a" : "#94a3b8",
                  border: "none", borderRadius: 14, padding: "13px 22px",
                  fontSize: 15, fontWeight: 800, cursor: weather ? "pointer" : "not-allowed",
                  boxShadow: weather ? "0 4px 14px rgba(245,158,11,0.3)" : "none",
                }}
              >
                {analysisError ? "Retry analysis" : "✨ Analyze this spot"}
              </button>
              <div style={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>
                {weather ? "AI verdict on weather, sun, and features — uses one API call." : "Waiting for weather…"}
              </div>
            </div>
          ) : analysis ? (
            <div style={{ animation: "fadeUp 0.4s ease both" }}>
              <div style={S.verdictRow}>
                <FunMeter score={analysis.overallScore} spotEmoji={getSpotInfo(activityMode, selected?.name).emoji} />
                <div style={S.verdictText}>
                  <div style={S.verdictHeadline}>{analysis.headline}</div>
                </div>
              </div>
              <div style={S.infoRow}>
                {analysis.weatherVerdict && !weather?.unavailable && (
                <div style={S.infoItem}>
                  <div style={S.infoLabel}>{weather?.icon || "🌡️"} Weather</div>
                  <div style={S.infoVal}>{analysis.weatherVerdict}</div>
                </div>
                )}
                {analysis.watchOut && analysis.watchOutRelevant !== false && (
                  <div style={{ ...S.infoItem, borderColor: "rgba(251,113,133,0.25)", background: "rgba(251,113,133,0.06)" }}>
                    <div style={{ ...S.infoLabel, color: "#f87171" }}>⚠️ Watch out</div>
                    <div style={S.infoVal}>{analysis.watchOut}</div>
                  </div>
                )}
                {analysis.bestFor && (
                  <div style={{ fontSize: 12, color: "#94a3b8", paddingTop: 4 }}>
                    Best for: {analysis.bestFor}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* User rating — Phase 9c: rating moved into the new community
            block above the photo section. Hidden here. */}
        {false && selected && (() => {
          const submitRating = async (r) => {
            if (submittingRating) return;
            setSubmittingRating(true);
            setUserRating(r);
            try {
              const saved = JSON.parse(localStorage.getItem("pg_ratings") || "{}");
              saved[selected.osmId] = r;
              localStorage.setItem("pg_ratings", JSON.stringify(saved));
              const res = await fetch("/api/rate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ osmId: selected.osmId, rating: r }),
              });
              const updated = await res.json();
              setPgRating(updated);
            } catch {}
            setSubmittingRating(false);
          };
          const display = hoverRating || userRating || 0;
          return (
            <div style={{ margin: "0 16px 16px", background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: "16px", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
                {userRating ? "Your Rating" : `Rate This ${getSpotInfo(activityMode, selected?.name).rateLabel}`}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {[1,2,3,4,5].map(i => (
                  <span key={i}
                    style={{ fontSize: 28, cursor: "pointer", opacity: i <= display ? 1 : 0.25, filter: i <= display ? "none" : "grayscale(1)", transition: "opacity 0.15s, transform 0.1s", transform: i <= display ? "scale(1.15)" : "scale(1)" }}
                    onMouseEnter={() => setHoverRating(i)}
                    onMouseLeave={() => setHoverRating(null)}
                    onClick={() => submitRating(i)}
                  >{getSpotInfo(activityMode, selected?.name).emoji}</span>
                ))}
                {userRating && <span style={{ fontSize: 12, color: "#4ade80", fontWeight: 700, marginLeft: 4 }}>Rated!</span>}
              </div>
              {pgRating?.count > 0 && (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 2 }}>
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} style={{ fontSize: 13, opacity: i < Math.round(parseFloat(pgRating.average)) ? 1 : 0.2 }}>
                      {getSpotInfo(activityMode, selected?.name).emoji}
                    </span>
                  ))}
                  <span style={{ fontSize: 11, color: "#64748b", marginLeft: 3 }}>
                    {parseFloat(pgRating.average).toFixed(1)}/5 · {pgRating.count} {pgRating.count === 1 ? "rating" : "ratings"}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Go Now banner — Phase 9d: redundant with the Verdict card, which
            already says "Great conditions" or similar in the headline.
            Hidden here. */}
        {false && analysis?.goNow && (
          <div style={S.goNowBanner}>
            {kidsMode ? (
              <>
                <div style={{ fontSize: 52, lineHeight: 1 }}>🎉</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: "#15803d" }}>YES! Let's GO!</div>
                <div style={{ fontSize: 14, color: "#166534" }}>It's a perfect time to play!</div>
              </>
            ) : (
              <>
                <span style={{ fontSize: 28 }}>🟢</span>
                <div>
                  <div>Great time to go!</div>
                  <div style={{ fontSize: 12, fontWeight: 400, color: "#86efac" }}>Conditions are good right now</div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Address + neighborhood — Phase 9c: address is in the unified
            address+parking card above. Year built moved into the WHAT'S
            HERE features grid as a status chip. Hidden here. */}
        {false && (selected.address || detailGeoInfo?.address || selected.neighborhood) && (
          <div style={{ margin: "0 16px 4px", fontSize: 13, color: "#64748b", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>📍</span>
            {(selected.address || detailGeoInfo?.address) && <span>{selected.address || detailGeoInfo.address}</span>}
          </div>
        )}
        {false && selected.yearBuilt && (
          <div style={{ margin: "0 16px 4px", fontSize: 13, color: "#cbd5e1", display: "flex", alignItems: "center", gap: 6 }}>
            <span>📅</span>
            <span>Built {selected.yearBuilt}</span>
          </div>
        )}

        {/* Website / Google Maps links — Phase 9b: directions consolidated into
            the new map card; website link will return as a "small actions row"
            in 9d. Hidden for now. */}
        {false && (selected.website || selected.googleMapsUri) && (
          <div style={{ margin: "0 16px 16px", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {selected.website && (() => {
              let href = selected.website;
              try {
                const u = new URL(selected.website);
                u.searchParams.set("utm_source", "playgroundfinder");
                u.searchParams.set("utm_medium", "app");
                href = u.toString();
              } catch {}
              return (
                <a href={href} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#60a5fa", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)", borderRadius: 8, padding: "5px 10px", textDecoration: "none" }}>
                  🌐 Website
                </a>
              );
            })()}
            {selected.googleMapsUri && (
              <a href={selected.googleMapsUri} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#60a5fa", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)", borderRadius: 8, padding: "5px 10px", textDecoration: "none" }}>
                🗺️ Google Maps
              </a>
            )}
          </div>
        )}

        {/* Satellite map + Apple/Google Maps buttons — Phase 9b: replaced by
            the new sticker-framed map card with a single "📍 Directions →"
            primary + ··· overflow above. Hidden for now. */}
        <div style={{ display: "none", ...S.mapContainer, overflow: "hidden" }}>
          <Suspense fallback={<div style={{ height: 200 }} />}>
            <VenueMap lat={selected.lat} lng={selected.lng} />
          </Suspense>
        </div>

        {/* Parking — Phase 9c: merged into the new address+parking unified
            card above. Hidden here. */}
        {false && selected.parking && (() => {
          const { type, fee, distance, lat: pLat, lng: pLng } = selected.parking;
          const icon = type === "garage" ? "🏢" : type === "street" ? "🚗" : "🅿️";
          const typeLabel = type === "garage" ? "Parking garage" : type === "street" ? "Street parking" : "Parking lot";
          const feeLabel = fee ? "· paid" : "· free";
          const distLabel = distance < 0.05 ? "on-site" : `${fmtShortDist(distance, useMetric)} away`;
          const navigate = () => {
            if (!pLat || !pLng) return;
            if (location?.lat && location?.lng) {
              openAppleMapsViaParking(location.lat, location.lng, pLat, pLng, selected.lat, selected.lng);
            } else {
              openAppleMaps("Parking", pLat, pLng);
            }
          };
          return (
            <div onClick={navigate} style={{ margin: "0 16px 16px", background: kidsMode ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.04)", borderRadius: 14, padding: "12px 16px", border: `1px solid ${kidsMode ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.08)"}`, display: "flex", alignItems: "center", gap: 10, cursor: pLat ? "pointer" : "default" }}>
              <span style={{ fontSize: 22 }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: kidsMode ? "#1e293b" : "#e2e8f0" }}>{typeLabel}</div>
                <div style={{ fontSize: 12, color: kidsMode ? "#475569" : "#64748b" }}>{distLabel} {feeLabel}{pLat ? " · tap for directions" : ""}</div>
              </div>
              {pLat && <span style={{ fontSize: 18, opacity: kidsMode ? 0.4 : 0.5, color: kidsMode ? "#1e293b" : undefined }}>›</span>}
            </div>
          );
        })()}

        {/* Food Nearby — Phase 9d: superseded by the new "Pair this with"
            section above, which fetches fresh suggestions per category and
            connects them to the outing builder. Hidden here. */}
        {false && selected.nearbyFood?.filter(f => f.name && f.name !== "Food").length > 0 && (() => {
          const FOOD_ICON = { ice_cream_shop: "🍦", cafe: "☕", bakery: "🥐", restaurant: "🍽️" };
          const FOOD_LABEL = { ice_cream_shop: "Ice cream", cafe: "Cafe", bakery: "Bakery", restaurant: "Restaurant" };
          return (
            <div style={{ margin: "0 16px 16px" }}>
              <div style={S.detailFeatureTitle}>Food nearby</div>
              {selected.nearbyFood.filter(f => f.name && f.name !== "Food").map((f, i) => {
                const icon = FOOD_ICON[f.type] || "🍽️";
                const label = FOOD_LABEL[f.type] || "Food";
                const distLabel = fmtShortDist(f.distance, useMetric);
                const onTheWay = location && (() => {
                  const toFood = Math.atan2(f.lng - selected.lng, f.lat - selected.lat) * 180 / Math.PI;
                  const toHome = Math.atan2(location.lng - selected.lng, location.lat - selected.lat) * 180 / Math.PI;
                  return Math.abs(((toFood - toHome + 540) % 360) - 180) < 60;
                })();
                const openLabel = f.openNow === true ? { text: "Open", color: "#4ade80" } : f.openNow === false ? { text: "Closed", color: "#f87171" } : null;
                return (
                  <div key={i} onClick={() => openAppleMaps(f.name, f.lat, f.lng, selected.lat, selected.lng)}
                    style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, background: kidsMode ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.04)", borderRadius: 14, padding: "10px 14px", border: `1px solid ${kidsMode ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.08)"}`, cursor: "pointer" }}>
                    <span style={{ fontSize: 20 }}>{icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: kidsMode ? "#1e293b" : "#e2e8f0" }}>{f.name}</div>
                      <div style={{ fontSize: 12, color: kidsMode ? "#475569" : "#64748b", display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <span>{label} · {distLabel}</span>
                        {onTheWay && <span>· on the way home</span>}
                        {openLabel && <span style={{ color: openLabel.color, fontWeight: 700 }}>· {openLabel.text}</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 16, opacity: 0.4, color: kidsMode ? "#1e293b" : undefined }}>›</span>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Features — Phase 9b: visible features rendered above as the sticker
            "WHAT'S HERE" grid. Keep this block rendered (display:none) so the
            "Something missing?" community-features submission UI continues
            to work; will be migrated into the new design in 9c. */}
        {(() => {
          const modeFeatureId = activityMode ? ACTIVITY_MODES.find(m => m.id === activityMode)?.featureId : null;
          const allFeatures = [...new Set([...selected.features, ...communityFeatures])];
          const visibleFeatures = allFeatures.filter(f => f !== modeFeatureId);
          const missingFeatures = FEATURES.filter(f => !allFeatures.includes(f.id));
          return (
            <div style={{ ...S.detailFeatures, display: "none" }}>
              <div style={S.detailFeatureTitle}>Features at this {getSpotInfo(activityMode, selected?.name).noun}</div>
              {visibleFeatures.length > 0 && (
                <div style={{ ...S.detailFeatureGrid, marginBottom: 10 }}>
                  {visibleFeatures.map(f => {
                    const feat = FEATURES.find(x => x.id === f);
                    const icon = feat?.icon || SPOT_INFO[f]?.emoji || "";
                    const label = feat?.label || SPOT_INFO[f]?.rateLabel || f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                    const isCommunity = communityFeatures.includes(f) && !selected.features.includes(f);
                    return (
                      <span key={f} style={{ ...S.detailFeatureTag, ...(isCommunity ? { opacity: 0.85 } : {}) }}>
                        {icon} {label}{isCommunity && <span style={{ fontSize: 10, color: "#64748b", marginLeft: 3 }}>👥</span>}
                      </span>
                    );
                  })}
                </div>
              )}
              {/* Add missing features */}
              {missingFeatures.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowAddFeatures(p => !p)}
                    style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, color: "#64748b", fontSize: 12, padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    {showAddFeatures ? "▲" : "➕"} {showAddFeatures ? "Hide" : "Something missing?"}
                  </button>
                  {showAddFeatures && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: "#475569", marginBottom: 6 }}>Tap to report a feature you see here</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {missingFeatures.map(f => (
                          <button key={f.id} onClick={() => {
                            fetch("/api/features", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ osmId: selected.osmId, feature: f.id, action: "add" }),
                            })
                              .then(r => r.ok ? r.json() : null)
                              .then(data => {
                                if (!data) return;
                                setCommunityFeatures(data.features);
                                setPlaygrounds(prev => prev.map(p =>
                                  String(p.osmId) === String(selected.osmId)
                                    ? { ...p, features: [...new Set([...p.features, ...data.features])] }
                                    : p
                                ));
                              })
                              .catch(() => {});
                          }} style={{
                            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 99, color: "#94a3b8", fontSize: 12, padding: "4px 10px", cursor: "pointer",
                            display: "inline-flex", alignItems: "center", gap: 4,
                          }}>
                            {f.icon} {f.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Equipment condition — moved into the new Community block.
            Old presentation hidden here. */}
        {false && selected && !activityMode && (() => {
          const RATING_LABELS = ["Unsafe", "Rough", "OK", "Good", "Like new"];
          const RATING_COLORS = ["#f87171", "#fb923c", "#fbbf24", "#86efac", "#4ade80"];
          const submitCondition = async (rating, tags) => {
            if (submittingCondition) return;
            setSubmittingCondition(true);
            setUserCondition(rating);
            setUserConditionTags(tags);
            try {
              const saved = JSON.parse(localStorage.getItem("pg_condition") || "{}");
              saved[selected.osmId] = { rating, tags };
              localStorage.setItem("pg_condition", JSON.stringify(saved));
              const res = await fetch("/api/condition", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ osmId: selected.osmId, rating, tags }),
              });
              const updated = await res.json();
              if (updated && updated.count) setPgCondition(updated);
            } catch {}
            setSubmittingCondition(false);
          };
          const toggleTag = (t) => {
            const next = userConditionTags.includes(t)
              ? userConditionTags.filter(x => x !== t)
              : [...userConditionTags, t];
            setUserConditionTags(next);
            if (userCondition) submitCondition(userCondition, next);
          };
          const topTags = pgCondition?.tags
            ? Object.entries(pgCondition.tags).sort((a, b) => b[1] - a[1]).slice(0, 3)
            : [];
          return (
            <div style={{ margin: "0 16px 16px", background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: "16px", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
                {userCondition ? "Your Condition Report" : "How's the equipment?"}
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                {[1,2,3,4,5].map(i => {
                  const active = i === userCondition;
                  const color = RATING_COLORS[i - 1];
                  return (
                    <button key={i}
                      onClick={() => submitCondition(i, userConditionTags)}
                      disabled={submittingCondition}
                      style={{
                        flex: 1, padding: "8px 4px", borderRadius: 10, cursor: "pointer",
                        fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
                        background: active ? `${color}26` : "rgba(255,255,255,0.04)",
                        border: `1px solid ${active ? color : "rgba(255,255,255,0.1)"}`,
                        color: active ? color : "#94a3b8",
                        transition: "background 0.15s, border-color 0.15s, color 0.15s",
                      }}>
                      {RATING_LABELS[i - 1]}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {getConditionTags(activityMode).map(t => {
                  const active = userConditionTags.includes(t.id);
                  return (
                    <button key={t.id} onClick={() => toggleTag(t.id)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "5px 10px", borderRadius: 99, fontSize: 12,
                        background: active ? "rgba(96,165,250,0.18)" : "rgba(255,255,255,0.05)",
                        border: active ? "1px solid rgba(96,165,250,0.45)" : "1px solid rgba(255,255,255,0.08)",
                        color: active ? "#93c5fd" : "#94a3b8",
                        cursor: "pointer", fontWeight: active ? 700 : 500,
                      }}>
                      <span>{t.icon}</span><span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
              {pgCondition?.count > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                  Community: {pgCondition.average} / 5 from {pgCondition.count} {pgCondition.count === 1 ? "report" : "reports"}
                  {topTags.length > 0 && (
                    <span> · {topTags.map(([id, n]) => {
                      const t = ALL_CONDITION_TAGS.find(x => x.id === id);
                      return t ? `${t.icon} ${t.label} (${n})` : null;
                    }).filter(Boolean).join(" · ")}</span>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Weather details — Phase 9d: dropped from the redesigned detail
            screen. The Verdict card already covers temp/condition/cloud/fog,
            and the WeatherSticker on the home/list shows wind. The 6-tile
            grid was redundant. Hidden here. */}
        {false && weather && (
          <div style={{ padding: "0 16px 48px" }}>
            <div style={S.detailFeatureTitle}>Current conditions</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                ["🌡️ Temp", fmtTemp(weather.temp, useMetric)],
                ["💨 Wind", fmtWind(weather.wind, useMetric, weather.windDirection)],
                ["💧 Humidity", `${weather.humidity}%`],
                ["👁 Visibility", fmtVis(weather.visibility, useMetric)],
                ["☁️ Clouds", `${weather.cloud}%`],
                ["🌁 Fog", weather.fog ? "Present" : "Clear"],
              ].map(([label, val]) => (
                <div key={label} style={S.infoItem}>
                  <div style={S.infoLabel}>{label}</div>
                  <div style={{ ...S.infoVal, fontSize: 18, fontWeight: 700 }}>{val}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inspector — raw source data (enabled via debug panel toggle) */}
        {debugInspector && (
          <div style={{ margin: "16px", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 14, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#60a5fa", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>🔍 Source data</div>
            <InspectorBlock label="result object" value={selected} />
            {analysis && <InspectorBlock label="AI analysis" value={analysis} />}
            {weather && <InspectorBlock label="weather / sun input" value={{ weather, sunInfo, activityMode, selectedFeatures }} />}
            {selected?.osmId && !String(selected.osmId).startsWith("google") && !String(selected.osmId).startsWith("curated") && (
              <a
                href={`https://www.openstreetmap.org/${selected.osmType || "node"}/${selected.osmId}`}
                target="_blank" rel="noreferrer"
                style={{ display: "inline-block", marginTop: 10, fontSize: 12, color: "#60a5fa" }}
              >Open in OSM →</a>
            )}
          </div>
        )}
      </div>
    </div>
    {isDesktop && location && (
      <div style={{ flex: 1, position: "relative", height: "100dvh", overflow: "hidden" }}>
        <Suspense fallback={<div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: SUNNY.inkSoft, fontFamily: SUNNY.fontBody, fontWeight: 800 }}>Loading map…</div>}>
          <MapView
            playgrounds={filteredPlaygrounds.length ? filteredPlaygrounds : [selected]}
            userLat={location.lat}
            userLng={location.lng}
            radiusMeters={searchRadius}
            onSelect={pg => { openDetail(pg); }}
            onMapMove={(lat, lng, viewRadius) => setMapCenter({ lat, lng, viewRadius })}
            viewportSearchToken={viewportSearchToken}
            activityEmoji={ACTIVITY_MODES.find(m => m.id === activityMode)?.icon ?? "🛝"}
            bottomSheetHeight={0}
          />
        </Suspense>
      </div>
    )}
    </div>
  );

  return null;
}
